/**
 * CodableChatPane — dock sidebar chat pro workspace.
 *
 * Filip 2026-07-08: "codable chat má být naopak na straně jako skrývatelný,
 * ale s pevným místem". Pravý dock, skrývatelný toggle-em v toolbaru, ale
 * když je viditelný sedí na fixed šířce (ne floating overlay).
 *
 * SSE klient nad /apiext/chat/stream. Přepínač modelu (Qwen local / Claude /
 * GPT / …) v header. Předává `project_id + slug + repo` do systémového kontextu.
 *
 * Vizuál 2026-07-09: Lovable-style — user bubliny vpravo (blue-600), assistant
 * plná šířka bez bubliny, composer box s vnitřní lištou (attach / plán /
 * settings / kruhový send), smart autoscroll + empty stav s náměty.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  interruptUiChatTurn,
  openUiChatStream,
  openUiChatTurn,
  openUiChatTurnEvents,
  uiChatFailureMessage,
} from '../../../lib/chat-stream';
import { codableWorkModeSticky } from '../../../lib/codable-work-mode';
import { codableAddressLines } from '../../../lib/codable-addresses';
import { ThinkVisibleBuffer } from '../../../lib/think-visible';
import { historyForLlm } from '../../../lib/codable-llm-history';
import {
  describeInfoNotice,
  describeProfileSwitch,
} from '../../../lib/codable-chat-notices';
import {
  type ToolOutputMeta,
  hostToolOutputs,
  stripToolFollowupPrefix as stripToolFollowupPrefixFrom,
} from '../../../lib/codable-chat-tool-panel';
import {
  completePatchBlocks,
  COMPLETION_EVIDENCE_PREFIX,
  completionEvidenceFollowup,
  completionEvidenceGap,
  incompletePatchStart,
  priorCompletionEvidence,
} from '../../../lib/codable-completion-evidence';
import {
  buildCodableChatTools,
  isCodableChatGitTool,
  isCodableChatWriteTool,
} from '../../../lib/codable-chat-tools';
import { replaceLegacyProjectId, visibleProjectId } from '../../../lib/project-id';
import { CODABLE_CHAT_ASK, takeCodableChatAsk } from '../../../lib/codable-chat-ask';
import {
  loadChatAutoDone,
  persistChatAutoDone,
  planAutoClaimKey,
} from '../../../lib/codable-chat-auto';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  Bot,
  Camera,
  Check,
  ChevronDown,
  ChevronsUpDown,
  Cloud,
  Download,
  FileText,
  FoldVertical,
  History,
  Home,
  Image as ImageIcon,
  Lightbulb,
  Loader2,
  Lock,
  Mic,
  Paperclip,
  Plug,
  Plus,
  RefreshCw,
  Settings,
  Square,
  Trash2,
  Unlock,
  Upload,
  X,
} from 'lucide-react';
import { CodableIcon } from '../../../components/icons/CodableIcon';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { type Lang, normalizeLang } from '../../../lib/i18n';
import { getUiContextSnapshot, type UiContext } from '../../../lib/ui-context';
import {
  formatCommitFence,
  isGitCommitIntent,
  type WorkspaceStatusLike,
} from '../../../lib/codable-git-intent';
import { Button, Modal } from '../../../components/ui';
import { type ActionPayload, AssistantMessage, PROSE_CLASSES } from './AssistantMessage';
import { themedConfirm } from '../../../lib/themed-dialogs';

const CODABLE_CODEX_PROFILE_ID = 'codable-user-codex';
const CODABLE_MODEL_PREF_KEY = 'codable.preferred_model';

type AutonomyMode = 'ask' | 'workspace';

/**
 * Default práce agenta je **Live** (auto-approve), ne Návrhy.
 *
 * Filip 2026-09-09: „default by to mělo fungovat v live autoapprove režimu".
 * V Návrzích se režim podepisuje přímo do promptu („NÁVRHY: nezapisuj; jen
 * patch karta, čekej na klik uživatele") — model se tak učí *navrhovat* volání
 * nástroje místo aby ho zavolal, a když uživatel kartu schválí a zápis selže,
 * odpovědnost za chybu leží na člověku, ne v historii konverzace.
 *
 * Přepínač zůstává: kdo chce potvrzování, může si Návrhy zapnout. Proto schema
 * klíč — uložené `ask` z éry, kdy `ask` byl default, není rozhodnutí uživatele,
 * a přepíše se jednou; každá volba po dnešku už se respektuje.
 */
const AUTONOMY_MODE_SCHEMA = 'live-autoapprove-v3';

function toolKind(name: string): 'read' | 'write' | 'commit' {
  if (isCodableChatGitTool(name)) return 'commit';
  if (isCodableChatWriteTool(name)) return 'write';
  return 'read';
}

function toolKindLabel(kind: ReturnType<typeof toolKind>): string {
  if (kind === 'commit') return 'GIT';
  if (kind === 'write') return 'WRITE';
  return 'READ';
}

type ToolbarTone = 'amber' | 'emerald' | 'purple' | 'slate';

/** Ghost ovládání: stisk mění jen barvu symbolu, ne výplň (jako clip/mic vlevo). */
const CHAT_ICON =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-100 disabled:opacity-45 disabled:cursor-not-allowed';

const TOOLBAR_TONE_CLASS: Record<
  ToolbarTone,
  { active: string; idle: string }
> = {
  amber: {
    active: 'text-amber-300 hover:text-amber-200',
    idle: 'text-gray-400 hover:text-gray-100',
  },
  emerald: {
    active: 'text-emerald-300 hover:text-emerald-200',
    idle: 'text-gray-400 hover:text-gray-100',
  },
  purple: {
    // purple-300 (ne -200): light themes remapují 200/300 na dark ink v index.css
    active: 'text-purple-300 hover:text-purple-200',
    idle: 'text-gray-400 hover:text-gray-100',
  },
  slate: {
    active: 'text-gray-100 hover:text-white',
    idle: 'text-gray-400 hover:text-gray-100',
  },
};

function ToolbarToggle({
  active,
  onClick,
  icon,
  label,
  title,
  disabled,
  tone = 'slate',
}: {
  active: boolean;
  onClick: () => void;
  icon?: ReactNode;
  label: string;
  title: string;
  disabled?: boolean;
  tone?: ToolbarTone;
}) {
  const styles = TOOLBAR_TONE_CLASS[tone];
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-lg px-2 text-[11px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        active ? styles.active : styles.idle
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

/**
 * Stavové tlačítko spodní lišty v velikosti ikon („nahrát soubor",
 * „screenshot"). Filip 10. 9.: ovládání má být na spodní liště a textové
 * štítky („Návrhy | Live", „Auto", „Git") táhly šířku — ikona se štítkem se
 * nevešla vedle tlačítka Stop. Zapnutý stav je jen barva symbolu, vypnutý
 * šedivý jako clip/mic vlevo.
 */
function IconToggle({
  active,
  onClick,
  title,
  disabled,
  tone = 'purple',
  activeClass,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  disabled?: boolean;
  tone?: ToolbarTone;
  /** Barva pro zapnutý stav, když tón nestačí (zámek je červený). */
  activeClass?: string;
  children: ReactNode;
}) {
  const styles = TOOLBAR_TONE_CLASS[tone];
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold leading-none transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        active
          ? activeClass ?? styles.active
          : 'text-gray-400 hover:bg-gray-800 hover:text-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

function toolChipClass(name: string, ok?: boolean): string {
  const kind = toolKind(name);
  if (ok === false) return 'bg-red-500/10 text-red-300 border-red-500/20';
  if (kind === 'commit') {
    return ok === undefined
      ? 'bg-purple-500/10 text-purple-300 border-purple-500/25 animate-pulse'
      : 'bg-purple-500/15 text-purple-300 border-purple-500/35';
  }
  if (kind === 'write') {
    return ok === undefined
      ? 'bg-amber-500/10 text-amber-300 border-amber-500/25 animate-pulse'
      : 'bg-amber-500/15 text-amber-200 border-amber-500/35';
  }
  return ok === undefined
    ? 'bg-sky-500/10 text-sky-300 border-sky-500/25 animate-pulse'
    : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20';
}

// Streamující se speciální fence (patch/plan/commit/…) nezobrazuj syrově —
// obsah se do chatu sype bez formátování a rozbíjí layout; karta vznikne až
// po dojetí streamu (Filip 2026-07-12). Neuzavřený blok → placeholder.
const OPEN_FENCE_RE = /```(plan|patch|commit|question|tool_\w+|step_done)(?:[ \t]+([^\n]+))?/g;
function maskStreamingFence(content: string): string {
  OPEN_FENCE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = OPEN_FENCE_RE.exec(content)) !== null) {
    const after = content.slice(m.index + m[0].length);
    if (after.includes('```')) continue; // uzavřený blok — nech být
    const kind = m[1]!;
    const label = kind === 'patch'
      ? i18n.t('codableChat.writingPatch', { path: m[2]?.trim() ?? '' })
      : kind === 'plan'
      ? i18n.t('codableChat.buildingPlan')
      : kind === 'commit'
      ? i18n.t('codableChat.preparingCommit')
      : kind === 'question'
      ? i18n.t('codableChat.formingQuestion')
      : `🔧 ${kind}…`;
    return `${content.slice(0, m.index).trimEnd()}\n\n*${label}*`;
  }
  return content;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt?: string;
  pending?: boolean;
  interrupted?: boolean;
  /** Nativní tool cally z backend exec loopu (SSE tool_call/tool_result) —
   * rendrují se jako chips řádek nad obsahem zprávy. */
  nativeTools?: Array<{ name: string; ok?: boolean }>;
  /** Souhrn vzniklý autocompactem — renderuje se jako sbalený marker. */
  compacted?: boolean;
  /** Model právě přemýšlí (reasoning_content stream) — UI ukáže indikátor
   * místo zamrzlé zprávy; flag zhasne s prvním text chunkem. */
  thinking?: boolean;
  /** Auto-followup zpráva s výstupem MCP tool volání — v UI defaultně sbalená;
   * v LLM prompt payloadu se všechny kromě POSLEDNÍ tool_output zkrátí na
   * summary (šetří kontext). */
  toolOutput?: {
    tools: string[]; // ["tool_read src/App.tsx", "tool_list"]
    summary: string; // krátký text, který jde do LLM místo full content
    /** Výsledky fence nástrojů pro panel nástrojů agentova tahu. Bez ní jde o
     * starou uloženou historii — ta se renderuje samostatně (bez nativního tahu,
     * kam by se dala vtělit). Do LLM payloadu se `content` bere i bez ní. */
    body?: string;
  };
  /** Plán v této zprávě už auto/ručně vyřízen — PlanCard ukáže badge, ne tlačítka. */
  planDecision?: 'auto' | 'accepted' | 'rejected';
  /**
   * Platformní zpráva: tah nezaložil člověk, ale server po dokončení operace,
   * kterou agent spustil (stream/turns.ts follow-up). Renderuje se jako
   * sbalený marker, ne jako bublina usera.
   */
  platformEvent?: boolean;
}

/** Tah konverzace tak, jak ho vrací GET /conversations/:id (stream/turns.ts). */
interface PlatformTurn {
  id: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'interrupted';
  request?: { origin?: string } | null;
  user_message?: { content?: unknown } | null;
  created_at?: string;
}

const PLATFORM_EVENT_PREFIX = '[Platforma]';
const PLATFORM_TURNS_SEEN_KEY = 'codable-chat-platform-turns:';

function readSeenPlatformTurns(historyKey: string): Set<string> {
  try {
    const raw = localStorage.getItem(PLATFORM_TURNS_SEEN_KEY + historyKey);
    const ids = raw ? (JSON.parse(raw) as string[]) : [];
    return new Set(Array.isArray(ids) ? ids.slice(-200) : []);
  } catch {
    return new Set();
  }
}

function rememberSeenPlatformTurn(historyKey: string, seen: Set<string>): void {
  try {
    localStorage.setItem(PLATFORM_TURNS_SEEN_KEY + historyKey, JSON.stringify([...seen].slice(-200)));
  } catch { /* soukromý režim — stačí paměť */ }
}

/** Titulek markeru: první řádek bez prefixu a bez dvojtečky. */
function platformEventTitle(content: string): string {
  const first = content.split('\n')[0] ?? '';
  return first.replace(PLATFORM_EVENT_PREFIX, '').trim().replace(/:$/, '');
}

/** Tělo markeru: bez prefixu a bez strojového JSON bloku (ten je pro model). */
function platformEventBody(content: string): string {
  return content
    .replace(PLATFORM_EVENT_PREFIX, '')
    .replace(/```json[\s\S]*?```\s*$/m, '')
    .trim();
}

/** Extrahuj těla ```plan``` bloků z assistant textu. */
function extractPlanBlocks(text: string): string[] {
  const re = /```plan(?:[ \t]+[^\n`]*)?(?:\n([\s\S]*?)\n?)?```/gi;
  const out: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const body = (m[1] ?? '').trim();
    if (body) out.push(body);
  }
  return out;
}

/** Krátký continue po prvním přijetí plánu — NErestartovat od nuly. */
function planAcceptFollowup(): string {
  return i18n.t(
    'codableChat.planAcceptFollowup',
    'Plán je aktivní. NEposílej znovu ```plan, NEopakuj úvod ani hotové kroky. Pokračuj jen NEJBLIŽŠÍM nedokončeným krokem přes nativní tooly (read → codable_workspace_replace nebo codable_workspace_write). Po doloženém kroku ```step_done N```. Bez dlouhého „co budu dělat“.',
  );
}

/** Normalize plan steps for compare (re-plan detection). */
function normalizePlanStepsKey(steps: string[]): string {
  return steps.map((s) => s.toLowerCase().replace(/\s+/g, ' ').trim()).filter(
    Boolean,
  ).join('|');
}

function plansRoughlySame(a: string[], b: string[]): boolean {
  if (a.length === 0 || b.length === 0) return false;
  const ka = normalizePlanStepsKey(a);
  const kb = normalizePlanStepsKey(b);
  if (ka === kb) return true;
  // overlap ≥ 60 % → consider same task (model rephrased)
  const sa = new Set(ka.split('|'));
  const sb = new Set(kb.split('|'));
  let hit = 0;
  for (const x of sa) if (sb.has(x)) hit++;
  return hit / Math.max(sa.size, sb.size) >= 0.6;
}

/**
 * Aktivní plán projektu (převzato ze superagent-runner.ts propose_plan +
 * complete_step). Když user klikne „Přijmout" u ```plan bloku, plán se uloží
 * do stateu a dále je injektován do KAŽDÉ zprávy do LLM se stavem hotových
 * kroků. Agent tak vždy ví „kde v plánu jsi" bez scrollování historie.
 *
 * step_done N fence od agenta → přidá N do doneSteps.
 */
interface ActivePlan {
  steps: string[]; // kroky (parsed z ```plan bloku)
  done: number[]; // 0-based indexy hotových kroků
  createdAt: string; // ISO timestamp přijetí
  historyId?: string; // acm.codable_plan_history.id
  title?: string;
  source?: 'chat' | 'import' | 'manual';
}

type PlanHistoryStatus = 'active' | 'completed' | 'cancelled';
type PlanHistorySource = 'chat' | 'import' | 'manual';

interface PlanHistoryItem {
  id: string;
  title?: string | null;
  source: PlanHistorySource;
  status: PlanHistoryStatus;
  steps: string[];
  done: number[];
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
  cancelled_at?: string | null;
}

// ── Kontext management ────────────────────────────────────────────────────────
// Hrubý odhad tokenů (~3.5 znaku/token pro mix CZ+kód). Přesné počty zná jen
// backend/model — tohle je orientační ukazatel + práh pro autocompact.
const estTokens = (s: string) => Math.ceil(s.length / 3.5);
// Realistický kontext moderních coder modelů: Claude Sonnet 4/Opus ~200k,
// Qwen 2.5-Coder 128k, GPT-4o 128k. 64k byl konzervativní strop dřívějšího
// Qwen 3-Coder — dnes je overkill i pro Kimi K2 s 128k. Vysvětlení Filip
// 2026-07-10: „superagent má autocompact >120k token." Přijímáme 120k jako
// safe floor pro každý dnešní podporovaný model, autocompact projede při
// 75 % = 90k reálného obsazení.
const CTX_BUDGET = 120_000;
const SYSTEM_OVERHEAD = 2_800; // systemPreamble + project orientation snapshot + obálka
const COMPACT_AT = 0.75; // nad 75 % rozpočtu → autocompact před odesláním
const KEEP_RECENT = 8; // posledních N zpráv zůstává doslovně

function formatTokenCount(tokens: number): string {
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(tokens >= 10_000_000 ? 0 : 1)}M`;
  }
  return `${Math.round(tokens / 1000)}k`;
}

interface ChatProfile {
  id?: string;
  name?: string;
  label?: string;
  provider?: string;
  model?: string;
  endpoint?: string;
  url?: string;
  backend?: string;
  node_scope?: string;
  enabled?: boolean;
  agent_family?: 'codex' | 'claude' | 'openai' | 'qwen' | 'gpt-oss' | 'generic';
  preferred_patch_mode?: 'full-file' | 'unified-diff';
  context_budget?: number;
  codable_compatible?: boolean;
  codable_enabled?: boolean;
  consumers?: string[];
  /** Model umí obrázky — bez toho se image attachmenty/screenshoty nepřikládají
   * (text-only modely na image part padají; formát pro OpenAI-compat překládá
   * backend streamOpenAI na image_url). */
  vision?: boolean;
  source?: 'llm-profiles' | 'codable-user';
  has_token?: boolean;
}

interface CodableChatPaneProps {
  projectId: string;
  orchestratorProjectId?: string | null;
  slug: string;
  repo?: string;
  seedPrompt?: string;
  onClose?: () => void;
  previewUrl?: string | null;
  /**
   * Modrá náhledová adresa PROD instance (`production.preview_url`) — ta, na
   * kterou ukazuje slot „Náhled" v karuselu release. Bez ní prompt rozezná
   * jen dev náhled a agent zamění kandidáta na ostrou s dev workspaceem.
   */
  prodPreviewUrl?: string | null;
  gitlabUrl?: string | null;
  publish?: {
    domain: string | null;
    desired: boolean;
    actual: boolean;
    url: string | null;
  } | null;
  envStatus?: string | null;
  workspaceInstance?: string | null;
  prodInstance?: string | null;
  feSplit?: boolean;
  topology?: {
    mode?: string | null;
    target?: string | null;
    actual?: string | null;
    label?: string | null;
    frontend_split?: boolean | null;
    backend_split?: boolean | null;
    verified?: boolean | null;
    drift?: boolean | null;
    dev_domain?: string | null;
    prod_domain?: string | null;
    issues?: string[];
  } | null;
  projectStatus?: {
    checked_at?: string;
    dev?: {
      short_head?: string | null;
      dirty?: boolean;
      ahead?: number | null;
      behind?: number | null;
      changed_files?: number;
    };
    prod?: {
      short_head?: string | null;
      dirty?: boolean;
      ahead?: number | null;
      behind?: number | null;
      changed_files?: number;
    };
    relation?: {
      same_instance?: boolean;
      same_head?: boolean | null;
      states?: string[];
      warnings?: string[];
    };
    carousel?: {
      live?: string | null;
      preview?: string | null;
      reserved?: string | null;
      preview_is_live?: boolean;
      next?: string;
    } | null;
  } | null;
  lastCommitSha?: string | null;
  lastPublishEvent?: {
    tool_name: string;
    raw_tool_name?: string | null;
    actor_entity_id?: string | null;
    actor_key_preview?: string | null;
    ok: boolean;
    error?: string | null;
    created_at: string;
  } | null;
  /** Per-projekt instrukce (codable.knowledge) — přisypou se do system promptu */
  knowledge?: string | null;
  /** Otevře existující modal pro per-project MCP/IDE tokeny v parent workspace. */
  onOpenConnectIde?: () => void;
  onOpenLlmModels?: () => void;
  /** Telefonní práce: tenčí hlavička, bez desktopových ikon vpravo. */
  compactChrome?: boolean;
  /** Zavolá se když patch úspěšně commitl + deployl — parent bump iframe key
   * aby se preview refreshlo bez F5. Filip 2026-07-11: „vývojový náhled má
   * ukázat změnu rovnou, ne po celém kolečku." */
  onDeployed?: () => void;
}

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

interface Attachment {
  name: string;
  content: string; // text obsah pro kind='text'; data-URL pro kind='image'
  size: number;
  /** 'text' (default) — inline blok v user zprávě; 'image' — multimodal content
   * block s type='image_url' / anthropic 'image'. Filip 2026-07-10: agent
   * dostane screenshot přímo. */
  kind?: 'text' | 'image';
  /** Base64 payload (bez data:image/png;base64, prefix) pro image kind */
  base64?: string;
  /** Media type — image/png, image/jpeg */
  media_type?: string;
}

/**
 * Jméno, pod kterým se příloha ze sponky uloží mezi media chatu.
 *
 * Media patří do `<zdroje projektu>/chat/`, tedy MIMO git checkout — do
 * 2026-09-13 chodila do `public/uploads/` uvnitř repa a každý screenshot z něj
 * dělal untracked změnu, na které padal git sync (Filip: „při sync to řve, že
 * git sync pool selhal a bere to jako untracked changes"). Do workspace se
 * soubor dostane teprve vědomým `codable_chat_media {action:'promote'}`.
 *
 * Jméno se sanitizuje na `[a-zA-Z0-9._-]` — cizí znaky by rozbily cestu i URL.
 */
export function chatAssetName(name: string): string {
  const safe = (name.split(/[\\/]/).pop() ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^[-.]+/, '')
    .slice(0, 120);
  return safe || 'soubor';
}

// Prefixy auto-followup zpráv z tool_read flow — renderují se jako kompaktní
// tool řádky, ne jako user bubliny (a nepřerušují assistant sekvenci).
const TOOL_FOLLOWUP_PREFIX_CS = 'Výsledky nástrojů:';
const TOOL_ERROR_PREFIX_CS = '**Chyba MCP nástroje:**';
function toolFollowupPrefix(): string {
  return i18n.t('codableChat.toolResultsPrefix', 'Výsledky nástrojů:');
}
function toolErrorPrefix(): string {
  return i18n.t('codableChat.toolErrorPrefix', '**Chyba MCP nástroje:**');
}
function startsWithToolFollowup(content: string): boolean {
  return content.startsWith(TOOL_FOLLOWUP_PREFIX_CS) || content.startsWith(toolFollowupPrefix());
}
function startsWithToolError(content: string): boolean {
  return content.startsWith(TOOL_ERROR_PREFIX_CS) || content.startsWith(toolErrorPrefix());
}
const isToolRow = (m: Message) =>
  !!m.toolOutput ||
  (m.role === 'user' &&
    (startsWithToolFollowup(m.content) || startsWithToolError(m.content)));

// Výsledky fence nástrojů se vtělí do panelu nástrojů daného tahu, takže
// render už nemá ukazovat protokolový prefix „Výsledky nástrojů:". Prefix
// zůstává jen v LLM payloadu (domluva s modelem) a ve staré uložené historii.
function stripToolFollowupPrefix(content: string): string {
  return stripToolFollowupPrefixFrom(content, [
    TOOL_FOLLOWUP_PREFIX_CS,
    toolFollowupPrefix(),
  ]);
}

// Kolik posledních tool_output zpráv přeposílat v LLM prompt v plné podobě.
// Starší se nahradí `toolOutput.summary` (šetří kontext — full výstup z 5 kol
// tool_readů může sežrat 20-40k tokenů). Filip 2026-07-10: „výstupy musí být
// sbalené, jinak plevelí prompt."

const PLAN_MD_MAX_BYTES = 256_000;

interface SnapshotFailure {
  failure_kind?: string;
  action_required?: string;
  diagnostics?: Array<{ step?: string; ok?: boolean; message?: string }>;
}

function snapshotErrorText(r: { error?: string } & SnapshotFailure): string {
  const parts = [
    r.failure_kind ? `[${r.failure_kind}]` : null,
    r.error ?? 'unknown',
    r.action_required
      ? i18n.t('codableChat.snapshotNeed', 'Potřeba: {{action}}', { action: r.action_required })
      : null,
  ].filter(Boolean);
  return parts.join(' ');
}

function snapshotDiagnosticsText(r: SnapshotFailure): string {
  const rows = (r.diagnostics ?? []).slice(-6).map((d) =>
    `- ${d.ok ? 'OK' : 'FAIL'} ${d.step ?? 'step'}: ${d.message ?? ''}`
  );
  return rows.length
    ? `\n\n${i18n.t('codableChat.snapshotDiag', 'Diagnostika:')}\n${rows.join('\n')}`
    : '';
}

function cleanPlanStep(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

function planMarkdownBody(md: string): string {
  const fenced = md.match(/```plan[^\n]*\n([\s\S]*?)```/i);
  return (fenced?.[1] ?? md).replace(/\r\n?/g, '\n');
}

/**
 * Parse plan bloku / MD souboru na kroky. Umí původní ```plan bloky,
 * checkboxy (- [x] / - [ ]), bullets i numbered listy.
 */
function parsePlanMarkdown(md: string): Pick<ActivePlan, 'steps' | 'done'> {
  const steps: string[] = [];
  const done: number[] = [];
  const body = planMarkdownBody(md);
  for (const raw of body.split('\n')) {
    const line = raw.trimEnd();
    const checkbox = line.match(/^\s*(?:[-*+]|\d+[.)])\s+\[([ xX])\]\s+(.+)$/);
    if (checkbox?.[2]) {
      const step = cleanPlanStep(checkbox[2]);
      if (!step) continue;
      const idx = steps.length;
      steps.push(step);
      if (checkbox[1]?.toLowerCase() === 'x') done.push(idx);
      continue;
    }
    const list = line.match(/^\s*(?:[-*+]|\d+[.)])\s+(.+)$/);
    if (list?.[1]) {
      const step = cleanPlanStep(list[1].replace(/^\[[ xX]\]\s+/, ''));
      if (step) steps.push(step);
    }
  }
  if (steps.length === 0) {
    for (const raw of body.split('\n')) {
      const step = cleanPlanStep(raw.replace(/^#+\s+/, ''));
      if (
        step && !/^export|^project|^projekt|^created|^vytvořeno/i.test(step)
      ) {
        steps.push(step);
      }
    }
  }
  return { steps: steps.slice(0, 100), done: done.filter((i) => i < 100) };
}

function formatActivePlanMarkdown(plan: ActivePlan): string {
  const title = plan.title?.trim() || i18n.t('codableChat.activePlanFallback');
  return [
    `# ${title}`,
    '',
    ...plan.steps.map((step, i) => `- [${plan.done.includes(i) ? 'x' : ' '}] ${step}`),
    '',
  ].join('\n');
}

// Historická položka má stejný tvar (steps/done/title) jako ActivePlan —
// export/reactivate z Historie plánů nepotřebuje round-trip na backend,
// data už máme v paměti z codable.chat_history (Filip 2026-07-19: "menu
// pro historii plánů... jen zobrazí seznam který je k ničemu").
function formatPlanHistoryItemMarkdown(item: PlanHistoryItem): string {
  return formatActivePlanMarkdown({
    steps: item.steps,
    done: item.done,
    title: item.title ?? undefined,
    createdAt: item.created_at,
  });
}

function planTitleFromMarkdown(md: string, fallback?: string | null): string {
  const heading = md.match(/^\s*#\s+(.+?)\s*$/m)?.[1]?.trim();
  return heading || fallback || i18n.t('codableChat.editedPlan');
}

function topologyModeLabel(mode?: string | null): string {
  if (mode === 'beta') return 'Beta';
  if (mode === 'simple') return 'Simple';
  if (mode === 'normal') return 'Normal';
  if (mode === 'full_split') return 'Full Split';
  if (mode === 'none') return i18n.t('codableChat.notFilled');
  return mode || i18n.t('codableChat.unverified');
}

/** Compact tree for system prompt — agent must see workspace before acting. */
function summarizeWorkspaceTree(
  entries: Array<{ path: string; type: string }> | undefined,
  maxLines = 90,
): { lines: string[]; total: number; truncated: boolean } {
  const list = entries ?? [];
  if (list.length === 0) return { lines: [], total: 0, truncated: false };
  // Prefer shallow paths first so root layout is always visible.
  const ranked = [...list].sort((a, b) => {
    const da = a.path.split('/').length;
    const db = b.path.split('/').length;
    if (da !== db) return da - db;
    return a.path.localeCompare(b.path);
  });
  const lines: string[] = [];
  for (const e of ranked) {
    if (lines.length >= maxLines) break;
    // Skip deep node_modules / build noise
    if (/(^|\/)node_modules(\/|$)/.test(e.path)) continue;
    if (/(^|\/)(dist|build|\.git)(\/|$)/.test(e.path)) continue;
    lines.push(`${e.type === 'tree' ? '📁' : '📄'} ${e.path}`);
  }
  return { lines, total: list.length, truncated: lines.length < list.length };
}

function parsePackageJsonMeta(raw: string | undefined | null): {
  name?: string;
  version?: string;
  description?: string;
} | null {
  if (!raw?.trim()) return null;
  try {
    const j = JSON.parse(raw) as Record<string, unknown>;
    return {
      name: typeof j.name === 'string' ? j.name : undefined,
      version: typeof j.version === 'string' ? j.version : undefined,
      description: typeof j.description === 'string' ? j.description : undefined,
    };
  } catch {
    return null;
  }
}

function truncateForPrompt(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars)}\n…[zkráceno ${text.length - maxChars} znaků — plné přes tool]`;
}

function closeCodableMenus(except?: HTMLDetailsElement | null) {
  if (typeof document === 'undefined') return;
  document.querySelectorAll<HTMLDetailsElement>(
    'details[data-codable-menu][open]',
  ).forEach((d) => {
    if (d !== except) d.open = false;
  });
}

function makePlanHistoryId(): string {
  return crypto.randomUUID?.() ?? `plan-${Date.now()}-${uid()}`;
}

function normalizePlanHistoryItem(v: unknown): PlanHistoryItem | null {
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  const id = typeof r.id === 'string' ? r.id : '';
  if (!id) return null;
  const parsed = parsePlanMarkdown(
    Array.isArray(r.steps)
      ? (r.steps as unknown[]).map((x, i) => `${i + 1}. ${String(x ?? '')}`)
        .join('\n')
      : String(r.steps ?? ''),
  );
  const steps = Array.isArray(r.steps)
    ? (r.steps as unknown[]).map((x) => cleanPlanStep(String(x ?? ''))).filter(
      Boolean,
    )
    : parsed.steps;
  const done = Array.isArray(r.done)
    ? Array.from(
      new Set(
        (r.done as unknown[]).map((x) => typeof x === 'number' ? x : parseInt(String(x), 10))
          .filter((x) => Number.isFinite(x) && x >= 0 && x < steps.length),
      ),
    ).sort((a, b) => a - b)
    : parsed.done;
  const status = r.status === 'completed' || r.status === 'cancelled' ||
      r.status === 'active'
    ? r.status
    : 'active';
  const source = r.source === 'import' || r.source === 'manual' || r.source === 'chat'
    ? r.source
    : 'chat';
  return {
    id,
    title: typeof r.title === 'string' ? r.title : null,
    source,
    status,
    steps,
    done,
    created_at: String(r.created_at ?? ''),
    updated_at: String(r.updated_at ?? ''),
    completed_at: typeof r.completed_at === 'string' ? r.completed_at : null,
    cancelled_at: typeof r.cancelled_at === 'string' ? r.cancelled_at : null,
  };
}

function normalizePlanHistory(items: unknown): PlanHistoryItem[] {
  if (!Array.isArray(items)) return [];
  return items.map(normalizePlanHistoryItem).filter((x): x is PlanHistoryItem => !!x);
}

function formatPlanTime(value?: string | null): string {
  if (!value) return i18n.t('codableChat.noTime');
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMessageTime(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function planStatusLabel(status: PlanHistoryStatus): string {
  if (status === 'completed') return i18n.t('codableChat.statusDone');
  if (status === 'cancelled') return i18n.t('codableChat.statusCancelled');
  return i18n.t('codableChat.statusRunning');
}

function planStatusClass(status: PlanHistoryStatus): string {
  if (status === 'completed') {
    return 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25';
  }
  if (status === 'cancelled') {
    return 'bg-gray-700/45 text-gray-400 border-gray-700';
  }
  return 'bg-amber-500/10 text-amber-300 border-amber-500/25';
}

/**
 * Id konverzace = klíč přepisu na serveru (stream/turns.ts). Stabilní přes
 * reload (localStorage), nové po „Vyčistit". Tvar omezený na [A-Za-z0-9._:-].
 */
function conversationStorageKey(historyKey: string): string {
  return `${historyKey}_conv`;
}
function newConversationId(historyKey: string): string {
  const id = `${historyKey}_${Date.now().toString(36)}`.replace(/[^A-Za-z0-9._:-]/g, '-');
  try {
    window.localStorage.setItem(conversationStorageKey(historyKey), id);
  } catch { /* ignore */ }
  return id;
}
function loadConversationId(historyKey: string): string {
  try {
    const stored = window.localStorage.getItem(conversationStorageKey(historyKey));
    if (stored && /^[A-Za-z0-9._:-]{1,200}$/.test(stored)) return stored;
  } catch { /* ignore */ }
  return newConversationId(historyKey);
}

// Sanitize history před posláním na LLM: jen poslední tahy, ne celý chat.
function sanitizeHistoryForLlm(
  history: Message[],
): { role: string; content: string }[] {
  return historyForLlm(history);
}

// Náměty pro empty stav — klik předvyplní input
function getSuggestions(tr: (k: string) => string): string[] {
  return [
    tr('codableChat.suggestDark'),
    tr('codableChat.suggestLanding'),
    tr('codableChat.suggestSecurity'),
  ];
}

function getPromptTips(tr: (k: string) => string): string[] {
  return [
    tr('codableChat.suggestSecurity'),
    tr('codableChat.suggestVersions'),
    tr('codableChat.suggestLog'),
    tr('codableChat.suggestTopology'),
  ];
}

function normalizeRestoredMessages(messages: Message[]): Message[] {
  return messages.map((m, i) => {
    const restored = {
      ...m,
      createdAt: m.createdAt ??
        new Date(Date.now() - (messages.length - i) * 1000).toISOString(),
    };
    if (!restored.pending) return restored;
    // Reload/tab restore — ne „fatal interrupt“, jen uzavři pending bublinu
    const note = `\n\n*${
      i18n.t(
        'codableChat.restoredContinue',
        'Obnoveno po obnovení stránky — napiš dál, chat pokračuje.',
      )
    }*`;
    return {
      ...restored,
      pending: false,
      thinking: false,
      interrupted: false,
      content: `${restored.content.trimEnd()}${note}`,
    };
  });
}

function readLocalHistory(
  historyKey: string,
): { messages: Message[]; hadPending: boolean } | null {
  if (typeof window === 'undefined') return null;
  try {
    const saved = window.localStorage.getItem(historyKey);
    if (!saved) return null;
    const arr = JSON.parse(saved) as Message[];
    if (!Array.isArray(arr) || arr.length === 0) return null;
    return {
      messages: normalizeRestoredMessages(arr),
      hadPending: arr.some((m) => m?.pending === true),
    };
  } catch {
    return null;
  }
}

function readPromptHistory(historyKey: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(historyKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .slice(-50);
  } catch {
    return [];
  }
}

function profileLabel(p: ChatProfile): string {
  return p.label ?? p.name ?? p.id ?? p.model ?? 'model';
}

function profileKey(p: ChatProfile): string {
  return p.id ?? p.model ?? profileLabel(p);
}

function inferAgentFamily(
  p?: ChatProfile,
): NonNullable<ChatProfile['agent_family']> {
  const s = `${p?.id ?? ''} ${p?.name ?? ''} ${p?.label ?? ''} ${p?.model ?? ''}`.toLowerCase();
  if (p?.agent_family) return p.agent_family;
  if (s.includes('codex')) return 'codex';
  if (s.includes('claude') || s.includes('fable')) return 'claude';
  if (s.includes('gpt-oss')) return 'gpt-oss';
  if (s.includes('qwen')) return 'qwen';
  if (s.includes('gemma') || s.includes('gemini')) return 'generic';
  if (s.includes('gpt')) return 'openai';
  return 'generic';
}

function profileOrigin(p: ChatProfile): 'private' | 'platform' | 'cloud' {
  if (p.source === 'codable-user') return 'private';
  const s = [
    p.id,
    p.name,
    p.label,
    p.provider,
    p.model,
    p.endpoint,
    p.url,
    p.backend,
    p.node_scope,
  ].filter(Boolean).join(' ').toLowerCase();
  if (
    /openrouter|api\.openai|anthropic|generativelanguage|googleapis|gemini|moonshot|kimi|grok|x\.ai|\bcloud\b/
      .test(s)
  ) {
    return 'cloud';
  }
  if (
    /bo[zž]enka|vlad[eě]nka|qwen|gpt-oss|llama|ollama|vllm|local|localhost|127\.0\.0\.1|10\.\d+\.\d+\.\d+/
      .test(s) ||
    p.agent_family === 'qwen' ||
    p.agent_family === 'gpt-oss'
  ) {
    return 'platform';
  }
  return 'platform';
}

function profileOriginLabel(origin: ReturnType<typeof profileOrigin>): string {
  if (origin === 'private') return i18n.t('codableChat.privateModel');
  if (origin === 'cloud') return 'Cloud model';
  return i18n.t('codableChat.platformModel');
}

function agentDialectLines(
  p: ChatProfile | undefined,
  planningMode: boolean,
  autonomyMode: AutonomyMode,
): string[] {
  const family = inferAgentFamily(p);
  const patchMode = p?.preferred_patch_mode ?? 'full-file';
  const budget = p?.context_budget ?? CTX_BUDGET;
  const common = [
    `- Aktivní profil: ${profileLabel(p ?? {})} (${family}, context budget ~${
      Math.round(budget / 1000)
    }k tokenů).`,
    autonomyMode === 'workspace'
      ? `- Live transport: pouze nativní workspace_replace/workspace_write; patch karty ani výpisy celých souborů nepoužívej.`
      : `- Patch transport UI: ${
        patchMode === 'unified-diff'
          ? 'model smí uvažovat v diffech, ale karta zatím aplikuje full-file obsah'
          : 'full-file patch cards'
      }.`,
  ];
  if (family === 'codex') {
    return [
      ...common,
      `- Codex režim: buď úsporný a operuj jako coding agent. Neopakuj pravidla, nejdřív si načti relevantní soubory, potom navrhni minimální změnu.`,
      autonomyMode === 'workspace'
        ? `- Preferuj malé, ověřitelné kroky: read → workspace_replace → verification → commit.`
        : `- Preferuj malé, ověřitelné kroky: read → patch card → explicit verification → commit card.`,
      `- Když nemáš důkaz z toolu nebo aplikované karty, neoznačuj krok za hotový.`,
      planningMode
        ? `- Planning mode ZAP: krátký plán (2-5) jednou na začátku, pak tooly + step_done.`
        : `- Planning mode VYP: NEposílej \`\`\`plan\`\`\` karty. Práce je schválená Live/Auto — rovnou tooly, ne plánovací ceremonie.`,
      `- Zákaz smyčky: nezačínej znovu od nuly, neopakuj „teď udělám…“ — po tool resultu rovnou další akce.`,
    ];
  }
  if (family === 'claude') {
    return [
      ...common,
      `- Claude/Fable režim: můžeš dát o něco víc návrhového kontextu, ale výsledek stále musí končit konkrétní kartou nebo jasným blokátorem.`,
      `- U produktových rozhodnutí stručně pojmenuj tradeoff; u implementace drž odpověď krátkou a pokračuj tooly/kartami.`,
      planningMode
        ? `- Planning ZAP: plán jednou, pak implementace.`
        : `- Planning VYP: žádné plan karty — rovnou implementace tooly.`,
    ];
  }
  if (family === 'qwen' || family === 'gpt-oss') {
    return [
      ...common,
      `- Lokální coder režim: drž formát přesně, nepoužívej XML tool syntax ani neexistující tooly.`,
      autonomyMode === 'workspace'
        ? `- LIVE: workspace_replace/write = náhled. GitLab se nemění, dokud nevoláš codable_commit. To není chyba.`
        : `- NÁVRHY: nezapisuj; jen patch karta.`,
      `- Pokud potřebuješ starší kontext, použij codable_chat_search; pokud potřebuješ pattern, použij codable_library_search/read.`,
      `- Po tool resultu rovnou další tool — neopakuj plán ani dlouhý status report.`,
      planningMode ? null : `- Planning VYP: neposílej \`\`\`plan\`\`\` fence.`,
    ].filter((x): x is string => x != null);
  }
  // Gemma / Gemini / generic — často umí nativní FC, ale UI karty a diffy je brzdí.
  return [
    ...common,
    `- Preferuj NATIVNÍ function calling (codable_chat_file_read, codable_workspace_write, …) před textovými fence tooly.`,
    autonomyMode === 'workspace'
      ? `- Pro změnu části existujícího souboru použij codable_workspace_replace; celý soubor posílej do workspace_write jen když je nový nebo skutečně celý přepisuješ.`
      : `- Patch karty jen s KOMPLETNÍM novým obsahem souboru — unified diff (---/+++/@@) se neodmítne/neaplikuje.`,
    `- Po tool resultu hned pokračuj dalším tool call nebo krátkým shrnutím; nečekej na ruční potvrzení, když běží Live/Auto.`,
    `- Neopisuj celý soubor do chatu po read — rovnou piš/změň.`,
    planningMode
      ? `- Planning ZAP: plan karta jen jednou na začátku.`
      : `- Planning VYP: NEposílej \`\`\`plan\`\`\` — user už schválil práci (Live/Auto/pokyn). Rovnou dělej.`,
  ];
}

const LANG_INFO: Record<
  Lang,
  { label: string; summaryPrompt: string; summaryHeading: string }
> = {
  cs: {
    label: 'čeština',
    summaryHeading: 'Souhrn starší konverzace',
    summaryPrompt:
      'Jsi sumarizátor vývojářské konverzace. Vytvoř hutný souhrn v češtině se sekcemi: Cíl projektu, Provedené změny (soubor → co), Rozhodnutí a konvence, Otevřené úkoly. Zachovej přesné názvy souborů, komponent a domén. Max 400 slov, žádný úvod ani závěr.',
  },
  en: {
    label: 'English',
    summaryHeading: 'Older Conversation Summary',
    summaryPrompt:
      'You summarize a developer conversation. Create a dense English summary with sections: Project goal, Completed changes (file → what), Decisions and conventions, Open tasks. Preserve exact file names, components, and domains. Max 400 words, no intro or conclusion.',
  },
  it: {
    label: 'italiano',
    summaryHeading: 'Riepilogo della conversazione precedente',
    summaryPrompt:
      'Sei un riepilogatore di una conversazione di sviluppo. Crea un riepilogo denso in italiano con le sezioni: Obiettivo del progetto, Modifiche completate (file → cosa), Decisioni e convenzioni, Attività aperte. Mantieni esatti i nomi di file, componenti e domini. Massimo 400 parole, senza introduzione né conclusione.',
  },
  cvm: {
    label: 'Caveman',
    summaryHeading: 'Kámen paměť',
    summaryPrompt:
      'Summarize in playful Czech-Caveman style, but keep it understandable. Very short primitive sentences like: "Já kouk. Kámen bolí. Udělat potom." Sections: Cíl kámen, Hotovo kámen (file → co), Kmen pravidla, Další lov. Preserve exact file names, components, domains, code identifiers, URLs, and numbers. Max 180 words.',
  },
};

function normalizeUiLang(raw: unknown): Lang {
  return normalizeLang(raw);
}

export default function CodableChatPane(
  {
    projectId,
    orchestratorProjectId,
    slug,
    repo,
    seedPrompt,
    knowledge,
    onOpenConnectIde,
    onOpenLlmModels,
    compactChrome = false,
    onClose,
    onDeployed,
    previewUrl,
    prodPreviewUrl,
    gitlabUrl,
    publish,
    envStatus,
    workspaceInstance,
    prodInstance,
    feSplit,
    topology,
    projectStatus,
    lastCommitSha,
    lastPublishEvent,
  }: CodableChatPaneProps,
) {
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [showModelSettings, setShowModelSettings] = useState(false);
  const [showOwnModelSetup, setShowOwnModelSetup] = useState(false);
  const [showPromptTips, setShowPromptTips] = useState(false);
  const [liveCtx, setLiveCtx] = useState<UiContext>(() => getUiContextSnapshot());
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [codexSaveBusy, setCodexSaveBusy] = useState(false);
  const modelMenuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  useEffect(() => {
    const sync = () => setLiveCtx(getUiContextSnapshot());
    sync();
    window.addEventListener('acm-ui-context', sync);
    return () => window.removeEventListener('acm-ui-context', sync);
  }, []);
  const chatReloginCopy = {
    error: t('auth.sessionExpired', 'Přihlášení vypršelo; obnovuji autorizaci.'),
    remediation: t(
      'auth.sessionExpiredRemediation',
      'Po obnovení autorizace budete vráceni na tuto stránku.',
    ),
  };
  const preferredLang = normalizeUiLang(i18n.language);
  const preferredLangInfo = LANG_INFO[preferredLang];
  const [autonomyMode, setAutonomyMode] = useState<AutonomyMode>(() => {
    if (typeof window === 'undefined') return 'workspace';
    const key = `acm_codable_autonomy_${projectId}`;
    const schemaKey = `${key}_schema`;
    const v = window.localStorage.getItem(key);
    if (v === 'commit') {
      window.localStorage.setItem(key, 'workspace');
      window.localStorage.setItem(schemaKey, AUTONOMY_MODE_SCHEMA);
      return 'workspace';
    }
    // Volbu respektujeme až od schématu, ve kterém je Live default. Předtím
    // bylo `ask` to, co se do localStorage zapsalo samo při prvním otevření.
    if (window.localStorage.getItem(schemaKey) === AUTONOMY_MODE_SCHEMA) {
      if (v === 'ask' || v === 'workspace') return v;
    }
    return 'workspace';
  });
  // Default ON — plný cyklus (write → commit/push) musí být v prostředí dostupný,
  // ne schovaný za skrytým toggle. Explicitní "0" ho vypne.
  const [autoCommit, setAutoCommit] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const key = `acm_codable_autonomy_${projectId}`;
    const autoKey = `acm_codable_auto_commit_${projectId}`;
    const legacyMode = window.localStorage.getItem(key);
    const stored = window.localStorage.getItem(autoKey);
    if (stored === '1' || stored === 'true') return true;
    if (stored === '0' || stored === 'false') return false;
    if (legacyMode === 'commit') return true;
    return true;
  });
  // `autoCommitActive` je až za `autoAcceptOn` níže: G má smysl jen v režimu A.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const key = `acm_codable_autonomy_${projectId}`;
      window.localStorage.setItem(key, autonomyMode);
      window.localStorage.setItem(`${key}_schema`, AUTONOMY_MODE_SCHEMA);
    } catch { /* ignore */ }
  }, [autonomyMode, projectId]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        `acm_codable_auto_commit_${projectId}`,
        autoCommit ? '1' : '0',
      );
    } catch { /* ignore */ }
  }, [autoCommit, projectId]);
  // Auto-accept (Filip 2026-07-18): plan karty se prijmou a patch karty
  // aplikuji samy — jen na posledni assistant zprave, kazdy blok max jednou
  // (claim set prezije reload pres localStorage).
  //
  // 10. 9.: tlačítko A musí **přepínat schvalování jednotlivých tahů**, ne být
  // dekorací. Dřívší `autoModeOn = autoAccept || workspace` znamenalo, že v
  // odomčeném režimu se A ptalo vedle — karty šly auto vždy. Teď je A ten
  // přepínač: A = tah se schvaluje sám, M = každá karta chce klik.
  // Klíč je `_v2`: uložená `0` z éry, kdy A nic neovládalo, není rozhodnutí
  // uživatele. Stejný vzor jako `AUTONOMY_MODE_SCHEMA` výše.
  const [autoAccept, setAutoAccept] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(
      `acm_codable_auto_accept_v2_${projectId}`,
    ) !== '0';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        `acm_codable_auto_accept_v2_${projectId}`,
        autoAccept ? '1' : '0',
      );
    } catch { /* ignore */ }
  }, [autoAccept, projectId]);
  /** Zamčeno = agent jen konzultuje (Filip 10. 9.: „je to jen pojistka"). */
  const consultLock = autonomyMode !== 'workspace';
  /**
   * Jediná pravda o tom, jestli se karty tohohle tahu schvalují samy:
   * tlačítko A zapnuté a odomčeno. Za zámkem se auto-accept nepoužije ani při
   * uložené předvolbě — jinak by zámek byl dekorace. Předvolba se nemění.
   */
  const autoAcceptOn = autoAccept && !consultLock;
  /**
   * Git checkpoint (G) platí jen v A: v M člověk schvaluje každý tah a commit
   * leze z karty. Prompt, indikátor i karta tak vidí tutéž pravdu — dřív
   * `autoCommitActive` neviděl do schvalování a v M režimu model komitoval dál.
   */
  const autoCommitActive = autoCommit && autoAcceptOn;
  const autoDoneRef = useRef<Set<string>>(loadChatAutoDone(projectId));
  useEffect(() => {
    autoDoneRef.current = loadChatAutoDone(projectId);
  }, [projectId]);
  const claimAuto = useCallback((key: string): boolean => {
    if (autoDoneRef.current.has(key)) return false;
    autoDoneRef.current.add(key);
    persistChatAutoDone(projectId, autoDoneRef.current);
    return true;
  }, [projectId]);
  const wasAuto = useCallback((key: string): boolean => {
    return autoDoneRef.current.has(key);
  }, []);
  /** Tahy z této session v Auto/Live — karty zůstanou auto i po followupu (ne jen last assistant). */
  const liveAutoMsgIdsRef = useRef<Set<string>>(new Set());

  // ── God mode (admin unlock ACL k souborům chatu; agent neodemyká) ─────────
  // Odemčení: klik na nápis „Codable AI“ v hlavičce → ACM Modal (ne window.confirm).
  // Aktivní: nápis se změní na žlutý „God Mode“; další klik → zamčení (s potvrzením).
  const [godActive, setGodActive] = useState(false);
  const [godBusy, setGodBusy] = useState(false);
  const [godExpires, setGodExpires] = useState<string | null>(null);
  const [godConfirmOpen, setGodConfirmOpen] = useState(false);
  /** Continuity text po deployi — pošle se až !busy (send existuje). */
  const [pendingGodResume, setPendingGodResume] = useState<string | null>(null);
  const godResumeDoneRef = useRef(false);

  const refreshGodStatus = useCallback(async () => {
    try {
      const r = await api.mcp<{
        ok: boolean;
        active?: boolean;
        session?: { expires_at?: string; model_key?: string | null } | null;
      }>('codable.god_status', { slug }, { silent: true });
      if (r.ok) {
        setGodActive(!!r.active);
        setGodExpires(r.session?.expires_at ?? null);
      }
    } catch {
      setGodActive(false);
      setGodExpires(null);
    }
  }, [slug]);

  useEffect(() => {
    void refreshGodStatus();
    // Neaktivní God session není co hlídat každou minutu. Jednorázový stav po
    // mountu stačí; interval běží jen po odemčení a skončí s expirací.
    if (!godActive) return;
    const t = setInterval(() => void refreshGodStatus(), 60_000);
    return () => clearInterval(t);
  }, [refreshGodStatus, godActive]);

  // Po deployi / reloadu: vezmi pending resume (model + continuity).
  useEffect(() => {
    if (godResumeDoneRef.current || !slug) return;
    godResumeDoneRef.current = true;
    void (async () => {
      try {
        const r = await api.mcp<{
          ok: boolean;
          pending?: boolean;
          model_key?: string | null;
          continuity?: string;
        }>('codable.god_resume', { slug, action: 'take' }, { silent: true });
        if (!r.ok || !r.pending || !r.continuity) return;
        if (r.model_key) {
          // Jen session po God deploy — NEpřepisovat trvalou user pref
          setSelectedModel(r.model_key);
          try {
            window.localStorage.setItem(`codable.model.${slug}`, r.model_key);
          } catch { /* ignore */ }
        }
        setPendingGodResume(r.continuity);
        toast.success(
          r.model_key
            ? t(
              'codableChat.godResumeOkModel',
              'God resume: model obnoven, kontext se vrací',
            )
            : t(
              'codableChat.godResumeOk',
              'God resume: kontext po deployi se vrací',
            ),
        );
      } catch {
        /* žádný pending resume */
      }
    })();
  }, [slug, t]);

  /** Otevře ACM dialog (unlock/lock) — ne systémový window.confirm. */
  const requestGodToggle = useCallback(() => {
    if (godBusy) return;
    setGodConfirmOpen(true);
  }, [godBusy]);

  const applyGodSession = useCallback(async (action: 'unlock' | 'lock') => {
    if (godBusy) return;
    setGodBusy(true);
    try {
      const modelKey = selectedModel ?? null;
      const r = await api.mcp<{
        ok: boolean;
        error?: string;
        active?: boolean;
        prompt?: string | null;
        session?: { expires_at?: string } | null;
      }>('codable.god_session', {
        slug,
        action,
        ...(modelKey ? { model_key: modelKey } : {}),
      });
      if (!r.ok) {
        toast.error(
          r.error ??
            t('codableChat.godToggleFail', 'God mode se nepodařilo přepnout'),
        );
        return;
      }
      setGodActive(!!r.active);
      setGodExpires(r.session?.expires_at ?? null);
      setGodConfirmOpen(false);
      if (r.active && r.prompt) {
        // Inject mapu cest do chatu (user zpráva = viditelná, agent ji dostane v historii)
        enqueueSendRef.current?.(r.prompt);
        toast.success(
          t(
            'codableChat.godUnlocked',
            'God mode odemčen — agent má ACL k souborům chatu',
          ),
        );
      } else {
        toast.success(t('codableChat.godLocked', 'God mode zamčen'));
      }
    } catch (e) {
      toast.error(String((e as Error).message ?? e));
    } finally {
      setGodBusy(false);
    }
  }, [godBusy, selectedModel, slug, t]);

  // enqueueSend ještě nemusí existovat při unlock — ref
  const enqueueSendRef = useRef<((text: string) => void) | null>(null);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      document.querySelectorAll<HTMLDetailsElement>(
        'details[data-codable-menu][open]',
      ).forEach(
        (d) => {
          if (!target || !d.contains(target)) d.open = false;
        },
      );
      if (target && modelMenuRef.current?.contains(target)) return;
      setShowModelPicker(false);
      setShowModelSettings(false);
      setShowOwnModelSetup(false);
      setShowPromptTips(false);
    };
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      closeCodableMenus();
      setShowModelPicker(false);
      setShowModelSettings(false);
      setShowOwnModelSetup(false);
      setShowPromptTips(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Projektový kontext. Browser dostává stav, nikdy credential; chat stream
  // běží pod HttpOnly session a server každé tool volání váže na tento slug.
  const projectQ = useQuery({
    queryKey: ['codable-project-chat-context', slug],
    queryFn: () =>
      api.mcp<
        {
          ok: boolean;
          project?: { id?: string; slug?: string } | null;
          backend?: { type: string; enabled: boolean };
        }
      >('codable.project_get', { slug }),
    staleTime: 0,
    gcTime: 0,
  });
  const tokenMatchesProject = projectQ.data?.ok === true &&
    projectQ.data?.project?.slug === slug;
  const backendInfo = tokenMatchesProject && projectQ.data?.backend
    ? projectQ.data.backend
    : { type: 'supabase', enabled: true };
  // Planning mode toggle — když ON, LLM VŽDY posílá plán před actions.
  // Když OFF, drobné změny může dělat rovnou. Persist per projekt.
  // Default OFF — silné i slabší modely potřebují funkční tool loop, ne povinný plán.
  // Explicitní "1" planning zapne.
  const [planningMode, setPlanningMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const v = window.localStorage.getItem(`acm_codable_planning_${projectId}`);
    return v === null ? false : v === '1';
  });
  const togglePlanning = () => {
    setPlanningMode((v) => {
      const next = !v;
      try {
        window.localStorage.setItem(
          `acm_codable_planning_${projectId}`,
          next ? '1' : '0',
        );
      } catch { /* ignore */ }
      return next;
    });
  };
  // Attachments — user připojené soubory před odesláním. Přidají se jako
  // extra system context v příští user message.
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [voiceOn, setVoiceOn] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const planFileInputRef = useRef<HTMLInputElement>(null);
  const voiceRef = useRef<{ stop: () => void } | null>(null);
  const voiceWantRef = useRef(false);
  const voiceBaseRef = useRef('');
  const inputLiveRef = useRef('');
  const addFiles = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files);
    const parsed = await Promise.all(
      list.map(async (f): Promise<Attachment | null> => {
        // Obrázky → multimodal image block (base64), stejně jako snapshot.
        // Dřív se četly přes f.text() → binární paskvil jako 'text' attachment
        // a soubory >500 kB se zahazovaly TIŠE (Filip 2026-07-12: „příloha
        // nefunguje" — fotka je skoro vždy větší). Teď: image limit 4 MB + toast.
        const isImage = /^image\/(png|jpe?g|webp|gif)$/.test(f.type);
        if (isImage) {
          if (f.size > 4_000_000) {
            toast.error(
              t('codableChat.fileTooBig', {
                name: f.name,
                kb: Math.round(f.size / 1024),
              }),
            );
            return null;
          }
          const buf = new Uint8Array(await f.arrayBuffer());
          let bin = '';
          for (let i = 0; i < buf.length; i += 0x8000) {
            bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
          }
          const b64 = btoa(bin);
          return {
            name: f.name,
            kind: 'image',
            media_type: f.type,
            base64: b64,
            content: `data:${f.type};base64,${b64}`,
            size: f.size,
          };
        }
        // Ostatní binární typy neumíme poslat smysluplně → řekni to nahlas.
        const looksBinary = f.type && !f.type.startsWith('text/') &&
          !/json|xml|javascript|typescript|yaml|svg|csv|x-sh/.test(f.type);
        if (looksBinary) {
          toast.error(
            t('codableChat.fileUnsupported', { name: f.name, type: f.type }),
          );
          return null;
        }
        if (f.size > 500_000) {
          toast.error(
            t('codableChat.textTooBig', {
              name: f.name,
              kb: Math.round(f.size / 1024),
            }),
          );
          return null;
        }
        const content = await f.text();
        return { name: f.name, content, size: f.size };
      }),
    );
    const ok = parsed.filter((x): x is Attachment => !!x);
    if (ok.length > 0) {
      setAttachments((prev) => [...prev, ...ok]);
      toast.success(
        t('codableChat.attached', { names: ok.map((a) => a.name).join(', ') }),
      );
    }
  }, []);
  const removeAttachment = (idx: number) =>
    setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const stopVoice = useCallback(() => {
    voiceWantRef.current = false;
    voiceBaseRef.current = '';
    try {
      voiceRef.current?.stop();
    } catch { /* already stopped */ }
    voiceRef.current = null;
    setVoiceOn(false);
  }, []);

  const toggleVoice = useCallback(async () => {
    if (voiceRef.current) {
      stopVoice();
      return;
    }
    type Rec = {
      lang: string;
      interimResults: boolean;
      continuous: boolean;
      onresult: ((ev: {
        results: ArrayLike<{ isFinal?: boolean; 0?: { transcript?: string } }>;
      }) => void) | null;
      onerror: ((ev: { error?: string }) => void) | null;
      onend: (() => void) | null;
      start: () => void;
      stop: () => void;
    };
    const w = window as unknown as {
      SpeechRecognition?: new () => Rec;
      webkitSpeechRecognition?: new () => Rec;
    };
    const SR = w.SpeechRecognition ?? w.webkitSpeechRecognition;
    if (!SR) {
      toast.error(t('codableChat.voiceUnsupported', 'Hlas v tomhle prohlížeči není.'));
      return;
    }
    if (!window.isSecureContext) {
      toast.error(t('codableChat.voiceNeedHttps', 'Hlas chce HTTPS.'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((tr) => tr.stop());
    } catch {
      toast.error(t('codableChat.voiceDenied', 'Mikrofon není povolený.'));
      return;
    }
    const rec = new SR();
    const lang = (i18n.language || 'cs').toLowerCase();
    rec.lang = lang.startsWith('it')
      ? 'it-IT'
      : lang.startsWith('en')
      ? 'en-US'
      : 'cs-CZ';
    rec.interimResults = true;
    rec.continuous = true;
    voiceBaseRef.current = inputLiveRef.current;
    rec.onresult = (ev) => {
      if (!voiceWantRef.current) return;
      let finalText = '';
      let interim = '';
      for (let i = 0; i < ev.results.length; i++) {
        const row = ev.results[i];
        const bit = row?.[0]?.transcript ?? '';
        if (row && 'isFinal' in row && row.isFinal) finalText += bit;
        else interim += bit;
      }
      const spoken = `${finalText} ${interim}`.replace(/\s+/g, ' ').trim();
      const base = voiceBaseRef.current.trim();
      setInput(base ? `${base} ${spoken}` : spoken);
    };
    rec.onerror = (ev) => {
      const err = String((ev as { error?: string }).error || '');
      stopVoice();
      if (err === 'aborted' || err === 'no-speech') return;
      if (err === 'not-allowed' || err === 'service-not-allowed') {
        toast.error(t('codableChat.voiceDenied', 'Mikrofon není povolený.'));
        return;
      }
      if (err === 'network') {
        toast.error(t('codableChat.voiceNetwork', 'Hlas potřebuje síť (rozpoznání řeči).'));
        return;
      }
      toast.error(t('codableChat.voiceFail', 'Hlas selhal ({{err}}).', { err: err || '?' }));
    };
    rec.onend = () => {
      if (!voiceWantRef.current) {
        stopVoice();
        return;
      }
      try {
        rec.start();
      } catch {
        stopVoice();
      }
    };
    voiceWantRef.current = true;
    voiceRef.current = rec;
    setVoiceOn(true);
    try {
      rec.start();
    } catch {
      stopVoice();
      toast.error(t('codableChat.voiceFail', 'Hlas selhal ({{err}}).', { err: 'start' }));
    }
  }, [i18n.language, stopVoice, t]);
  useEffect(() => () => stopVoice(), [stopVoice]);

  // Snapshot preview přes headless chromium (codable.preview_screenshot).
  // Filip 2026-07-10: „potřebujeme volání co udělá screenshot toho projektu."
  // User klik → PNG se přidá jako image attachment; při dalším send() půjde
  // do LLM jako multimodal content block (Anthropic 'image' / OpenAI 'image_url').
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const takeSnapshot = useCallback(async () => {
    if (snapshotBusy) return;
    setSnapshotBusy(true);
    try {
      const r = await api.mcp<{
        ok: boolean;
        error?: string;
        failure_kind?: string;
        action_required?: string;
        diagnostics?: Array<{ step?: string; ok?: boolean; message?: string }>;
        png_base64?: string;
        size_bytes?: number;
        url?: string;
      }>('codable.preview_screenshot', { slug });
      if (!r.ok || !r.png_base64) {
        toast.error(
          t('codableChat.snapshotFailed', 'Snapshot selhal: {{detail}}', {
            detail: snapshotErrorText(r),
          }),
          { duration: 9000 },
        );
        return;
      }
      const name = `snapshot-${new Date().toISOString().slice(11, 19).replace(/:/g, '-')}.png`;
      setAttachments((prev) => [...prev, {
        name,
        kind: 'image',
        media_type: 'image/png',
        base64: r.png_base64!,
        content: `data:image/png;base64,${r.png_base64}`,
        size: r.size_bytes ?? 0,
      }]);
      toast.success(
        t('codableChat.snapshotAttached', {
          kb: Math.round((r.size_bytes ?? 0) / 1024),
        }),
      );
    } catch (e) {
      toast.error(t('codableChat.snapshotError', 'Snapshot chyba: {{msg}}', { msg: (e as Error).message }));
    } finally {
      setSnapshotBusy(false);
    }
  }, [slug, snapshotBusy]);

  // Načti dostupné chat profily — agregátor codable_llm_models_list (vrstva
  // 1 registr + vrstva 2 tenant/projekt allow-list + vrstva 3 vlastní BYOK
  // profily). Codex OAuth se řeší zvlášť níže (vlastní credential jako BYOK).
  const profilesQ = useQuery({
    queryKey: ['codable-llm-profiles', projectId],
    queryFn: () =>
      api.mcp<{
        profiles?: ChatProfile[];
        defaults?: {
          tenant_profile_id?: string | null;
          project_profile_id?: string | null;
        };
      }>('codable.llm_models_list', {
        project_id: projectId,
      }),
    enabled: !!projectId,
    staleTime: 60_000,
  });
  const codexPrefQ = useQuery({
    queryKey: ['codable-user-codex-profile'],
    queryFn: () =>
      api.mcpSession<
        {
          connected?: boolean;
          label?: string | null;
          model?: string;
          needs_reconnect?: boolean;
        }
      >('codable.codex_status', {}),
    staleTime: 60_000,
    retry: false,
  });
  // User model pref — server-side ui_prefs (přežije refresh/device).
  // Shape: { profile_key?: string, by_project?: Record<projectId, profile_key> }
  const modelPrefQ = useQuery({
    queryKey: ['codable-preferred-model'],
    queryFn: () =>
      api.mcpSession<{
        value?: {
          profile_key?: string;
          by_project?: Record<string, string>;
        } | null;
      }>(
        'ui.prefs_get',
        { key: CODABLE_MODEL_PREF_KEY },
      ),
    staleTime: 60_000,
    retry: false,
  });
  // Filtrování (registr codable_enabled + vrstva 2 allow-list) už proběhlo
  // server-side v codable_llm_models_list — tady jen převezmeme výsledek.
  const allProfiles = profilesQ.data?.profiles ?? [];
  const platformProfiles = allProfiles;
  const modelDefaults = profilesQ.data?.defaults;
  const codexPref = codexPrefQ.data;
  const codexNeedsReconnect = codexPref?.connected === true &&
    codexPref.needs_reconnect === true;
  const userCodexProfile: ChatProfile | null = codexPref?.connected === true
    ? {
      id: CODABLE_CODEX_PROFILE_ID,
      label: codexPref.label ?? 'OpenAI Codex',
      model: codexPref.model ?? 'gpt-5.3-codex',
      enabled: true,
      agent_family: 'codex',
      preferred_patch_mode: 'unified-diff',
      context_budget: 400000,
      vision: true,
      source: 'codable-user',
      has_token: true,
    }
    : null;
  const profiles = userCodexProfile ? [userCodexProfile, ...platformProfiles] : platformProfiles;
  const selectedProfile = profiles.find((p) => profileKey(p) === selectedModel) ??
    profiles.find((p) => profileLabel(p) === selectedModel || p.model === selectedModel) ??
    profiles[0];
  const contextBudget = selectedProfile?.context_budget ?? CTX_BUDGET;

  // Hydratace jen jednou po načtení prefs+profiles — nikdy nepřepisovat user pref
  // race: profiles dřív než prefs → dřív se bralo profiles[0] a uložilo to do prefs.
  const modelHydratedRef = useRef(false);
  useEffect(() => {
    modelHydratedRef.current = false;
  }, [projectId]);

  useEffect(() => {
    if (modelHydratedRef.current) return;
    if (profilesQ.isLoading || modelPrefQ.isLoading) return;
    if (profiles.length === 0) return;

    const find = (key?: string | null): ChatProfile | null => {
      if (!key || typeof key !== 'string') return null;
      return profiles.find((p) =>
        profileKey(p) === key || p.id === key || p.model === key ||
        profileLabel(p) === key
      ) ?? null;
    };

    const prefs = modelPrefQ.data?.value;
    // 1) user (per-project, pak globální)  2) project chat_profile  3) tenant default  4) first
    const userKey = prefs?.by_project?.[projectId] ?? prefs?.profile_key ??
      null;
    // localStorage cache (rychlý refresh) — jen když sedí na server prefs nebo server ještě nemá by_project
    let cacheKey: string | null = null;
    try {
      cacheKey = window.localStorage.getItem(`codable.model.${projectId}`);
    } catch { /* ignore */ }

    const resolved = find(userKey) ??
      find(cacheKey) ??
      find(modelDefaults?.project_profile_id) ??
      find(modelDefaults?.tenant_profile_id) ??
      profiles[0]!;

    const key = profileKey(resolved);
    setSelectedModel(key);
    try {
      window.localStorage.setItem(`codable.model.${projectId}`, key);
    } catch { /* ignore */ }
    modelHydratedRef.current = true;
  }, [
    profiles,
    profilesQ.isLoading,
    modelPrefQ.isLoading,
    modelPrefQ.data,
    modelDefaults,
    projectId,
  ]);

  // Když hydratovaný model zmizí z listu (odebrán z allowlistu) — spadni na kaskádu, NE na zápis prefs.
  useEffect(() => {
    if (!modelHydratedRef.current) return;
    if (profiles.length === 0) return;
    if (
      selectedModel && profiles.some((p) => profileKey(p) === selectedModel)
    ) return;

    const find = (key?: string | null): ChatProfile | null => {
      if (!key) return null;
      return profiles.find((p) => profileKey(p) === key || p.id === key || p.model === key) ?? null;
    };
    const prefs = modelPrefQ.data?.value;
    const resolved = find(prefs?.by_project?.[projectId]) ??
      find(prefs?.profile_key) ??
      find(modelDefaults?.project_profile_id) ??
      find(modelDefaults?.tenant_profile_id) ??
      profiles[0]!;
    setSelectedModel(profileKey(resolved));
  }, [profiles, selectedModel, modelPrefQ.data, modelDefaults, projectId]);

  const qcModels = useQueryClient();
  const pickModel = useCallback((key: string) => {
    setSelectedModel(key);
    try {
      window.localStorage.setItem(`codable.model.${projectId}`, key);
    } catch { /* ignore */ }
    const prev = modelPrefQ.data?.value ?? {};
    const next = {
      profile_key: key,
      by_project: { ...(prev.by_project ?? {}), [projectId]: key },
    };
    qcModels.setQueryData(['codable-preferred-model'], { value: next });
    api.mcpSession('ui.prefs_set', {
      key: CODABLE_MODEL_PREF_KEY,
      value: next,
    }).catch(() => {});
  }, [projectId, modelPrefQ.data, qcModels]);

  const disconnectCodexProfile = useCallback(async () => {
    setCodexSaveBusy(true);
    try {
      await api.mcpSession('codable.codex_disconnect', {});
      toast.success('Codex profil odpojen');
      await codexPrefQ.refetch();
      if (selectedModel === CODABLE_CODEX_PROFILE_ID) setSelectedModel(null);
    } catch (e) {
      toast.error(
        t('codableChat.codexDisconnectFail', { msg: (e as Error).message }),
      );
    } finally {
      setCodexSaveBusy(false);
    }
  }, [codexPrefQ, selectedModel]);

  const startCodexConnect = useCallback(() => {
    const returnTo = `${location.pathname}${location.search}`;
    window.location.href = `/apiext/codable/codex-oauth-login?return=${
      encodeURIComponent(returnTo)
    }`;
  }, []);

  // Po návratu z OAuth (viz acm-link capture) — ?codex_connected=1 nebo
  // ?codex_oauth_error=... v URL. Toast + refetch + vyčistit query string.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connected = params.get('codex_connected');
    const error = params.get('codex_oauth_error');
    if (!connected && !error) return;
    if (connected) {
      toast.success(t('codableChat.codexConnected'));
      void codexPrefQ.refetch();
    } else if (error) {
      toast.error(`Codex OAuth selhalo: ${error}`);
    }
    params.delete('codex_connected');
    params.delete('codex_oauth_error');
    const query = params.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, {
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Persist chat history per projekt (localStorage per projectId).
  // Cross-device persist by chtěl backend — zatím per-browser stačí.
  const historyKey = `acm_codable_chat_${projectId}`;
  const planKey = `acm_codable_plan_${projectId}`;
  const [activePlan, setActivePlan] = useState<ActivePlan | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const saved = window.localStorage.getItem(planKey);
      return saved ? JSON.parse(saved) as ActivePlan : null;
    } catch {
      return null;
    }
  });
  const [planHistory, setPlanHistory] = useState<PlanHistoryItem[]>([]);
  const [showPlanHistory, setShowPlanHistory] = useState(false);
  const [expandedPlanHistoryId, setExpandedPlanHistoryId] = useState<
    string | null
  >(null);
  const [planEditorOpen, setPlanEditorOpen] = useState(false);
  const [planEditorMd, setPlanEditorMd] = useState('');
  // Trvalý plan panel (Filip 2026-07-18: "je v liště, neplní se, neodškrtává,
  // je to k ničemu") — dřív jediná viditelnost byl "Plán X/Y" button v
  // dropdownu s checklistem jen jako HTML title tooltip. Panel nad zprávami
  // ukazuje živý checklist, dokud je plán aktivní; sbalitelný pro delší plány.
  const [planPanelExpanded, setPlanPanelExpanded] = useState(true);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (activePlan) {
        window.localStorage.setItem(planKey, JSON.stringify(activePlan));
      } else window.localStorage.removeItem(planKey);
    } catch { /* quota */ }
  }, [activePlan, planKey]);
  // Ref mirror plánu — send() běží i rekurzivně (setTimeout followupy) se
  // STAROU closure: čtení `activePlan` z closure injektovalo do LLM zastaralý
  // stav (špatné počty hotových kroků, minuté completion 3/3) a merge
  // step_done zahazoval dřívější kroky (Filip 2026-07-12: „fantazíruje o tom,
  // kolik udělal kroků"). Veškeré čtení/zápis plánu uvnitř send jde přes ref.
  const activePlanRef = useRef<ActivePlan | null>(activePlan);
  const updatePlan = useCallback((
    p: ActivePlan | null,
    opts: {
      status?: PlanHistoryStatus;
      source?: PlanHistorySource;
      title?: string;
    } = {},
  ) => {
    const previous = activePlanRef.current;
    const next = p
      ? {
        ...p,
        historyId: p.historyId ?? makePlanHistoryId(),
        source: opts.source ?? p.source ?? 'chat' as const,
        title: opts.title ?? p.title,
      }
      : null;
    activePlanRef.current = next;
    setActivePlan(next);
    if (next) {
      const item: PlanHistoryItem = {
        id: next.historyId!,
        title: next.title ?? next.steps[0] ?? null,
        source: next.source ?? 'chat',
        status: 'active',
        steps: next.steps,
        done: next.done,
        created_at: next.createdAt,
        updated_at: new Date().toISOString(),
        completed_at: null,
        cancelled_at: null,
      };
      setPlanHistory((prev) => [item, ...prev.filter((x) => x.id !== item.id)].slice(0, 50));
    } else if (previous?.historyId) {
      const status = opts.status ?? 'cancelled';
      setPlanHistory((prev) =>
        prev.map((x) =>
          x.id === previous.historyId
            ? {
              ...x,
              status,
              done: previous.done,
              updated_at: new Date().toISOString(),
              completed_at: status === 'completed' ? new Date().toISOString() : x.completed_at,
              cancelled_at: status === 'cancelled' ? new Date().toISOString() : x.cancelled_at,
            }
            : x
        )
      );
    }
    // Server-side persist — cross-device stav + agent ho vidí přes
    // codable_chat_search (active_plan v odpovědi). Best-effort pro
    // průběžné kroky, ALE ukončení (cancel/complete) musí selhání ohlásit:
    // lokální stav je null hned (optimisticky), takže bez ohlášení chyby
    // uživatel vidí "smazáno" — jenže při dalším loadu projektu sync efekt
    // čte DB jako autoritu a starý plán "vzkřísí" (Filip 2026-07-19: "plán je
    // smazaný, ale dole v ikoně plánu je pořád vidět").
    const endingPlan = next === null;
    api.mcp<{
      ok: boolean;
      error?: string;
      active_plan?: ActivePlan | null;
      plan_history?: unknown[];
    }>('codable.chat_history', {
      slug,
      action: 'set_plan',
      ...(next ? { plan: next, source: next.source, title: next.title } : {}),
      ...(opts.status ? { status: opts.status } : {}),
    })
      .then((r) => {
        if (!r.ok) {
          if (endingPlan) {
            toast.error(
              t(
                'codableChat.planCancelPersistFail',
                'Zrušení plánu se neuložilo na server — může se po refreshi vrátit. Zkus to znovu.',
              ),
            );
          }
          return;
        }
        if (r.active_plan) {
          activePlanRef.current = r.active_plan;
          setActivePlan(r.active_plan);
        }
        const hist = normalizePlanHistory(r.plan_history);
        if (hist.length > 0) setPlanHistory(hist);
      })
      .catch(() => {
        if (endingPlan) {
          toast.error(
            t(
              'codableChat.planCancelPersistFail',
              'Zrušení plánu se neuložilo na server — může se po refreshi vrátit. Zkus to znovu.',
            ),
          );
        }
      });
  }, [slug]);
  const importPlanMarkdown = useCallback(async (
    markdown: string,
    source: PlanHistorySource,
    title?: string,
    filename?: string,
  ): Promise<ActivePlan | null> => {
    const r = await api.mcp<{
      ok: boolean;
      error?: string;
      active_plan?: ActivePlan | null;
      plan_history?: unknown[];
    }>('codable.plan_import_md', { slug, markdown, source, title, filename });
    if (!r.ok || !r.active_plan) {
      toast.error(r.error ?? t('codableChat.planImportFail'));
      return null;
    }
    activePlanRef.current = r.active_plan;
    setActivePlan(r.active_plan);
    const hist = normalizePlanHistory(r.plan_history);
    if (hist.length > 0) setPlanHistory(hist);
    return r.active_plan;
  }, [slug]);
  const importPlanMd = useCallback(async (file: File) => {
    if (file.size > PLAN_MD_MAX_BYTES) {
      toast.error(
        t('codableChat.planTooBig', {
          name: file.name,
          kb: Math.round(PLAN_MD_MAX_BYTES / 1024),
        }),
      );
      return;
    }
    const md = await file.text();
    const plan = await importPlanMarkdown(
      md,
      'import',
      file.name.replace(/\.(md|markdown)$/i, ''),
      file.name,
    );
    if (plan) {
      toast.success(
        t('codableChat.planImported', {
          done: plan.done.length,
          total: plan.steps.length,
        }),
      );
    }
  }, [importPlanMarkdown]);
  const exportPlanMd = useCallback(async () => {
    const plan = activePlanRef.current;
    if (!plan) {
      toast.info(t('codableChat.noActivePlanExport'));
      return;
    }
    const r = await api.mcp<
      { ok: boolean; error?: string; markdown?: string; filename?: string }
    >(
      'codable.plan_export_md',
      { slug },
    );
    if (!r.ok || !r.markdown) {
      toast.error(r.error ?? t('codableChat.planExportFail'));
      return;
    }
    const blob = new Blob([r.markdown], {
      type: 'text/markdown;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = r.filename ?? `${slug}-codable-plan.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(t('codableChat.planExported'));
  }, [slug]);
  // Export konkrétní položky z Historie plánů — na rozdíl od exportPlanMd
  // (jen aktivní plán, přes backend) tady data máme rovnou v paměti, žádný
  // roundtrip netřeba (Filip 2026-07-19: "menu... jen zobrazí seznam
  // který je k ničemu").
  const exportPlanHistoryItem = useCallback((item: PlanHistoryItem) => {
    const markdown = formatPlanHistoryItemMarkdown(item);
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const base = (item.title || 'codable-plan').toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'codable-plan';
    a.download = `${slug}-${base}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(t('codableChat.planExported'));
  }, [slug]);
  const reactivatePlanHistoryItem = useCallback(
    async (item: PlanHistoryItem) => {
      if (
        activePlan && activePlan.done.length < activePlan.steps.length &&
        !(await themedConfirm(
          t(
            'codableChat.reactivatePlanConfirm',
            'Nahradit aktuálně aktivní plán tímhle z historie? Rozpracovaný plán se zruší.',
          ),
        ))
      ) {
        return;
      }
      const markdown = formatPlanHistoryItemMarkdown(item);
      const plan = await importPlanMarkdown(
        markdown,
        'manual',
        item.title ?? undefined,
      );
      if (plan) {
        toast.success(
          t('codableChat.planReactivated', {
            done: plan.done.length,
            total: plan.steps.length,
          }),
        );
        setShowPlanHistory(false);
      }
    },
    [activePlan, importPlanMarkdown],
  );
  const openPlanEditor = useCallback(() => {
    const plan = activePlanRef.current;
    if (!plan) {
      toast.info(t('codableChat.noActivePlanEdit'));
      return;
    }
    setPlanEditorMd(formatActivePlanMarkdown(plan));
    setPlanEditorOpen(true);
  }, []);
  const savePlanEditor = useCallback(() => {
    const parsed = parsePlanMarkdown(planEditorMd);
    if (parsed.steps.length === 0) {
      toast.error(t('codableChat.planNoSteps'));
      return;
    }
    const current = activePlanRef.current;
    const title = planTitleFromMarkdown(planEditorMd, current?.title);
    updatePlan({
      steps: parsed.steps,
      done: parsed.done,
      createdAt: current?.createdAt ?? new Date().toISOString(),
      historyId: current?.historyId,
      source: 'manual',
      title,
    }, { source: 'manual', title });
    setPlanEditorOpen(false);
    toast.success(t('codableChat.planEdited'));
  }, [planEditorMd, updatePlan]);
  const [messages, setMessages] = useState<Message[]>(() => {
    const local = readLocalHistory(historyKey);
    if (local?.messages.length) return local.messages;
    const now = new Date().toISOString();
    return seedPrompt
      ? [
        { id: uid(), role: 'user', content: seedPrompt, createdAt: now },
        {
          id: uid(),
          role: 'assistant',
          createdAt: now,
          content: t('codableChat.welcomeNew', { slug }),
        },
      ]
      : [
        {
          id: uid(),
          role: 'assistant',
          createdAt: now,
          content: t('codableChat.welcomeBack', { slug }),
        },
      ];
  });
  const [input, setInput] = useState('');
  inputLiveRef.current = input;
  // Výsledky fence nástrojů se renderují v panelu nástrojů agentova tahu, ne
  // jako samostatné řádky v proudu — proto mapování na id assistant zprávy.
  const hostedToolOutputs = useMemo(() => hostToolOutputs(messages), [messages]);
  const promptHistoryKey = `acm_codable_prompt_history_${projectId}`;
  const [promptHistory, setPromptHistory] = useState<string[]>(() =>
    readPromptHistory(promptHistoryKey)
  );
  const [promptHistoryIndex, setPromptHistoryIndex] = useState<number | null>(
    null,
  );
  const promptDraftRef = useRef('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);
  const abortRef = useRef<AbortController | null>(null);
  // Tahy na serveru (stream/turns.ts): id běžícího tahu pro přerušení a stabilní
  // id konverzace = klíč přepisu na serveru (nové po „Vyčistit").
  const turnIdRef = useRef<string | null>(null);
  const conversationIdRef = useRef<string>(loadConversationId(historyKey));
  // Platformní tahy (stream/turns.ts follow-up): server po dokončení operace,
  // kterou agent spustil, zařadí do konverzace tah s výsledkem. UI ho
  // nezaložilo, takže se o něm dozví dotazem na konverzaci — po eventu
  // `follow_up_scheduled` každých 10 s, jinak po načtení a při návratu do okna.
  // Filip 2026-09-12: „přijde mu potom samo upozornění? … asi není."
  const seenPlatformTurnsRef = useRef<Set<string>>(readSeenPlatformTurns(historyKey));
  const platformPollRef = useRef<{ timer: number | null; until: number } | null>(null);
  const attachPlatformTurn = useCallback(async (turn: PlatformTurn) => {
    if (seenPlatformTurnsRef.current.has(turn.id)) return;
    seenPlatformTurnsRef.current.add(turn.id);
    rememberSeenPlatformTurn(historyKey, seenPlatformTurnsRef.current);
    const userId = `pe-${turn.id}`;
    const aId = `pa-${turn.id}`;
    const content = typeof turn.user_message?.content === 'string'
      ? turn.user_message.content
      : PLATFORM_EVENT_PREFIX;
    setMessages((m) => [
      ...m,
      {
        id: userId,
        role: 'user',
        content,
        platformEvent: true,
        createdAt: turn.created_at ?? new Date().toISOString(),
      },
      { id: aId, role: 'assistant', content: '', pending: true, createdAt: new Date().toISOString() },
    ]);
    const res = openUiChatTurnEvents(turn.id, 0);
    const reader = res.body?.getReader();
    let acc = '';
    let endStatus: string = 'done';
    const visibleThink = new ThinkVisibleBuffer();
    if (reader) {
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split(/\n/);
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const data = line.slice(5).trim();
          if (!data || data === '[DONE]') continue;
          try {
            const evt = JSON.parse(data) as {
              type?: string;
              text?: string;
              name?: string;
              message?: string;
              status?: string;
              result?: unknown;
            };
            if (evt.type === 'tool_call' && evt.name) {
              const name = evt.name;
              setMessages((m) =>
                m.map((x) =>
                  x.id === aId
                    ? { ...x, thinking: false, nativeTools: [...(x.nativeTools ?? []), { name }] }
                    : x
                )
              );
            } else if (evt.type === 'tool_result' && evt.name) {
              const r = evt.result as { ok?: boolean; error?: string } | undefined;
              const ok = !(r && (r.error !== undefined || r.ok === false));
              const toolName = evt.name;
              if (ok && /commit|orchestrator[._]sync|release_switch|promote/.test(toolName)) {
                onDeployed?.();
              }
              setMessages((m) =>
                m.map((x) => {
                  if (x.id !== aId) return x;
                  let marked = false;
                  return {
                    ...x,
                    nativeTools: (x.nativeTools ?? []).map((tc) => {
                      if (!marked && tc.ok === undefined && tc.name === toolName) {
                        marked = true;
                        return { ...tc, ok };
                      }
                      return tc;
                    }),
                  };
                })
              );
            } else if (evt.type === 'thinking') {
              setMessages((m) => m.map((x) => x.id === aId ? { ...x, thinking: true } : x));
            } else if (evt.type === 'text' && evt.text) {
              const visible = visibleThink.push(evt.text);
              if (visible) {
                acc += visible;
                setMessages((m) =>
                  m.map((x) => x.id === aId ? { ...x, content: acc, thinking: false } : x)
                );
              }
            } else if (evt.type === 'error' && evt.message) {
              acc += `\n\n**Chyba:** ${evt.message}`;
            } else if (evt.type === 'turn_end') {
              endStatus = evt.status ?? 'done';
            }
          } catch { /* ne-JSON řádek */ }
        }
      }
    }
    acc += visibleThink.finish();
    const ended = endStatus !== 'done'
      ? `\n\n*${t('codableChat.turnEnded', { status: endStatus, defaultValue: 'Tah skončil: {{status}}' })}*`
      : '';
    setMessages((m) =>
      m.map((x) =>
        x.id === aId
          ? { ...x, content: (acc + ended).trim(), pending: false, thinking: false }
          : x
      )
    );
  }, [historyKey, onDeployed, t]);

  /** Zeptá se serveru na platformní tahy konverzace; true = něco nového přišlo. */
  const pollPlatformTurns = useCallback(async (): Promise<boolean> => {
    const conversationId = conversationIdRef.current;
    const r = await fetch(
      `/ui/capabilities/chat-turns/conversation/${encodeURIComponent(conversationId)}`,
      { credentials: 'include' },
    );
    if (!r.ok) return false;
    const data = await r.json().catch(() => null) as { turns?: PlatformTurn[] } | null;
    // Zařazený tah ještě nemá eventy (route by vrátila turn_end queued) — počká se.
    const fresh = (data?.turns ?? [])
      .filter((turn) =>
        turn.request?.origin === 'platform-event' && turn.status !== 'queued' &&
        !seenPlatformTurnsRef.current.has(turn.id)
      )
      .sort((a, b) => String(a.created_at ?? '').localeCompare(String(b.created_at ?? '')));
    for (const turn of fresh) await attachPlatformTurn(turn);
    return fresh.length > 0;
  }, [attachPlatformTurn]);

  /** Po `follow_up_scheduled`: ptát se každých 10 s, než platformní tah dorazí (max ~65 min). */
  const startPlatformPolling = useCallback(() => {
    if (platformPollRef.current?.timer) window.clearTimeout(platformPollRef.current.timer);
    const until = Date.now() + 65 * 60_000;
    const tick = async () => {
      const found = await pollPlatformTurns().catch(() => false);
      if (found || Date.now() > until) {
        platformPollRef.current = null;
        return;
      }
      platformPollRef.current = { timer: window.setTimeout(tick, 10_000), until };
    };
    platformPollRef.current = { timer: window.setTimeout(tick, 5_000), until };
  }, [pollPlatformTurns]);
  useEffect(() => {
    // Návrat do okna: platformní tah mohl přijít, když nikdo nekoukal.
    const onFocus = () => {
      pollPlatformTurns().catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      if (platformPollRef.current?.timer) window.clearTimeout(platformPollRef.current.timer);
    };
  }, [pollPlatformTurns]);
  // Zařazená zpráva (fronta „klasicky") — jen pro zobrazení; pravdu drží sendAfterBusyRef.
  const [queuedPreview, setQueuedPreview] = useState<string | null>(null);
  const chatSessionIdRef = useRef<string | null>(null);
  /** Followup fronta — když auto-accept/plan/patch střílí send() ještě během busy, nezahodí se. */
  const sendAfterBusyRef = useRef<string | null>(null);
  const sendAfterBusyExtrasRef = useRef<
    | { toolMeta?: ToolOutputMeta; images?: Attachment[] }
    | null
  >(null);
  // Unmount (swipe na náhled, zpět z práce) NESMÍ zabít běžící tah.

  // Composer textarea auto-grow (max ~6 řádků ≈ 144px)
  const taRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 144)}px`;
  }, [input]);
  useEffect(() => {
    setPromptHistory(readPromptHistory(promptHistoryKey));
    setPromptHistoryIndex(null);
    promptDraftRef.current = '';
  }, [promptHistoryKey]);

  const rememberPrompt = useCallback((value: string) => {
    const normalized = value.trim();
    if (!normalized) return;
    setPromptHistory((prev) => {
      const next = [...prev.filter((item) => item !== normalized), normalized]
        .slice(-50);
      try {
        window.localStorage.setItem(promptHistoryKey, JSON.stringify(next));
      } catch {
        // Prompt history is only a convenience; ignore quota/private-mode failures.
      }
      return next;
    });
  }, [promptHistoryKey]);

  const setComposerFromHistory = useCallback((value: string) => {
    setInput(value);
    requestAnimationFrame(() => {
      const el = taRef.current;
      if (!el) return;
      const end = value.length;
      el.focus();
      el.setSelectionRange(end, end);
    });
  }, []);

  // Smart autoscroll — drž se dna, ale neskákej když je user odscrollovaný
  // nahoru (> 120px od konce). Místo toho floating "dolů" tlačítko.
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickRef.current = dist <= 120;
    setShowScrollDown(dist > 120);
  };
  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    stickRef.current = true;
    setShowScrollDown(false);
  };
  useEffect(() => {
    if (stickRef.current) {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // DB persist (Filip 2026-07-10: chat nesmí zmizet při refreshi).
  // Mount: načti z DB — když tam je novější/neprázdná historie, přepiš lokální.
  const dbLoadedRef = useRef(false);
  const lastPersistedHistoryRef = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    dbLoadedRef.current = false;
    lastPersistedHistoryRef.current = null;
    (async () => {
      try {
        const r = await api.mcp<
          {
            ok: boolean;
            messages?: Message[] | null;
            active_plan?: ActivePlan | null;
            plan_history?: unknown[];
          }
        >(
          'codable.chat_history',
          { slug, action: 'get' },
        );
        if (cancelled || !r.ok) return;
        const remoteMessages = Array.isArray(r.messages)
          ? normalizeRestoredMessages(r.messages)
          : [];
        const local = readLocalHistory(historyKey);
        const localMessages = local?.messages ?? [];
        const keepLocal = localMessages.length > 0 &&
          (
            local?.hadPending === true ||
            remoteMessages.length === 0 ||
            localMessages.length > remoteMessages.length
          );
        if (keepLocal) {
          setMessages(localMessages);
          const clean = localMessages.filter((m) => !m.pending);
          const serialized = JSON.stringify(clean);
          lastPersistedHistoryRef.current = JSON.stringify(remoteMessages);
          if (
            clean.length > 0 &&
            (remoteMessages.length === 0 ||
              clean.length > remoteMessages.length)
          ) {
            // Optimistic dedupe: rollback only when this exact write fails.
            lastPersistedHistoryRef.current = serialized;
            api.mcp('codable.chat_history', {
              slug,
              action: 'set',
              messages: clean,
            })
              .catch(() => {
                if (lastPersistedHistoryRef.current === serialized) {
                  lastPersistedHistoryRef.current = null;
                }
              });
          }
        } else if (remoteMessages.length > 0) {
          lastPersistedHistoryRef.current = JSON.stringify(remoteMessages);
          setMessages(remoteMessages);
        } else {
          lastPersistedHistoryRef.current = JSON.stringify([]);
        }
        // DB je autorita pro životní cyklus plánu (updatePlan píše DB i
        // localStorage). Sladit lokál podle DB: plán v DB → adoptuj; DB bez
        // plánu + lokál má (stará veš z localStorage jiné session/zařízení) →
        // uklidit, ať nezůstane duch, o kterém agent „neví" (injektoval by se
        // stav, který v DB ani historii není). Legitimní běžící plán je VŽDY
        // i v DB, takže se nesmaže — maže se jen skutečný sirotek.
        if (r.active_plan) {
          activePlanRef.current = r.active_plan;
          setActivePlan(r.active_plan);
        } else if (activePlanRef.current) {
          activePlanRef.current = null;
          setActivePlan(null);
          try {
            window.localStorage.removeItem(planKey);
          } catch { /* quota */ }
        }
        setPlanHistory(normalizePlanHistory(r.plan_history));
      } catch { /* offline/bez práv — localStorage fallback stačí */ }
      dbLoadedRef.current = true;
      // Platformní tahy, které přišly, když bylo okno zavřené.
      if (!cancelled) pollPlatformTurns().catch(() => {});
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Debounced save do DB — až po initial load, bez pending zpráv
  useEffect(() => {
    if (!dbLoadedRef.current) return;
    const t = setTimeout(() => {
      const clean = messages.filter((m) => !m.pending);
      if (clean.length === 0) return;
      const serialized = JSON.stringify(clean);
      if (serialized === lastPersistedHistoryRef.current) return;
      // Nastavit před requestem, aby remount/refetch nevytvořil souběžný set.
      lastPersistedHistoryRef.current = serialized;
      api.mcp('codable.chat_history', { slug, action: 'set', messages: clean })
        .catch(() => {
          if (lastPersistedHistoryRef.current === serialized) {
            lastPersistedHistoryRef.current = null;
          }
        });
    }, 1_500);
    return () => clearTimeout(t);
  }, [messages, slug]);

  // Save history do localStorage při každé změně messages (throttling ne — objem
  // messages je typicky < 100 za session, JSON.stringify je rychlý).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(historyKey, JSON.stringify(messages));
    } catch { /* quota exceeded — ignoruj */ }
  }, [messages, historyKey]);

  // Znalostní báze projektu = MD v repu (docs/** + README) — do promptu
  // seznam + živý workspace snapshot (tree/MEMORY/package), aby agent
  // věděl na čem pracuje ještě před první akcí.
  const treeQ = useQuery({
    queryKey: ['codable-tree', slug],
    queryFn: () =>
      api.mcp<{ ok: boolean; entries?: Array<{ path: string; type: string }> }>(
        'codable.files',
        { slug, action: 'tree' },
      ),
    staleTime: 60_000,
  });
  const packageMetaQ = useQuery({
    queryKey: ['codable-package-meta', slug],
    queryFn: async () => {
      const r = await api.mcp<
        { ok: boolean; content?: string; error?: string }
      >(
        'codable.files',
        { slug, action: 'read', path: 'package.json' },
      );
      if (!r.ok || !r.content) return null;
      return parsePackageJsonMeta(r.content);
    },
    staleTime: 120_000,
  });
  const agentMemoryQ = useQuery({
    queryKey: ['codable-agent-memory', slug],
    queryFn: async () => {
      return api.mcp<{
        ok: boolean;
        missing?: boolean;
        path?: string;
        content?: string;
        error?: string;
      }>('codable.agent_memory_get', { slug });
    },
    staleTime: 30_000,
  });
  const knowledgeFiles = (treeQ.data?.entries ?? [])
    .filter((e) =>
      e.type === 'blob' &&
      (/^docs\/.+\.md$/i.test(e.path) || /^readme\.md$/i.test(e.path))
    )
    .map((e) => e.path)
    .slice(0, 40);
  const agentMemoryFiles = knowledgeFiles.filter((p) => /^docs\/agent\//i.test(p));
  const hasAgentMemory =
    agentMemoryFiles.some((p) =>
      /\/MEMORY\.md$/i.test(p) || p.toLowerCase() === 'docs/agent/memory.md'
    ) || !!(agentMemoryQ.data?.ok && agentMemoryQ.data.content);
  const workspaceTreeSnap = summarizeWorkspaceTree(treeQ.data?.entries, 90);
  const packageMeta = packageMetaQ.data;
  const memoryContent = agentMemoryQ.data?.ok && agentMemoryQ.data.content
    ? agentMemoryQ.data.content
    : null;
  const memoryPath = agentMemoryQ.data?.path ?? 'docs/agent/MEMORY.md';

  // Orientační obsazenost kontextu (historie + rozepsaný input + systém)
  const ctxUsed = estTokens(
    messages.filter((m) => !m.pending).map((m) => m.content).join('\n'),
  ) +
    estTokens(input) + SYSTEM_OVERHEAD;
  const ctxPct = Math.min(100, Math.round((ctxUsed / contextBudget) * 100));

  // Autocompact: starší zprávy → LLM souhrn (stejný /stream endpoint), poslední
  // KEEP_RECENT zpráv zůstává doslovně. Souhrn je viditelný v historii jako
  // sbalený marker — user vidí, CO model po kompresi ví.
  const compactHistory = async (hist: Message[]): Promise<Message[] | null> => {
    const old = hist.slice(0, -KEEP_RECENT);
    const recent = hist.slice(-KEEP_RECENT);
    if (old.length < 4) return null;
    toast.info(t('codableChat.compacting', { n: old.length }));
    try {
      const serialized = old.map((m) => `[${m.role}]\n${m.content}`).join(
        '\n\n---\n\n',
      )
        .slice(0, 350_000);
      const r = await openUiChatStream({
        messages: [
          {
            role: 'system',
            content: preferredLangInfo.summaryPrompt,
          },
          { role: 'user', content: serialized },
        ],
      }, undefined, chatReloginCopy);
      if (!r.ok || !r.body) return null;
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let acc = '';
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split(/\n/);
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const data = line.slice(5).trim();
          if (data === '[DONE]') continue;
          try {
            const ev = JSON.parse(data) as { type?: string; text?: string };
            if (ev.type === 'text' && ev.text) acc += ev.text;
          } catch { /* skip */ }
        }
      }
      if (!acc.trim()) return null;
      return [
        {
          id: uid(),
          role: 'assistant' as const,
          createdAt: new Date().toISOString(),
          compacted: true,
          content:
            `**${preferredLangInfo.summaryHeading}** (${old.length} zpráv):\n\n${acc.trim()}`,
        },
        ...recent,
      ];
    } catch {
      return null; // komprese selhala → pošli plnou historii (lepší než blokovat)
    }
  };

  const send = useCallback(async (
    override?: string,
    toolMeta?: ToolOutputMeta,
    extraImages?: Attachment[],
  ) => {
    const text = (override ?? input).trim();
    if (busyRef.current) {
      // Fronta „klasicky" (Filip 2026-09-06): zpráva během běžícího tahu se
      // zařadí a pošle po jeho dokončení. Odeslat 2× (znovu, s prázdným polem)
      // = jednoznačné přerušení: běžící tah se zastaví, zařazená zpráva jde hned.
      // Programové followupy (override) řeší enqueueSend, tady jen ruční odeslání.
      if (!override) {
        if (text) {
          const prev = sendAfterBusyRef.current;
          sendAfterBusyRef.current = prev ? `${prev}\n${text}` : text;
          setQueuedPreview(sendAfterBusyRef.current);
          setInput('');
          toast.info(
            t('codableChat.queuedToast', 'Zařazeno — pošle se po dokončení tahu. Odeslat znovu = přerušit.'),
          );
        } else if (sendAfterBusyRef.current) {
          toast.info(t('codableChat.interruptToast', 'Přerušuji běžící tah, zařazená zpráva jde hned.'));
          abortRef.current?.abort();
          if (turnIdRef.current) void interruptUiChatTurn(turnIdRef.current);
        }
      }
      return;
    }
    if (!text) return;
    // Stream accumulator — musí být mimo try, aby catch/abort měl partial text
    let acc = '';
    if (!override) {
      // Diktát je continuous — bez stopVoice() mikrofon běží dál a onresult
      // znovu vyplní composer textem, který jsme právě odeslali.
      stopVoice();
      rememberPrompt(text);
      setPromptHistoryIndex(null);
      promptDraftRef.current = '';
      setInput('');
    }

    // Attachments split: text jde inline do textového bloku, image jde
    // jako samostatný content block (multimodal — Anthropic 'image' /
    // OpenAI 'image_url'). Consumed attachments se hned smažou.
    // extraImages přidávají obrázky programově (auto-followup s tool_screenshot),
    // aniž by procházely state (setAttachments je async).
    let fullText = text;
    // Co se posílá modelu a co se zobrazí člověku jsou dva řetězce. Dokud se
    // oboje melo do `fullText`, user bublina vykreslila i „…neukládej je znovu
    // a nežádej uživatele, ať je někam kopíruje" — návod pro model uprostřed
    // vlastní zprávy (Filip 10. 9.: „při nahrání obrázku se mi do chatu vypsali
    // i instrukce pro model"). Model text se posílá a server si ho drží v přepisu
    // tahu; do bubliny patří jen to, co člověk napsal.
    let displayText = text;
    const imageAttachments: Attachment[] = [];
    // Soubor od uživatele patří DO PROJEKTU, ne jen do promptu (Filip 2026-09-06:
    // „dám mu přes sponku logo a on řekne, že si ho tam musím nakopírovat sám,
    // a pak se zasekne"). Obrázek šel modelu jen jako vision blok — agent ho
    // viděl, ale neměl z čeho udělat soubor, protože base64 payload zůstal
    // v prohlížeči. Nahrajeme ho tedy rovnou do workspace (binárky přes
    // encoding=base64) a modelu dáme cestu; vidět ho může dál přes vision.
    const assetsToUpload = !override
      ? attachments.filter((a) => a.kind === 'image' || /\.svg$/i.test(a.name))
      : [];
    if (attachments.length > 0 && !override) {
      const textOnes = attachments.filter((a) =>
        a.kind !== 'image' && !/\.svg$/i.test(a.name)
      );
      const imgOnes = attachments.filter((a) => a.kind === 'image');
      if (textOnes.length > 0) {
        const attachBlock = textOnes
          .map((a) =>
            t(
              'codableChat.attachedFileBlock',
              '\n\n**Přiložený soubor `{{name}}`** ({{size}} b):\n```\n{{content}}\n```',
              { name: a.name, size: a.size, content: a.content },
            )
          )
          .join('');
        fullText = text + attachBlock;
        // Obsah souboru, který přiložil člověk, je jeho vlastní text — ten se v
        // bublině ukazuje dál. Instrukce k němu ne.
        displayText = fullText;
      }
      imageAttachments.push(...imgOnes);
      setAttachments([]);
    }
    if (extraImages && extraImages.length > 0) {
      imageAttachments.push(...extraImages);
    }

    // Nahrání příloh do workspace. Děje se i v režimu Návrhy: soubor přiložil
    // ČLOVĚK vědomým gestem, není to zápis agenta (patch/commit brány se netýkají).
    if (assetsToUpload.length > 0) {
      setBusy(true);
      const uploaded: string[] = [];
      for (const a of assetsToUpload) {
        const path = chatAssetName(a.name);
        const binary = a.kind === 'image';
        const res = await api.mcp<{ ok?: boolean; error?: string }>(
          'codable.chat_media',
          {
            slug,
            action: 'write',
            name: path,
            content: binary ? (a.base64 ?? '') : a.content,
            ...(binary ? { encoding: 'base64' } : {}),
          },
          { silent: true },
        ).catch((e: unknown) => ({
          ok: false,
          error: e instanceof Error ? e.message : String(e),
        }));
        if (res && res.ok !== false) uploaded.push(path);
        else {
          toast.error(
            t('codableChat.assetUploadFailed', 'Soubor {{name}} se nepodařilo uložit do projektu: {{err}}', {
              name: a.name,
              err: res?.error ?? '?',
            }),
          );
        }
      }
      if (uploaded.length > 0) {
        // Dávkový zápis běžel bez sync — preview dorovnáme jednou na konci.
        await api.mcp('codable.preview_rebuild', { slug }, { silent: true }).catch(() => {});
        // JEN do model textu: „použij cesty, nepřekopírovat" je pokyn pro
        // adresáta s nástroji, ne poznámka pro člověka. Ten dostane toast
        // „Uloženo do projektu: …" o pár řádků níže.
        fullText += `\n\n${
          t(
            'codableChat.assetsUploadedNote',
            'Přiložené soubory leží mezi medii chatu ve zdrojích projektu: {{paths}}. Čti je nástrojem `codable_chat_media` (action: read) — neukládej je znovu a nežádej uživatele, ať je někam kopíruje. Do aplikace patří teprve tehdy, když je opravdu použiješ: `codable_chat_media` (action: promote, target_path) je zkopíruje do workspace.',
            { paths: uploaded.map((p) => `\`${p}\``).join(', ') },
          )
        }`;
        toast.success(
          t('codableChat.assetsUploaded', 'Uloženo do projektu: {{paths}}', {
            paths: uploaded.join(', '),
          }),
        );
      }
    }

    // Vision guard — text-only model na image part padá (Gemini 400, llama.cpp
    // 500). Obrázky se nepřiloží a agent dostane textovou poznámku, ať může
    // navrhnout přepnutí na multimodální profil.
    const visionProfile = selectedProfile ?? profiles[0];
    if (imageAttachments.length > 0 && visionProfile?.vision !== true) {
      fullText += `\n\n*(${
        t('codableChat.noVisionNote', '{{count}}× obrázek NEPŘILOŽEN — aktivní model "{{model}}" nemá vision. Přepni v pickeru na multimodální model, pokud obrázek potřebuješ.', {
          count: imageAttachments.length,
          model: visionProfile?.name ?? selectedModel ?? '?',
        })
      })*`;
      imageAttachments.length = 0;
      toast.warning(t('codableChat.noVision'));
    }

    // Autocompact před odesláním — když se blížíme rozpočtu kontextu.
    // Odhad počítá se sanitized history (starší tool_output → summary), takže
    // reálně budget threshold sedí na to, co skutečně poteče do LLM.
    let baseHistory = messages.filter((m) => !m.pending);
    const sanitizedForEst = sanitizeHistoryForLlm(baseHistory);
    const estimated = estTokens(
      sanitizedForEst.map((m) => m.content).join('\n'),
    ) +
      estTokens(fullText) + SYSTEM_OVERHEAD;
    if (
      estimated > contextBudget * COMPACT_AT &&
      baseHistory.length > KEEP_RECENT + 4
    ) {
      setBusy(true);
      const compacted = await compactHistory(baseHistory);
      if (compacted) baseHistory = compacted;
    }

    const uMsg: Message = {
      id: uid(),
      role: 'user',
      content: displayText,
      createdAt: new Date().toISOString(),
      ...(toolMeta ? { toolOutput: toolMeta } : {}),
    };
    const aMsg: Message = {
      id: uid(),
      role: 'assistant',
      content: '',
      pending: true,
      createdAt: new Date().toISOString(),
    };
    if (autoAcceptOn) {
      liveAutoMsgIdsRef.current.add(aMsg.id);
    }
    stickRef.current = true; // po odeslání vždy sleduj konec
    setMessages([...baseHistory, uMsg, aMsg]);
    setBusy(true);

    const ac = new AbortController();
    abortRef.current = ac;
    try {
      if (!toolMeta && isGitCommitIntent(text)) {
        const status = await api.mcp<WorkspaceStatusLike>(
          'codable.workspace_status',
          { slug },
          { silent: true },
        ).catch((e: unknown) => ({
          ok: false,
          error: e instanceof Error ? e.message : String(e),
        }));
        const content = formatCommitFence(status);
        setMessages((prev) =>
          prev.map((m) => m.id === aMsg.id ? { ...m, pending: false, content } : m)
        );
        return;
      }

      // Chat stream běží pod HttpOnly user session (`chat:read`). Server vynutí
      // projektový slug i request-scoped seznam toolů; browser credential nezná.

      // Live UI kontext — co má user právě otevřené (CodePanel publikuje do
      // ui-context registru; tady čteme lokální snapshot, vždy čerstvý).
      const uiCtx = getUiContextSnapshot();
      const modelHasVision = selectedProfile?.vision === true;
      const shownProjectId = visibleProjectId(orchestratorProjectId, projectId);
      const projectToolId = shownProjectId ?? orchestratorProjectId ?? null;

      // Codable system prompt — role, stack, workflow, boundary.
      const backendDesc = backendInfo.enabled
        ? `- Backend: ${
          backendInfo.type === 'supabase'
            ? 'Supabase (Postgres + Auth + Storage + Edge Functions)'
            : backendInfo.type
        }`
        : `- Backend: **žádný** (jen frontend). Uživatel může zapnout přes tlačítko "Zapnout backend" v workspace toolbaru — spustí Supabase.`;
      const dialect = agentDialectLines(selectedProfile, planningMode, autonomyMode);
      const topologyTarget = topology?.target ?? topology?.mode ??
        (feSplit ? 'normal' : publish?.actual ? 'simple' : 'beta');
      const topologyActual = topology?.actual ?? null;
      const topologyLine = [
        `- Varianta práce/topologie: zvolená ${topologyModeLabel(topologyTarget)}`,
        topologyActual ? `, skutečně ověřená ${topologyModeLabel(topologyActual)}` : '',
        topology?.drift ? ` (DRIFT: zvolená varianta nesedí na ověřenou topologii)` : '',
        topology?.verified === false ? ` (neověřeno)` : '',
        `.`,
      ].join('');
      const driftStates = projectStatus?.relation?.states ?? [];
      const driftWarnings = projectStatus?.relation?.warnings ?? [];
      const projectStatusLine = projectStatus
        ? `- Aktuální Git/DEV/PROD stav (${projectStatus.checked_at ?? 'nezjištěno'}): states=${
          driftStates.length ? driftStates.join(', ') : 'clean'
        }; DEV=${projectStatus.dev?.short_head ?? 'unknown'}${
          projectStatus.dev?.dirty ? ` dirty(${projectStatus.dev.changed_files ?? '?'})` : ''
        } ahead=${projectStatus.dev?.ahead ?? '?'} behind=${
          projectStatus.dev?.behind ?? '?'
        }; PROD=${projectStatus.prod?.short_head ?? 'unknown'}${
          projectStatus.prod?.dirty ? ` dirty(${projectStatus.prod.changed_files ?? '?'})` : ''
        } ahead=${projectStatus.prod?.ahead ?? '?'} behind=${projectStatus.prod?.behind ?? '?'}.`
        // Karuselovy hodnoty tu byly dřív podruhé — a pod jiným jménem než v bloku
        // adres. Dvě místa se stejnými čísly je přesná cesta, jak se ztratit v tom,
        // kde je náhled. Jedno místo: sekce Adresy níže.
        : `- Aktuální Git/DEV/PROD stav: nezjištěn; zavolej codable_project_status před zásahem, pokud řešíš sync/publish.`;
      const orientationBlock = [
        `## AKTUÁLNÍ PROJEKT — načti před jakoukoli akcí`,
        `Pracuješ **výhradně** na Codable projektu:`,
        `- **slug:** \`${slug}\``,
        `- **projectId:** \`${projectId}\``,
        packageMeta?.name
          ? `- **package.json name:** \`${packageMeta.name}\`${
            packageMeta.version ? ` @${packageMeta.version}` : ''
          }`
          : `- **package.json name:** (zatím nenačteno — při pochybách \`codable_files read package.json\`)`,
        packageMeta?.description
          ? `- **popis appky:** ${packageMeta.description.slice(0, 240)}`
          : null,
        repo ? `- **GitLab repo:** ${repo}` : `- **GitLab repo:** neznámé`,
        // Jedna adresa nestačila: jmenuje se „náhled" jak dev workspace, tak
        // modrá doména prod instance v karuselu. Agent si je spletl a hledal
        // preview address tam, kde je kandidát na ostrou (Filip 2026-09-10).
        ...codableAddressLines({
          slug,
          devPreviewUrl: previewUrl,
          prodUrl: publish?.url ?? null,
          prodPreviewUrl,
          carousel: projectStatus?.carousel ?? null,
        }),
        `- **Pracovní adresář (logický):** repo root TOHOTO projektu — všechny cesty relativní k rootu (ne /opt/acm, ne jiné projekty).`,
        workspaceInstance
          ? `- **DEV instance:** ${workspaceInstance}`
          : `- **DEV instance:** nezjištěna`,
        lastCommitSha
          ? `- **Poslední známý git checkpoint:** ${lastCommitSha.slice(0, 12)}`
          : `- **Poslední známý git checkpoint:** nezjištěn`,
        ``,
        `### Orientační protokol`,
        `- Slug \`${slug}\`, workspace = repo root (snapshot níže). Cesty relativní.`,
        `- Když chybí snapshot / konkrétní soubor: tree / memory_get / chat_file_read, pak write.`,
        `- **Jednou** na začátku nového úkolu stačí; když běží aktivní plán, NEopakuj orientaci ani „pracuji na…“ — jdi na NEXT krok.`,
        ``,
        `### Snapshot workspace (repo tree, shallow-first)`,
        workspaceTreeSnap.lines.length > 0
          ? `Načteno ${workspaceTreeSnap.lines.length}/${workspaceTreeSnap.total} cest${
            workspaceTreeSnap.truncated ? ' (zkráceno — zbytek přes tree tool)' : ''
          }:`
          : `Tree zatím není v UI cache — **před akcí** zavolej \`codable_files {slug:"${slug}", action:"tree"}\`.`,
        ...(workspaceTreeSnap.lines.length > 0 ? ['```', ...workspaceTreeSnap.lines, '```'] : []),
        ``,
        `### Agent MEMORY (docs/agent/)`,
        memoryContent
          ? `Načteno z \`${memoryPath}\` (živý snapshot do promptu):`
          : hasAgentMemory
          ? `MEMORY soubor existuje v tree, ale obsah se nenačetl — zavolej \`codable_agent_memory_get {slug:"${slug}"}\`.`
          : `MEMORY zatím chybí — při trvalé znalosti vytvoř \`codable_agent_memory_put\`.`,
        ...(memoryContent ? ['```markdown', truncateForPrompt(memoryContent, 5_500), '```'] : []),
      ].filter((x): x is string => x != null);

      const systemPreamble = [
        `Jsi projektový asistent pro Codable projekt "${slug}"${
          repo ? ` (repo ${repo})` : ''
        }, projectId ${projectId}.`,
        `Právě teď pracuješ v otevřeném workspace tohohle projektu — je to tvé sandboxované okno, ne celá ACM.`,
        ``,
        `## Jazyk komunikace`,
        `- preferred_language: ${preferredLang} (${preferredLangInfo.label})`,
        `- Odpovídej uživateli primárně v preferovaném jazyce UI uvedeném výše.`,
        preferredLang === 'cvm'
          ? `- Caveman styl: odpovídej krátkou hravou česko-primitivní řečí. Používej jednoduché věty: "Já kouk.", "Kámen bolí.", "Udělat.", "Oheň dobrý." Technické názvy, soubory, URL a kód nech přesně.`
          : `- Nepoužívej Caveman styl, pokud preferred_language není cvm.`,
        `- Pokud uživatel v aktuální zprávě výslovně požádá o jiný jazyk, aktuální pokyn uživatele má přednost.`,
        `- Kód, názvy souborů, identifikátory, CLI příkazy, API názvy a logy nech v původním technickém jazyce, typicky anglicky.`,
        ``,
        ...orientationBlock,
        ``,
        `## SCOPE — tentýž agent, jen jiné dveře`,
        `Tohle jsou **jiné dveře**, ne jiný agent: jsi tentýž asistent jako v běžném ACM chatu, jen s jinými nároky.`,
        `- **Vědět a povídat** umíš o celé platformě i odsud: jak co funguje, co by se dalo udělat, kde to je. Orientaci nikdo neomezuje.`,
        `- **Volat** odtud smíš jen to, co máš v \`tools\` — a ty jsou projektové, svázané se slugem \`${slug}\`. Server každé volání přepíše na \`${slug}\` a request-scoped allow-list nic jiného nepustí.`,
        `- Platformní operace (zálohy, domény, tenanty, SSO, cizí projekty) tedy **odsud** neuděláš. Ne proto, že by se nesměly — u jiných dveří se běžně dělají a vedle právě teď jsou agenti s jinými nároky. Pojmenuj uživateli, kam to patří (běžný ACM chat, konkrétní modul), ne že se to dělat nesmí.`,
        `- Cizí Codable projekt není tvůj: server přepisuje \`slug\` na \`${slug}\`. Zkusit to není útok, jen prázdný výsledek.`,
        `- **Nikdy netvař, že jsi platformní operaci provedl**, a neobcházej ji přes projektové nástroje.`,
        `- God mode je admin-only bonus pro vývoj chatu, ne zadní vrátka k platformě.`,
        ``,
        `## „Poslední změny“ / historie — ABSOLUTNÍ PRAVIDLO`,
        `- Otázky typu „poslední změny“, „co se změnilo“, „historie“, „kdo co commitol“ = VÝHRADNĚ tento projekt:`,
        `  1) \`codable_history {slug:"${slug}", action:"list"}\` — commity TOHOTO projektu; co ještě není v GitLabu má \`pushed:false\`. Obsah commitu: \`codable_history {action:"show", sha}\` (soubory, statistika, diff — i u nepushnutého). Rozdíl proti GitLabu: \`codable_history {action:"diff"}\` bez dalších argumentů; \`{action:"diff", from:"working"}\` = necommitnuté změny, \`path\` zúží na soubor.`,
        `  2) \`codable_project_log {slug:"${slug}"}\` — audit MCP toolů v projektu`,
        `  3) volitelně \`codable_agent_memory_get {slug:"${slug}"}\` / \`docs/agent/MEMORY.md\``,
        `- NIKDY nevyprávěj o commitech ACM platformy, masteru platformy, ani o jiných projektech.`,
        `- Bez tool resultu z \`codable_history\` pro slug \`${slug}\` NEHÁDEJ changelog — řekni že nevíš a tool zavolej.`,
        ``,
        `## Základní informace projektu`,
        `- Slug: ${slug}`,
        `- Project ID: ${projectId}`,
        `- Orchestrator env: ${slug}${envStatus ? ` (${envStatus})` : ''}`,
        repo
          ? `- GitLab vazba: ${repo}${gitlabUrl ? ` (${gitlabUrl})` : ''}`
          : `- GitLab vazba: není známá`,
        `- Dev/preview adresa pro uživatele: ${
          previewUrl ?? `https://${slug}.cdb.services.maxcloud.cz`
        }`,
        publish?.actual && (publish.url || publish.domain)
          ? `- Veřejná/publikovaná adresa: ${publish.url ?? `https://${publish.domain}`}`
          : publish?.desired && !publish?.actual
          ? `- Veřejná/publikovaná adresa: publikace probíhá, zatím není potvrzená jako aktivní`
          : `- Veřejná/publikovaná adresa: není aktivní / není publikováno`,
        topologyLine,
        `  Varianty: Beta = jen DEV bez PROD adresy; Simple = jedna FE instance pro DEV i PROD; Normal = oddělený DEV FE a PROD FE; Full Split = oddělený DEV/PROD frontend i backend.`,
        topology?.dev_domain || topology?.prod_domain
          ? `- Topology domény: DEV ${topology.dev_domain ?? 'nezjištěna'}, PROD ${
            topology.prod_domain ?? 'nezjištěna'
          }.`
          : `- Topology domény: nejsou explicitně zjištěné.`,
        topology?.issues?.length
          ? `- Topology issues: ${topology.issues.join('; ').slice(0, 500)}.`
          : `- Topology issues: žádné známé v načteném stavu.`,
        `- FE topologie: ${
          feSplit ? 'DEV/PROD frontend jsou oddělené' : 'jedna frontend instance'
        }; DEV instance: ${workspaceInstance ?? 'nezjištěna'}; PROD instance: ${
          prodInstance ?? 'nezjištěna'
        }.`,
        projectStatusLine,
        driftWarnings.length
          ? `- Drift varování: ${driftWarnings.join(' | ').slice(0, 700)}`
          : `- Drift varování: žádné známé.`,
        `- Poslední známý Git checkpoint projektu: ${
          lastCommitSha ? lastCommitSha.slice(0, 12) : 'nezjištěn'
        }.`,
        lastPublishEvent
          ? `- Poslední publish/promote akce: ${
            replaceLegacyProjectId(
              lastPublishEvent.raw_tool_name ?? lastPublishEvent.tool_name ?? '',
              shownProjectId,
            )
          } v ${lastPublishEvent.created_at}, actor ${
            lastPublishEvent.actor_entity_id ??
              lastPublishEvent.actor_key_preview ?? 'neznámý'
          }, výsledek ${
            lastPublishEvent.ok ? 'OK' : `CHYBA: ${lastPublishEvent.error ?? 'bez detailu'}`
          }.`
          : `- Poslední publish/promote akce: bez záznamu.`,
        feSplit
          ? `- U split projektu platí: live edit a Git sync pracují s DEV instancí; na PROD URL se nový commit dostane až přes projektově kvalifikovaný \`${
            projectToolId ?? 'project-id'
          }.orchestrator.promote\` tool z nabídky / tlačítko Publikovat.`
          : `- U single-FE projektu DEV a PROD/public mohou sdílet stejnou frontend instanci; nevyvozuj split jen z rozdílných URL.`,
        `- Interní apphost port (pokud ho uvidíš ve službách) NENÍ veřejná adresa. Uživatelům uváděj jen URL z \`publish.url\` / \`preview_url\` / \`gitlab_web_url\`, ne interní port.`,
        `- Pokud potřebuješ čerstvý stav adres, služeb nebo publish statusu, zavolej \`codable_project_get {slug}\`; tyto údaje nehádej.`,
        `- Pokud řešíš změny, Git sync nebo publish, nejdřív \`codable_project_status {slug}\`. \`dev_behind_git\` → sync. \`prod_behind_git\` → promote na náhled. \`prod_preview_waiting\` → ostrá URL je pozadu, přepni release_switch. \`prod_ahead_git\` / \`prod_diverged_git\` → bez potvrzení nepublikuj.`,
        `- Variantu/topologii projektu v chatu NEMĚŇ. Změna Beta/Simple/Normal/Full Split patří orchestrátoru, protože musí ověřit instance, domény a migrační postup. Ty ji jen čti, vysvětli a pracuj podle ní.`,
        ``,
        `## Demarkační čára oprávnění`,
        `- Codable chat je pracovní agent TOHOTO projektu. V Live máš tooly na skutečnou práci: workspace (číst/hledat/zapisovat), GitLab commit/push, sync, karusel verzí, parked větve, CI/worker, promote na PROD.`,
        `- God mode NENÍ potřeba k práci na projektu. Je to admin-only bonus (ACL k souborům chatu / vývoj chatu). Agent ho neodemyká.`,
        `- Topologii (Beta/Simple/Normal), mazání projektu a cizí nody nesahej. Když tool vrátí error, cituj ho — nevymýšlej lock.`,
        `- Když požadavek spadá mimo registrované tooly nebo mimo tenhle projekt, neobcházej to. Řekni přesný blokátor.`,
        ``,
        `## Stack projektu`,
        `- Frontend: React + Vite + TypeScript + Tailwind CSS + shadcn/ui`,
        backendDesc,
        `- Dev workspace: kód běží na serveru přes vite dev server — nativní workspace tooly`,
        `  zapisují PŘÍMO do něj a změna je v preview okamžitě (HMR, bez buildu).`,
        `  GitLab push není deploy mechanismus pro iteraci: je to až checkpoint po ověření.`,
        `  Správné pořadí je vždy: read → codable_workspace_replace nebo codable_workspace_write → verify → teprve potom commit.`,
        `- Preview URL je dev náhled projektu; produkční/publikovanou adresu ber z publish stavu výše nebo z \`codable_project_get\`.`,
        ``,
        `## Kde se změna projeví — 4 vrstvy (POVINNÉ znát)`,
        `1) **DEV workspace** — \`codable_workspace_replace\` / \`codable_workspace_write\` (Live) nebo patch karta Přijmout (Návrhy).`,
        `   → hned vidět v **DEV preview** (HMR). To je místo, kam user kouká při vývoji: ${
          previewUrl ?? `https://${slug}.cdb.services.maxcloud.cz`
        }.`,
        `2) **GitLab** — \`codable_commit\` = checkpoint. Není to „deploy preview“; preview už běží z (1).`,
        `3) **PROD náhled (karusel)** — \`${
          projectToolId ?? 'project-id'
        }.orchestrator.promote {confirm:true}\`. Položí commitnutý stav na slot Náhled. **Ostrá veřejná adresa se tím NEZMĚNÍ.**`,
        `4) **Ostrá veřejná adresa** — \`${
          projectToolId ?? 'project-id'
        }.apphost.release_switch {confirm:true}\`. Bez confirm jen ukáže Ostrá/Náhled/Záloha. Přepnutí je výměna symlinku, bez buildu.`,
        `   Když user chce, aby to lidi viděli („publikuj“, „pusť ven“, „přepni ostrou“, „ať je to live“), udělej krok 4 sám. NEposílej ho do menu Publikace.`,
        ``,
        `- Text v chatu / „udělal jsem X“ **bez** tool resultu workspace_replace/workspace_write (nebo Apply patche v Návrzích) = **nic se nestalo**.`,
        `- Po zápisu: readback \`codable_chat_file_read\` (očekávej source=workspace) + ověř DEV preview (screenshot/OCR).`,
        `- „nothing_to_commit“ = nic jsi nezapsal do workspace — nejdřív write.`,
        `- User se ptá „kde to je / proč to nevidím“ → zkontroluj: (a) byl write/Apply? (b) kouká na DEV preview ne PROD? (c) po commitu chce PROD náhled → promote; ostrá URL → ještě release_switch.`,
        `- NIKDY nehledej nasazení do ACM platformy (/opt/acm). Tohle je projekt \`${slug}\`.`,
        `- NIKDY nepoužívej \`codable_chat_file_write\` pro iteraci (jde mimo HMR workspace). Jen \`codable_workspace_replace\` / \`codable_workspace_write\` v Live nebo patch v Návrzích.`,
        `- **Obsah souboru NIKDY nevypisuj do zprávy v chatu.** Změnu posílej jen v argumentech nativního \`codable_workspace_replace\` nebo \`codable_workspace_write\`. Výpis do chatu narazí na output limit, uřízne se v půlce a zápis se nepoužije.`,
        `- V odpovědi po zápisu piš jen krátké shrnutí (co a proč se změnilo) — žádné opakování celého souboru ani „hotový kód" na konec.`,
        `- Velký soubor měň cíleně přes \`codable_workspace_replace\`; kompletní \`codable_workspace_write\` použij jen při nutném celkovém přepisu. Více souborů dělej postupně a každý ověř.`,
        ``,
        `## Pravda z toolů — POVINNÉ (žádné vymýšlení blokací)`,
        `- Stav platformy ber **jen** z tool resultu (\`ok\`, \`error\`, \`hint\`). Nepiš „je to blokované / zakázané / nejde“, pokud tool nevrátil konkrétní error.`,
        `- Chybí-li MEMORY / security scan / soubor → to **není** platformní lock; buď vytvoř (memory_put / security_scan / write), nebo řekni „zatím chybí“.`,
        `- Binární assety (3D \`.glb\`, obrázky…): \`codable_workspace_write\` s \`encoding:"base64"\` a base64 obsahem. Bez base64 tool textem binárku zničí — to není „blokace přenosu“, ale špatný encoding.`,
        `- Když tool selže (EACCES, payload too large, double path): přečti \`error\`/\`hint\`, oprav příčinu (heal, menší soubor, správná cesta). Neoznamuj to jako trvalý feature flag.`,
        ``,
        `## Cesty k souborům — POVINNÉ`,
        `- Všechny \`path\` parametry v toolech (\`codable_workspace_replace\`, \`codable_workspace_write\`, \`codable_chat_file_read\`, \`codable_files\`) jsou VŽDY relativní vůči **repo rootu** — přesně jak je vidíš ve výstupu \`codable_files {action:"tree"}\` (source=workspace).`,
        `- Příklady správně: \`src/App.tsx\`, \`package.json\`, \`backend/db.ts\`, \`public/logo.svg\`, \`public/models/x.glb\`, \`.env\`.`,
        `- NIKDY neposílej absolutní cesty jako \`/opt/apphost/...\`, \`/etc/...\`, \`/home/...\`. Cesty mimo repo neexistují a tooly je odmítnou.`,
        `- NIKDY nezdvojuj prefix — pokud tree ukáže \`src/App.tsx\`, pošli přesně \`src/App.tsx\`, ne \`src/src/App.tsx\` ani \`./src/App.tsx\`.`,
        `- Serverové umístění projektu (kde apphost drží checkout) je platformní detail. V chatu s uživatelem mluv o cestách repo-root-relative; nezmiňuj \`/opt/apphost\` ani jiné absolutní paths.`,
        ``,
        `## Režim modelu`,
        ...dialect,
        ``,
        `## Režim autonomie`,
        autonomyMode === 'ask'
          ? `- Režim: NÁVRHY. Změny projektu jen přes interaktivní karty \`\`\`patch\`\`\`; čekej na klik uživatele. GitLab commit neposílej bez výslovného pokynu.`
          : autoCommitActive
          ? `- Režim: LIVE + COMMIT. Malé změny dělej přes \`codable_workspace_replace\`, nové/celé soubory přes \`codable_workspace_write\`; \`codable_commit\` je až závěrečný GitLab checkpoint.`
          : `- Režim: LIVE. Malé změny dělej přes \`codable_workspace_replace\`, nové/celé soubory přes \`codable_workspace_write\`. Změny se mají nejdřív projevit v živém preview.`,
        autonomyMode !== 'ask'
          ? `- Když user řekne "udělej", "dodělej", "oprav" nebo "pokračuj", NEZASTAVUJ u návrhu. Načti aktuální soubor, pak hned volej \`codable_workspace_replace\` pro cílenou změnu nebo \`codable_workspace_write\` pro nový/celý soubor. Po zápisu ověř stav.`
          : `- Bez potvrzení uživatele nic nezapisuj. Textový popis změny není provedená změna.`,
        autoCommitActive
          ? `- GitLab checkpoint dělej po malých funkčních celcích. Commit message musí být krátká a konkrétní; po commitu uveď SHA z tool resultu a stejný stav už znovu necommituj.`
          : `- Když vzniknou ověřené změny, navrhni commit kartu nebo se zeptej na commit podle rozsahu práce.`,
        autonomyMode !== 'ask'
          ? `- V Live režimu neposílej \`\`\`patch\`\`\` kartu ani celý soubor do chatu jako náhradu práce. Textový patch v chatu = jen návrh, ne provedená změna. Pokud máš write tool, použij ho.`
          : `- V Návrhy režimu je patch karta správně: UI ji aplikuje až po kliknutí uživatele.`,
        ...(autoCommitActive
          ? [
            `- LIVE + COMMIT pořadí je povinné: read → workspace_replace (preferuj) / workspace_write → ověření → codable_commit.`,
            `- Nikdy nezačínej úkol voláním \`codable_commit\`. Commit bez předchozího úspěšného workspace zápisu v tomto vlákně je chyba.`,
            `- \`codable_commit\` volej nejvýš jednou pro jeden dokončený funkční celek. Po úspěšném commitu už znovu nepushuj stejnou věc; pokračuj jen pokud zbývá další změna.`,
            `- Pokud \`codable_commit\` vrátí "nothing_to_commit", nehlaš hotovo: znamená to, že jsi nic reálně nezapsal. Nejprve proveď workspace write nebo vysvětli blokátor.`,
            `- Pokud \`codable_workspace_write\` nebo \`codable_commit\` selže, neopakuj stejný slepý krok. Přečti chybovou zprávu, oprav příčinu nebo jasně napiš blokátor.`,
          ]
          : []),
        ``,
        ...(uiCtx.open_file || uiCtx.tab
          ? [
            `## Kontext workspace (live)`,
            ...(uiCtx.open_file
              ? [
                `User má v **Code editoru** otevřený soubor: \`${uiCtx.open_file}\`` +
                  (typeof uiCtx.open_file_line === 'number'
                    ? ` na řádku **${uiCtx.open_file_line}**` +
                      (typeof uiCtx.open_file_column === 'number'
                        ? `:${uiCtx.open_file_column}`
                        : '')
                    : '') +
                  `.`,
                uiCtx.open_file_dirty
                  ? `Editor má **neuložené lokální změny** (dirty) — nejdřív si ověř aktuální obsah přes \`codable_chat_file_read\` / workspace, ať nepřepíšeš userovu rozepsanou práci slepým full write.`
                  : `Soubor v editoru je synchronní s posledním load/save (clean).`,
                `Když user řekne „tento soubor“, „tady“, „tady v kódu“, „uprav to“, „doplň“, „oprav“ bez cesty — myslí **\`${uiCtx.open_file}\`` +
                  (typeof uiCtx.open_file_line === 'number'
                    ? ` (řádek ${uiCtx.open_file_line})`
                    : '') +
                  `**.`,
                `Asistence při úpravě:`,
                `1) Přečti aktuální obsah: \`codable_chat_file_read\` (nebo files read) na \`${uiCtx.open_file}\`.`,
                `2) Cílená změna: preferuj \`codable_workspace_replace\` (find/replace). Celý soubor jen když je to nutné: \`codable_workspace_write\`.`,
                `3) Po zápisu krátce shrň co se změnilo v \`${uiCtx.open_file}\`; preview/HMR se aktualizuje z DEV workspace.`,
                `Nepiš patch kartu jako náhradu zápisu v Live režimu — zapiš tooly do workspace.`,
              ]
              : []),
            ...(uiCtx.tab
              ? [
                `Aktivní záložka workspace: **${uiCtx.tab}**` +
                  (uiCtx.tab === 'code'
                    ? ' (Code editor — user edituje soubory ručně + čeká pomoc chatu).'
                    : uiCtx.tab === 'preview'
                    ? ' (Preview — user kouká na běžící appku).'
                    : '.'),
              ]
              : []),
            ...(uiCtx.selection
              ? [
                `User **vybral prvek v náhledu**: selector=\`${
                  (uiCtx.selection as { selector?: string }).selector ?? '?'
                }\`` +
                  ((uiCtx.selection as { text?: string }).text
                    ? `, text=„${(uiCtx.selection as { text?: string }).text}"`
                    : '') +
                  ((uiCtx.selection as { tag?: string }).tag
                    ? `, tag=${(uiCtx.selection as { tag?: string }).tag}`
                    : '') +
                  `.`,
                `Když řekne „toto“, „tenhle prvek“, „tady v UI“, „to tlačítko“ — myslí TENHLE výběr. Najdi ho v kódu (id/class/text) a uprav ho. Nesahaj na jiný element.`,
              ]
              : []),
            ``,
          ]
          : []),
        `## Workflow`,
        // Planning OFF = práce už je schválená (Live/Auto/user pokyn). Plán NENÍ brána.
        planningMode && autonomyMode === 'ask'
          ? `Planning mode **ZAP** — jednou na začátku \`\`\`plan\`\`\`, čekej na Přijmout, pak implementuj.`
          : planningMode
          ? `Planning mode **ZAP** + autonomie — jednou \`\`\`plan\`\`\` (auto-accept), hned tooly + step_done. NEcykli plán.`
          : autonomyMode === 'ask'
          ? `Planning mode **VYP** — NEposílej plan karty. Rovnou \`\`\`patch\`\`\` (user je schvaluje klikem). Žádná plánovací ceremonie.`
          : autoCommitActive
          ? `Planning mode **VYP** — práce je schválená (Live+Git). INSTANT: read → workspace_replace/write → ověř → commit. **NEposílej \`\`\`plan\`\`\`**.`
          : `Planning mode **VYP** — práce je schválená (Live). INSTANT: read → workspace_replace/write → ověř. **NEposílej \`\`\`plan\`\`\`**.`,
        !planningMode
          ? [
            `### Plán (jen když je Planning VYP)`,
            `- \`\`\`plan\`\`\` **nepoužívej** jako bránu. User už dal pokyn / Live / Auto — to je schválení práce.`,
            `- Interní „co udělám“ max 1 krátká věta, ne plan karta s Přijmout.`,
            `- step_done jen pokud už existuje aktivní plán z dřívějška (import); jinak step_done nepotřebuješ.`,
            `- Po tool resultu rovnou další tool — ne status report „plánuju…“.`,
          ].join('\n')
          : [
            `### Zákaz restart smyčky`,
            `- Aktivní plán: NEposílej znovu stejný \`\`\`plan\`\`\`. Pokračuj prvním ⬜.`,
            `- Po tool resultu neopakuj celý status — rovnou další tool nebo step_done.`,
          ].join('\n'),
        `- GitLab commit/push je checkpoint po ověření, ne náhrada živé úpravy.`,
        `- Když narazíš na chybu, hlas ji přesně (soubor:řádek + text) místo hádání.`,
        `- Pokud user řeší bezpečnost, podezřelý kód, publikaci nebo produkční riziko, nejdřív si vyžádej aktuální projektový stav přes \`codable_project_get\` a security stav přes \`codable_security_results {slug}\`; výsledky neopisuj celé, jen shrň relevantní nálezy a navrhni/udělej opravu podle režimu autonomie.`,
        autonomyMode === 'ask'
          ? `- **Ground truth:** změna existuje TEPRVE po aplikované patch kartě (zápis do workspace). Popsat úpravu v textu ≠ provést ji — bez patch karty se NIC nestalo.`
          : `- **Ground truth:** změna existuje TEPRVE po úspěšném \`codable_workspace_replace\` nebo \`codable_workspace_write\` tool resultu. Popsat úpravu v textu ≠ provést ji.`,
        planningMode
          ? `- step_done jen když je krok doložený (write/verify/commit). nothing_to_commit ≠ step_done.`
          : `- step_done volitelně jen u existujícího aktivního plánu; jinak stačí krátké shrnutí po zápisu.`,
        ``,
        `## Co MŮŽEŠ a NEMŮŽEŠ vidět`,
        ...(modelHasVision
          ? [
            `- **Preview iframe**: máš vision schopnost. Když potřebuješ zkontrolovat UI,`,
            `  vyžádej si screenshot přes tool_screenshot fence (viz níže).`,
            `  V dalším turnu dostaneš PNG jako multimodal obrázek a vyhodnotíš ho sám.`,
            `- Nepoužívej OCR pro preview, pokud máš vision. OCR je fallback pro text-only modely`,
            `  a ztrácí layout, barvy i vizuální chyby.`,
          ]
          : [
            `- **Preview iframe**: aktivní model NEMÁ vision — NEPOUŽÍVEJ tool_screenshot,`,
            `  obrázek se ti stejně nepřiloží (zbytečné volání). Pro kontrolu stránky`,
            `  VŽDY použij \`codable_ocr {slug}\` — přečte text/layout OCR. Layout/barvy/vizuální`,
            `  chyby tím neuvidíš — pokud je potřebuješ, řekni userovi ať přepne na vision model.`,
          ]),
        modelHasVision
          ? `- Bez tool_screenshot preview NEVIDÍŠ. Nikdy si nevymýšlej co "vidíš" — to je` +
            ` halucinace. Buď použij tool_screenshot, nebo requestni popis od usera.`
          : `- Bez codable_ocr preview NEVIDÍŠ. Nikdy si nevymýšlej co "vidíš" — to je` +
            ` halucinace. Buď použij codable_ocr, nebo requestni popis od usera.`,
        `- Nevidíš terminal, docker logs mimo apphost.logs (tool_logs), ani filesystem`,
        `  mimo git repo projektu.`,
        ``,
        `## Nástroje (nativní function calling)`,
        `Máš registrované tyto tooly — volej je NATIVNĚ (function calling). Backend je`,
        `vykoná a výsledek dostaneš jako tool result ve stejném kole. Vždy posílej`,
        `slug "${slug}". Netipuj obsah souborů ani stav — přečti si je.`,
        `Všechny projektové operace jdou přes MCP kontrakt; UI je jen proxy/obal nad těmito tooly.`,
        `Výsledky read/search/fetch toolů jsou interní pracovní data: NIKDY je neopisuj celé`,
        `do chatu. Po přečtení souboru shrň jen relevantní řádky, nebo rovnou proveď`,
        `\`codable_workspace_replace\` / \`codable_workspace_write\` v Live nebo patch v Návrzích. Kód userovi ukazuj jen jako krátký relevantní`,
        `úryvek ve fenced code blocku, ne jako raw HTML/TSX/JSON dump.`,
        ``,
        `- \`codable_chat_file_read {slug, path}\` — obsah souboru z repa`,
        `- \`codable_files {slug, action:"tree"}\` — výpis souborů (git tree)`,
        `- \`codable_files {slug, action:"assets", path?, sort?, limit?}\` — inventura datových souborů (výchozí \`public/\`)`,
        `  s velikostí a časem, největší první. Na dotaz „co tady zabírá místo / co je tady za nahraný obrázky" volej tohle, ne tree.`,
        `- \`codable_workspace_search {slug, query, path?, glob?}\` — rg ve workspace`,
        `- \`codable_workspace_status {slug}\` — HEAD, dirty, parked větve`,
        `- \`codable_project_status {slug}\` — DEV/PROD git drift (před sync/publish)`,
        `- \`codable_history {slug, action:"list"}\` — posledních 30 commitů.`,
        `  action:"revert" NIKDY nevolej sám od sebe — jen na explicitní pokyn usera.`,
        `- \`codable_project_get {slug}\` — orch env stav, služby, publish stav + preview URL`,
        `- \`codable_project_log {slug, limit?}\` — projektový audit: kdo volal které MCP tooly a jak dopadly`,
        `- \`codable_security_scan {slug}\` — spustí security scan tohoto projektu a uloží výsledek.`,
        `- \`codable_security_results {slug, severity?}\` — poslední uložené nálezy. Když jsou empty / žádný scan, NEJDŘÍV volej security_scan, pak znovu results. HIGH/MED: přečti soubory a nabídni opravu.`,
        `- \`codable_logs {slug}\` — apphost build/runtime logy frontendu (build fail, runtime chyby)`,
        `- \`codable_ocr {slug?}\` / \`{url}\` — platformní OCR (preview screenshot nebo veřejná URL obrázku/PDF).`,
        `  \`save_to_library:true\` → draft note do společné knihovny. Layout/barvy nevrací.`,
        `- \`codable_platform_call {action, ...}\` — řízený most na ACM: ocr.submit, ocr.batch (urls_csv),`,
        `  ocr.status, websearch.query, websearch.fetch, knowledge.vault_ocr_ingest, knowledge.source_query.`,
        `  NEvymýšlej vlastní OCR/převod — volej platformu.`,
        `- \`codable_chat_search {slug, query}\` — hledání v perzistentní chat historii projektu`,
        `  (plan karty, step_done, commit followupy se sha ze starších sessions) + poslední historie plánů.`,
        `  Historie se ukládá server-side per projekt — když ti chybí starší kontext, NEHÁDEJ, vyhledej si ho.`,
        `- \`codable_plan_history {slug, limit?}\` — strukturovaná historie plánů projektu`,
        `  se stavem active/completed/cancelled, časy, kroky a progress. Použij, když potřebuješ zjistit,`,
        `  které plány už byly navrženy nebo realizovány. Aktivní plán normálně řídí UI přes plan kartu,`,
        `  import MD a step_done — nepřepisuj ho svévolně přes jiné MCP nástroje.`,
        `- \`codable_plan_export_md {slug, history_id?}\` — export aktivního/historického plánu do Markdownu.`,
        `- \`codable_library_search {query, kind?}\` — CODABLE PLAYBOOK (vrstva B): destilovaná **hotová řešení / recepty / moduly**`,
        `  pro webové appky (React/Vite, UI vzory, auth, shop patterny…). Než vymýšlíš netriviální věc, NEJDŘÍV hledej tady.`,
        `  NENÍ to katalog startovacích projektů a NENÍ to ACM wiki.`,
        `- \`codable_library_read {path}\` — plný obsah receptu/modulu (path z search).`,
        `- \`codable_web_search {query, limit?, language?}\` — veřejné webové hledání pro dokumentaci,`,
        `  API reference, schémata a integrační definice. Používej, když lokální playbook nestačí`,
        `  nebo když user chce napojit konkrétní veřejnou službu/knihovnu.`,
        `- \`codable_web_fetch {url, max_chars?}\` — stáhne veřejný textový zdroj: docs, XML/XSD/WSDL,`,
        `  JSON schema, OpenAPI, RSS/Atom, markdown. Nestahuje binární soubory a nechodí na private/local adresy.`,
        `- \`codable_template_list {}\` — katalog **šablon projektů** (landing, shop, app-shell, blank).`,
        `  To jsou kompletní startovací appky pro „Nový projekt“. Není to playbook recept.`,
        ...(autonomyMode !== 'ask'
          ? [
            `- \`codable_workspace_replace {slug, path, old_text, new_text, expected_replacements?}\` — preferovaný nástroj pro malou změnu existujícího textového souboru. Server ověří přesný počet shod; při 0/ambiguitě nezmění nic. Nevypisuj kvůli malé změně celý soubor.`,
            `- \`codable_workspace_write {slug, path, content, encoding?}\` — zapíše soubor do DEV workspace a **synchronizuje preview** (HMR nebo static dist rebuild). Není v gitu/PROD. Text default; **binárka** (\`.glb\`/png…): \`encoding:"base64"\` + content=base64. Dávka: skip_preview_sync:true + \`codable_preview_rebuild\`.`,
            `- \`codable_workspace_delete {slug, path, recursive?}\` — smaže soubor/složku z DEV workspace (úklid nahraných příloh a nepoužitých souborů). Do gitu až přes \`codable_commit\`.`,
            `- Soubory přiložené uživatelem sponkou leží mezi medii chatu ve zdrojích projektu (mimo git) — vypíšeš je \`codable_chat_media\` (action: list), přečteš action: read. Do workspace je kopíruj JEN když je opravdu použiješ, přes action: promote s target_path; nepoužité tam nech, v repu nemají co dělat.`,
            `  Co v \`public/\` je a kolik to zabírá: \`codable_files {slug, action:"assets"}\` — maže se až z tohohle přehledu, ne odhadem.`,
            `- \`codable_workspace_heal {slug}\` — opraví ownership workspace (EACCES).`,
            `- \`codable_preview_rebuild {slug}\` — ruční přestavění DEV preview (static nginx dist).`,
            `- \`codable_library_write {title, content, kind?, lifecycle?, path?, tags_csv?, source_urls_csv?, project_slug?, model?}\` — destiluje **část** řešení do knihovny (recipe/how-to/anti-pattern…).`,
            `  Default lifecycle=draft. project_slug="${slug}". Bez secretů a customer dat. Tohle je „převést část na modul/řešení“.`,
            `- \`codable_library_ingest {url?|text?, title?, kind?, lifecycle?, mode?}\` — OCR URL/PDF nebo raw text → společná knihovna (platform ocr_submit + vault).`,
            `- \`codable_plan_import_md {slug, markdown, source?, title?, filename?}\` — importuje Markdown/plan blok jako aktivní plán a zapíše plan history. Použij jen na výslovný pokyn nebo když user dodá plán k převzetí.`,
            `- \`codable_commit {slug, message, body?}\` — git checkpoint DEV workspace → GitLab. Až po zápisu + ověření v DEV preview. Není to PROD deploy. Má-li projekt CI, vrátí \`build.operation_id\` — build běží jako operace projektu a jeho výsledek ti přijde SÁM jako zpráva [Platforma].`,
            `- \`codable_workspace_discard {slug, confirm:true, to?}\` — vrátit workspace do známého stavu. DVĚ RŮZNÉ VĚCI: \`to:"head"\` (VÝCHOZÍ) zahodí jen NEZAPSANÉ změny a lokální commity NECHÁ, takže rozejití s GitLabem NESPRAVÍ; \`to:"origin"\` zahodí i commity a zarovná na GitLab. Je-li workspace napřed (dev_ahead_git / diverged) a ty chceš práci zachovat, nezahazuj — použij sync s rebase a commit. Nevratné.`,
            `- \`codable_merge_request {slug, title?, description?, target_branch?}\` — když je hlavní větev chráněná a commit do ní neprojde, nabídni práci ke sloučení místo hlášení „nemám právo".`,
            `- \`apphost.workspace_decide {slug|instance, branch, decision: merge|rebase|defer, defer_note?}\` — parked / odložená větev. defer_note je u defer povinná.`,
            `- \`gitlab.worker_resolve {}\` / \`gitlab.ci_enable {project, dry_run?}\` — build worker a \`.gitlab-ci.yml\`. project = GitLab path z project_get.`,
            `- \`orchestrator.project_operation_get {operation_id}\` — stav queued promote/sync/build. Výsledek operace, kterou jsi spustil, ti přijde SÁM jako zpráva [Platforma] po dokončení — nečekej na něj ve smyčce a nehlas queued jako hotovo.`,
            ...(projectToolId
              ? [
                `- \`${projectToolId}.orchestrator.sync {rebase?}\` — stáhne commitnutý stav do DEV workspace. Rozešly-li se větve (mám commity a v GitLabu jsou novější), \`rebase:true\` přesadí moji práci na aktuální origin místo merge commitu; konflikt se uklidí a vrátí jako chyba.`,
                `- \`${projectToolId}.orchestrator.promote {confirm:true}\` — položí **commitnutý** stav na PROD **náhled**. Ostrá URL se nemění. Bez confirm tool musí vrátit requires_confirm. Jméno volej S TEČKAMI, ne s podtržítky.`,
                `- \`${projectToolId}.apphost.release_switch {confirm?}\` — karusel Ostrá/Náhled/Záloha. Bez confirm = stav. S confirm:true přehodí ostrou adresu na náhled (nebo \`to\`). Tohle je krok, který lidé uvidí na veřejné URL.`,
                `- \`${projectToolId}.apphost.preview_set {service, enabled}\` — zapne/vypne náhledovou URL.`,
              ]
              : []),
          ]
          : []),
        ``,
        ...(modelHasVision
          ? [
            `**Screenshot preview** je výjimka — jde přes markdown fence (headless chromium`,
            `přes publish URL; PNG se přiloží jako multimodal obrázek v příštím turnu):`,
            `\`\`\`tool_screenshot`,
            `\`\`\``,
            `→ Použij když user říká "koukni co vidím" nebo pro verifikaci UI změn před/po.`,
            `NIKDY nezkoušej screenshot přes \`codable_platform_call\` — ten ho nepodporuje.`,
          ]
          : [
            `**Preview kontrola** — tenhle model nemá vision, takže screenshot (tool_screenshot)`,
            `ti k ničemu není. Použij \`codable_ocr {slug}\` když user říká "koukni co vidím"`,
            `nebo pro verifikaci UI změn — vrátí text/layout, ne obrázek.`,
            `NIKDY nezkoušej screenshot přes \`codable_platform_call\` — ten ho nepodporuje.`,
          ]),
        ``,
        `## Akce (změny stavu)`,
        ...(autonomyMode === 'ask'
          ? [
            `Pro modifikace repa a stavu projektu použij **markdown karty s tlačítky**`,
            `(patch, commit z sekce Interaktivní bloky). User musí kliknout Přijmout.`,
          ]
          : autoCommitActive
          ? [
            `Pro malé editace používej nativní \`codable_workspace_replace\`, pro nové/celé soubory \`codable_workspace_write\`; nikdy patch karty.`,
            `Po ověření smysluplného celku volej nativní \`codable_commit\` jako GitLab checkpoint. Neptej se na commit, pokud je změna součástí úkolu; necommituj bez ověření.`,
            `Když user chce nasadit / publikovat / přepnout verze / slít větev / zapnout CI — volej příslušný tool. Po queued promote volej project_operation_get, až completed/failed. Promote ≠ ostrá URL: po completed zavolej release_switch bez confirm, řekni live vs preview, a když user chce ostrou (nebo už řekl publikuj/pusť ven/přepni ostrou), volej release_switch s confirm:true. NEposílej ho do UI Publikace.`,
          ]
          : [
            `Pro malé editace používej nativní \`codable_workspace_replace\`, pro nové/celé soubory \`codable_workspace_write\`; nikdy patch karty.`,
            `Commit/promote/CI dělej, když o to user požádá; jinak po ověření se zeptej.`,
          ]),
        `Mazání projektu, změnu topologie a revert historie nespouštěj bez výslovného pokynu. Projektové tooly volej s tečkami (\`${
          projectToolId ?? 'project-id'
        }.orchestrator.promote\`), nikdy s podtržítky.`,
        ``,
        ...(knowledge?.trim()
          ? [
            `## Znalosti projektu (od uživatele — MUSÍŠ respektovat)`,
            knowledge.trim(),
            ``,
          ]
          : []),
        `## Persistentní paměť agenta (standard — všechny režimy)`,
        `- V dokumentaci projektu: složka \`docs/agent/\`, hlavní soubor \`docs/agent/MEMORY.md\`.`,
        `- **Funguje vždy** (Návrhy i Live) — ne jen God mode. Tooly:`,
        `  - \`codable_agent_memory_get {slug}\` — načti MEMORY`,
        `  - \`codable_agent_memory_put {slug, content}\` — ulož / vytvoř MEMORY (jen docs/agent/*.md)`,
        hasAgentMemory
          ? `- MEMORY existuje (${
            agentMemoryFiles.join(', ')
          }). Na začátku netriviálního úkolu **get**; po trvalém rozhodnutí **put**.`
          : `- MEMORY zatím chybí — při první trvalé znalosti ji **vytvoř put** (nemusíš se ptát).`,
        `- Do MEMORY: dlouhodobá rozhodnutí, konvence, stav feature, slepé uličky. **Ne** secrets/tokeny/dump chatu.`,
        `- Chat history = session. MEMORY = přes commity a nové session. Playbook = sdílené recepty (jiný projekt).`,
        ``,
        ...(knowledgeFiles.length > 0
          ? [
            `## Znalostní báze v repu (čti přes tool_read / codable_chat_file_read, když je relevantní)`,
            ...knowledgeFiles.map((p) =>
              /^docs\/agent\//i.test(p) ? `- ${p}  ← agent memory` : `- ${p}`
            ),
            ``,
          ]
          : [
            `## Znalostní báze v repu`,
            `- Zatím málo MD v \`docs/**\`. Agent memory drž v \`docs/agent/MEMORY.md\`; uživatelské docs klidně vedle v \`docs/\`.`,
            ``,
          ]),
        `## Dvě různé „sdílené věci“ — NEPLÉST`,
        ``,
        `### A) Šablona projektu (Project template)`,
        `- Co to je: **kompletní startovací appka** (landing / shop / dashboard / blank).`,
        `- Kde: UI \`/modules/codable/library?tab=templates\` + wizard Nový projekt; MCP \`codable_template_list\`.`,
        `- Nový projekt zakládá výhradně \`orchestrator.project_create\` s úplným deklarativním \`request\`; Codable pouze dodá consumer nastavení a šablonu.`,
        `- **„Převést projekt na šablonu“** znamená: z tohoto projektu udělat znovupoužitelný start pro *nové* projekty.`,
        `  Postup, když to user chce:`,
        `  1) Ověř, že app běží v preview a je to obecný product skeleton (ne customer data).`,
        `  2) Navrhni **očistu**: pryč secrets, .env, specifické brand texty → placeholdery \`{{PROJECT_NAME}}\`, demo data.`,
        `  3) Sestav checklist souborů (package.json, vite, src/…), co musí zůstat, co vyhodit.`,
        `  4) Vyčisti kód v workspace (live/patch) a shrň strukturu; zapiš do knihovny draft note`,
        `     path \`Templates/proposals/<slug>-template.md\` (kind=reference) s popisem šablony + tree + backend_default.`,
        `  5) Řekni jasně: **registrace do katalogu šablon** (modules/codable/templates/<id> + template_list)`,
        `     dělá platforma/admin — ty jako project agent **nepřidáváš** nový builtin template na server sám.`,
        `  6) Dokud není v katalogu, user může projekt klonovat ručně; ty připravíš čistý kód a návrh metadat.`,
        ``,
        `### B) Modul / hotové řešení v knihovně (Playbook recipe)`,
        `- Co to je: **očistěná část** (pattern, feature, how-to, anti-pattern, prompt) — ne celá app.`,
        `- Kde: UI \`/modules/codable/library\`; MCP \`codable_library_search|read|write|ingest\`.`,
        `- **„Převést část na modul“ / „uložit do hotových řešení“** znamená: destilovat obecnou část do playbooku.`,
        `  Postup:`,
        `  1) Identifikuj *obecnou* část (auth flow, cart, form pattern…) vs project-specific business.`,
        `  2) Očisti: žádné API keys, hesla, customer PII, interní URL s tokeny, jména konkrétních klientů.`,
        `  3) Napiš markdown recept: kdy použít, kroky, ukázkový kód, checklist ověření, anti-patterny.`,
        `  4) \`codable_library_write\` s kind=recipe|how-to|anti-pattern|prompt, lifecycle=**draft**,`,
        `     tags_csv, project_slug="${slug}", srozumitelný path (Recipes/… nebo How-to/…).`,
        `  5) active lifecycle **jen** když user výslovně schválí „publikuj do active“ / ověřené obecné.`,
        `  6) V odpovědi uveď path; další agent má search+read místo vymýšlení.`,
        ``,
        `| User řekne | Ty uděláš |`,
        `|------------|-----------|`,
        `| převést projekt na šablonu / udělat z toho template | očista celého FE skeletonu + návrh Templates/proposals + upozornění na admin katalog |`,
        `| převést část na modul / uložit řešení / recept do knihovny | library_write destilované části (draft) |`,
        `| založit nový projekt ze šablony | odkaž na UI Šablony / Nový projekt; nepleť s library_write |`,
        `| použij hotové řešení z knihovny | library_search → read → implementuj v *tomto* projektu |`,
        ``,
        `## Kuchařka — session → recepty (doplněk k B)`,
        `Po úspěšném celku (feature hotová, preview OK) nabídni uložení do knihovny (question karta:`,
        `Uložit jako recept / Ne / Později). Piš plán a step_done poctivě — pomáhají distilleru.`,
        `- **Nekopíruj — odkaž** playbook path, když už recept existuje.`,
        autonomyMode !== 'ask'
          ? `- V Live režimu můžeš zjevně obecný postup rovnou destilovat přes \`codable_library_write\` lifecycle=draft (ne raw chat dump).`
          : `- V Návrhy režimu library_write nevolej sám; navrhni obsah a nech user potvrdit.`,
        ``,
        `## Konvence`,
        `- Odpovídej v preferovaném jazyce UI; kód a identifikátory nech v angličtině.`,
        `- Krátce a k věci — user chce vidět kód, ne dlouhé úvody.`,
        `- Neopakuj celý soubor, jen měněné bloky nebo stručné shrnutí relevantních míst.`,
        `- Když použiješ webový zdroj, preferuj oficiální dokumentaci / primární zdroj a ve stručném`,
        `  shrnutí uveď URL, ze které implementace vychází.`,
        `- Když navrhuješ Supabase migrace, dej ji jako samostatný SQL blok.`,
        `- Pokud narážíš na známý pattern, přečti ho z Codable playbooku`,
        `  (\`codable_library_search\` + \`codable_library_read\`) místo vymýšlení.`,
        ``,
        `## Interaktivní bloky (UI je renderuje jako karty s tlačítky Přijmout / Odmítnout)`,
        `Pokud chceš user schválení před akcí, použij tyto speciální markdown bloky:`,
        ``,
        planningMode
          ? [
            '```plan',
            '1. Krok jedna',
            '2. Krok dva',
            '```',
            '→ karta "Návrh plánu" s Přijmout. Jen když je Planning ZAP — jednou na začátku úkolu.',
            '',
          ].join('\n')
          : autonomyMode === 'ask'
          ? `**(Planning VYP — \`\`\`plan\`\`\` nepoužívej.** Práce je schválená; rovnou patch karta.)\n`
          : `**(Planning VYP — \`\`\`plan\`\`\` nepoužívej.** Práce je schválená; rovnou nativní workspace tooly.)\n`,
        ``,
        ...(autonomyMode === 'ask'
          ? [
            `\`\`\`patch cesta/k/souboru.tsx`,
            `<KOMPLETNÍ nový obsah souboru — celý soubor od začátku po konec>`,
            `\`\`\``,
            `→ karta s Použít / Odmítnout. Použít = zápis do dev workspace na serveru,`,
            `změna je v preview OKAMŽITĚ (HMR) — do gitu se NIC necommituje. VŽDY plný`,
            `nový obsah souboru, ne unified diff (workspace_write přepisuje celý soubor).`,
            `Před úpravou si načti aktuální obsah přes codable_chat_file_read.`,
            ``,
            `\`\`\`commit "krátká zpráva"`,
            `Delší body commitu, volitelný.`,
            `\`\`\``,
            `→ karta "Commit + push" — po kliknutí UI provede skutečný GitLab checkpoint.`,
            ``,
          ]
          : [
            `**Live režim: syntaxe patch/commit karet je ZAKÁZANÁ.**`,
            `Existující soubor měň po malých úsecích přes codable_workspace_replace.`,
            `workspace_write s kompletním obsahem použij jen pro nový soubor nebo nutný celkový přepis.`,
            `Po ověření volej nativní codable_commit. Kód souboru do odpovědi nevypisuj.`,
            ``,
          ]),
        `\`\`\`question`,
        `Krátká otázka na usera (jen když je vyloženě potřeba).`,
        ``,
        `options:`,
        `  - Krátká volba A`,
        `  - Krátká volba B`,
        `  - Nevím / vysvětli`,
        `\`\`\``,
        `→ karta "Otázka" s tlačítky pro každou options bullet. User klikne`,
        ` tlačítko → text option jde jako user message (nebo napíše vlastní text).`,
        `Bez \`options:\` sekce se jen zobrazí text a čeká na user input dole.`,
        ``,
        `\`\`\`step_done N`,
        `\`\`\``,
        `→ oznámení, že krok N aktivního plánu je hotov (N = 1-based index).`,
        ` UI pak v „Aktivním plánu" překlopí checkbox z ⬜ na ✅. Použij ihned`,
        ` po dokončení kroku (patch commitnutý, tool výsledek ověřen atd.).`,
        ` Můžeš zaslat víc bloků najednou pokud si dokončil víc kroků.`,
        ` Po posledním kroku UI plán uzavře a pošle ti výzvu k závěrečnému`,
        ` ověření (preview + shrnutí + commit) — plán nikdy nekončí bez ověření.`,
        ``,
        `Kombinuj s běžným markdown textem. Bloky jsou opt-in — když akce nepotřebuje user schválení, prostě popiš co jsi udělal a pokračuj.`,
      ].join('\n');
      // User content — string nebo multimodal array (když jsou image attachments).
      // Anthropic 'image' block: {type:'image', source:{type:'base64', media_type, data}}.
      const userContent: string | Array<Record<string, unknown>> = imageAttachments.length > 0
        ? [
          { type: 'text', text: fullText },
          ...imageAttachments.map((a) => ({
            type: 'image',
            source: {
              type: 'base64',
              media_type: a.media_type ?? 'image/png',
              data: a.base64 ?? '',
            },
          })),
        ]
        : fullText;

      // Plan injection — když je schválený plán, přidáme system zprávu se stavem
      // (`X/Y kroků hotovo`) před historii. Agent tak vždycky ví, kde je,
      // bez scrollování starých zpráv. Ekvivalent superagent-runner.ts:354
      // (plan reminder injection).
      const planNow = activePlanRef.current; // ref, ne closure — viz deklarace
      const planNextIdx = planNow
        ? planNow.steps.findIndex((_, i) => !planNow.done.includes(i))
        : -1;
      // Planning OFF: aktivní plán je jen tracker (ne brána). Planning ON: striktní NEXT.
      const planSystem = planNow
        ? {
          role: 'system' as const,
          content: planningMode
            ? [
              `## Aktivní plán (${planNow.done.length}/${planNow.steps.length}) — POKRAČUJ, NERESTARTUJ`,
              planNow.steps.map((s, i) =>
                `${planNow.done.includes(i) ? '✅' : '⬜'} ${i + 1}. ${s}`
              ).join('\n'),
              planNextIdx >= 0
                ? `NEXT: ${planNextIdx + 1}. ${planNow.steps[planNextIdx]}`
                : `Vše hotové — ověř preview/commit, nový plán nezačínej.`,
              `ZÁKAZ znovu \`\`\`plan\`\`\`. Po kroku: step_done N, pak další ⬜ tooly.`,
            ].join('\n')
            : [
              `## Pomocný checklist (${planNow.done.length}/${planNow.steps.length}) — NENÍ schvalovací brána`,
              planNow.steps.map((s, i) =>
                `${planNow.done.includes(i) ? '✅' : '⬜'} ${i + 1}. ${s}`
              ).join('\n'),
              planNextIdx >= 0
                ? `Volitelný fokus: ${planNextIdx + 1}. ${planNow.steps[planNextIdx]}`
                : `Checklist hotový — ověř výsledek.`,
              `Planning je VYP: NEposílej nové \`\`\`plan\`\`\` karty. Práce je schválená — rovnou tooly.`,
            ].join('\n'),
        }
        : null;
      const hadActivePlanAtStart = !!planNow;
      const agentTools = buildCodableChatTools({
        autonomyMode,
        godActive,
        orchestratorProjectId: projectToolId,
        vision: modelHasVision,
      });

      // Tah na serveru (Filip 2026-09-06, stream/turns.ts): server drží přepis
      // konverzace S tool cally a výsledky, smyčka běží nezávisle na spojení.
      // Historie se sem už NEposílá — klientská kopie slouží jen zobrazení;
      // model dostává poctivý přepis ze serveru, ne textovou inscenaci z UI.
      const turnOrError = await openUiChatTurn({
        // Request-scoped registrace project toolů podle režimu autonomie.
        // Nahrazuje no_tools fence workaround — subset nemá array parametry,
        // Harmony Jinja bug gpt-oss-120b se nespustí. Interaktivní karty
        // (```patch`, ```plan`, ```commit`) zůstávají markdown protokol.
        tools: agentTools,
        consumer: 'codable',
        project_id: projectId,
        // Server hard-binds every tool call to this slug (sandbox).
        project_slug: slug,
        ...(selectedProfile?.id ? { profile_id: selectedProfile.id } : {}),
        conversation_id: conversationIdRef.current,
        system: [
          { role: 'system', content: systemPreamble },
          ...(planSystem ? [planSystem] : []),
        ],
        // Recency: Qwen utopí režim v dlouhém preamble. Tohle je až před user tah.
        // Řeč režimu = kombinace zámečku a A (M), aby prompt nelhal stavu lišty.
        system_tail: [
          {
            role: 'system',
            content: codableWorkModeSticky(
              consultLock
                ? 'consult'
                : !autoAcceptOn
                ? 'manual'
                : autoCommitActive
                ? 'auto-commit'
                : 'auto',
              slug,
            ),
          },
        ],
        message: { role: 'user', content: userContent },
      }, ac.signal, chatReloginCopy);
      if (turnOrError instanceof Response) {
        const failure = await uiChatFailureMessage(turnOrError);
        setMessages((m) =>
          m.map((x) =>
            x.id === aMsg.id ? { ...x, content: `Chyba: ${failure}`, pending: false } : x
          )
        );
        setBusy(false);
        return;
      }
      turnIdRef.current = turnOrError.turnId;
      chatSessionIdRef.current = null;
      const r = turnOrError.response;
      if (!r.ok || !r.body) {
        const failure = await uiChatFailureMessage(r);
        setMessages((m) =>
          m.map((x) =>
            x.id === aMsg.id ? { ...x, content: `Chyba: ${failure}`, pending: false } : x
          )
        );
        setBusy(false);
        return;
      }
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      acc = '';
      let buf = '';
      const visibleThink = new ThinkVisibleBuffer();
      let thinkingShown = false; // reasoning indikátor — flip jen při změně
      let repetitionStop = false; // degenerativní smyčka → utnout stream
      // Nativní tool jména z tohoto tahu (anti re-plan followup).
      const nativeToolsThisTurn: string[] = [];
      // Jen úspěšné výsledky, v pořadí dokončení. Dokončovací brána nesmí
      // považovat samotný tool_call ani text modelu za provedenou práci.
      const successfulToolsThisTurn: string[] = [];
      // Server posílá `profile` každé kolo a `info` při compaction / denním
      // limitu / Harmony / Gemini. Dřív se oboje polklo jako no-op → uživatel
      // neviděl, že engine uprostřed tahu přepnul profil (fallback po chybě),
      // zatímco prompt dál držel dialekt původního modelu. Vypadalo to jako
      // „model se zasekl", i když běžel. Držíme expectation a oznamujeme změnu.
      let expectedProfileId: string | null = selectedProfile?.id ?? null;
      const shownProfileLabels = new Set<string>();
      // Followup po streamu (fence tools / plan nudge) — flush až po busy=false.
      let pendingFollowup: {
        text: string;
        toolMeta?: ToolOutputMeta;
        images?: Attachment[];
      } | null = null;
      while (true) {
        if (repetitionStop) break;
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split(/\n/);
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const data = line.slice(5).trim();
          if (data === '[DONE]') continue;
          try {
            const evt = JSON.parse(data) as {
              type?: string;
              text?: string;
              delta?: string;
              content?: string;
              message?: string;
              name?: string;
              result?: unknown;
              id?: string;
              label?: string;
            };
            // ACM chat stream format: {type:'text', text:'chunk'} + {type:'usage'} + {type:'done'}
            // + {type:'tool_call', name} / {type:'tool_result', name, result} z backend
            // exec loopu (nativní tools). Fallback: OpenAI-style {delta}/{content}.
            if (evt.type === 'tool_call' && evt.name) {
              nativeToolsThisTurn.push(evt.name);
              thinkingShown = false;
              setMessages((m) =>
                m.map((x) =>
                  x.id === aMsg.id
                    ? {
                      ...x,
                      thinking: false,
                      nativeTools: [...(x.nativeTools ?? []), {
                        name: evt.name!,
                      }],
                    }
                    : x
                )
              );
              continue;
            }
            if (evt.type === 'tool_result' && evt.name) {
              const res = evt.result as
                | { ok?: boolean; error?: string }
                | undefined;
              const ok = !(res && (res.error !== undefined || res.ok === false));
              const toolName = evt.name;
              if (ok) successfulToolsThisTurn.push(toolName);
              // Native write/commit mění workspace/preview — refresh iframe (dřív
              // to dělaly jen UI patch karty; nativní tool loop preview neobnovil).
              if (
                ok &&
                (isCodableChatWriteTool(toolName) ||
                  isCodableChatGitTool(toolName) ||
                  toolName.endsWith('.orchestrator.sync') ||
                  toolName.endsWith('_orchestrator_sync'))
              ) {
                onDeployed?.();
              }
              // God deploy: flush historie teď (před restartem UI) + toast
              if (
                ok &&
                (toolName === 'codable_god_deploy' ||
                  toolName === 'codable.god_deploy')
              ) {
                const clean = messages.filter((m) => !m.pending && m.id !== aMsg.id);
                // + aktuální assistent s dosavadním acc
                const withCurrent = [
                  ...clean,
                  {
                    id: aMsg.id,
                    role: 'assistant' as const,
                    content: acc || '(god deploy…)',
                    createdAt: aMsg.createdAt,
                    nativeTools: undefined,
                    pending: false,
                  },
                ];
                api.mcp('codable.chat_history', {
                  slug,
                  action: 'set',
                  messages: withCurrent,
                }).catch(() => {});
                toast.info(
                  t(
                    'codableChat.godDeploying',
                    'God deploy: kontext a model uloženy — po restartu se vrátí',
                  ),
                );
              }
              setMessages((m) =>
                m.map((x) => {
                  if (x.id !== aMsg.id) return x;
                  let marked = false;
                  return {
                    ...x,
                    nativeTools: (x.nativeTools ?? []).map((tc) => {
                      if (
                        !marked &&
                        (tc.name === toolName ||
                          tc.name.replace(/\./g, '_') ===
                            toolName.replace(/\./g, '_')) &&
                        tc.ok === undefined
                      ) {
                        marked = true;
                        return { ...tc, ok, name: toolName };
                      }
                      return tc;
                    }),
                  };
                })
              );
              continue;
            }
            if (evt.type === 'follow_up_scheduled') {
              // Server sleduje operace, které tah spustil; výsledek přijde jako
              // platformní tah — začni se na něj ptát.
              startPlatformPolling();
              continue;
            }
            if (evt.type === 'heartbeat' || evt.type === 'done' || evt.type === 'profile' ||
              evt.type === 'usage' || evt.type === 'info') {
              // Keep-alive / meta — nesmí shodit stream ani přerušit UI
              if (evt.type === 'info') {
                const notice = describeInfoNotice(evt);
                if (notice) toast.info(notice.text);
              }
              if (evt.type === 'profile') {
                const notice = describeProfileSwitch(
                  evt,
                  expectedProfileId,
                  shownProfileLabels,
                );
                if (notice?.profileId) {
                  shownProfileLabels.add(notice.profileLabel ?? '');
                  expectedProfileId = notice.profileId;
                  toast.info(notice.text);
                  // Srovnat výběr, ať další kolo posílá profile_id nového profilu
                  // a system prompt přepne na jeho dialekt (jinak qwen dialekt +
                  // gemini engine = zasekaný tool loop).
                  const switched = profiles.find((p) => p.id === notice.profileId);
                  if (switched) setSelectedModel(profileKey(switched));
                }
              }
              continue;
            }
            if (evt.type === 'thinking') {
              // Reasoning — jen indikátor. Nesmí se vypínat s každým text
              // chunkem, jinak bublina skáče (think pryč → zase zpět).
              if (!thinkingShown) {
                thinkingShown = true;
                setMessages((m) => m.map((x) => x.id === aMsg.id ? { ...x, thinking: true } : x));
              }
              continue;
            }
            let chunk = '';
            if (evt.type === 'text') chunk = evt.text ?? '';
            else if (evt.type === 'error') {
              chunk = evt.message ? `\n\n**Chyba:** ${evt.message}` : '';
            } else if (!evt.type) {
              chunk = evt.delta ?? evt.content ?? '';
            } else {
              continue;
            }
            if (chunk) {
              const visible = visibleThink.push(chunk);
              if (!visible) {
                if (!thinkingShown) {
                  thinkingShown = true;
                  setMessages((m) =>
                    m.map((x) => x.id === aMsg.id ? { ...x, thinking: true } : x)
                  );
                }
                continue;
              }
              acc += visible;
              // Pojistka proti degenerativní smyčce generování (malé modely
              // opakují týž segment donekonečna — Filip 2026-07-19
              // "(Executing). (Actually, I'll do it)." dokola). Stejný úsek
              // ≥6× po sobě na konci → stream utnout s viditelnou hláškou.
              if (!repetitionStop && acc.length > 400) {
                const tail = acc.slice(-600);
                // 12znakové HTML/JSX (`    </div>\n`) 6× za sebou je běžný kód,
                // ne smyčka. Chyť jen delší frázi s mezerou.
                if (/([^\n]{32,120}?\s[^\n]{0,80}?)\1{5,}$/s.test(tail)) {
                  repetitionStop = true;
                  acc +=
                    '\n\n⚠ **Generování přerušeno** — model se zacyklil v opakování téhož textu. Pošli zprávu znovu (případně zvaž jiný model).';
                  try {
                    await reader.cancel();
                  } catch { /* stream už zavřený */ }
                }
              }
              setMessages((m) =>
                m.map((x) => x.id === aMsg.id ? { ...x, content: acc } : x)
              );
            }
          } catch {
            /* non-JSON line — ignoruj */
          }
        }
      }
      acc += visibleThink.finish();
      thinkingShown = false;
      setMessages((m) =>
        m.map((x) => x.id === aMsg.id ? { ...x, content: acc, thinking: false } : x)
      );
      // pending necháme true přes post-processing (fence tools) — jinak auto-accept
      // plan/patch karet střílí send() ještě při busy=true a followup se zahodí.

      // Slabší model někdy i v Live režimu ignoruje nativní write tool a vypíše
      // celý soubor jako patch kartu. Uzavřený fallback aplikujeme rovnou přes
      // stejný MCP write kontrakt a kód z chatu odstraníme. Neuzavřený blok je
      // uříznutý output limitem: nikdy ho neaplikovat, odstranit jeho torzo a
      // nechat completion guard vyžádat jeden nativní opravný tah.
      let livePatchFallbackHandled = false;
      // Model vypsal soubor do chatu a output limit ho uřízl → cílený pokyn níž.
      let truncatedInlineWrite = false;
      if (autonomyMode === 'workspace') {
        const patches = completePatchBlocks(acc);
        if (patches.length > 0) {
          livePatchFallbackHandled = true;
          let rewritten = '';
          let cursor = 0;
          for (const patch of patches) {
            rewritten += acc.slice(cursor, patch.index);
            try {
              const result = await api.mcp<{
                ok: boolean;
                error?: string;
                visible_now?: string;
              }>('codable.workspace_write', {
                slug,
                path: patch.path,
                content: patch.content,
              });
              if (result.ok) {
                successfulToolsThisTurn.push('codable_workspace_write');
                onDeployed?.();
                rewritten += `\n\n${
                  t('codableChat.liveWriteOk', '✅ Změna v `{{path}}` byla zapsána do DEV workspace.', { path: patch.path })
                }`;
              } else {
                rewritten += `\n\n${
                  t('codableChat.liveWriteFail', '⚠ Zápis `{{path}}` selhal: {{error}}', {
                    path: patch.path,
                    error: result.error ?? 'unknown',
                  })
                }`;
              }
            } catch (error) {
              rewritten += `\n\n${
                t('codableChat.liveWriteFail', '⚠ Zápis `{{path}}` selhal: {{error}}', {
                  path: patch.path,
                  error: (error as Error).message,
                })
              }`;
            }
            cursor = patch.index + patch.full.length;
          }
          acc = rewritten + acc.slice(cursor);
        }

        const truncatedPatchAt = incompletePatchStart(acc);
        if (truncatedPatchAt >= 0) {
          livePatchFallbackHandled = true;
          truncatedInlineWrite = true;
          acc = `${acc.slice(0, truncatedPatchAt).trimEnd()}\n\n` +
            t(
              'codableChat.liveWriteContinue',
              '_Zápis pokračuje nativním workspace nástrojem (obsah souboru se do chatu nevypisuje)._',
            );
        }
      }

      // Auto-execute read-only MCP nástroje. Každý fence blok → jeden MCP call;
      // výsledek se pošle jako auto-followup do stejné konverzace. Blok je vždy
      // \`\`\`tool_<name>[SPACE arg]\n[body?]\n\`\`\`.
      // Tolerantní na jedno- i víceřádkový zápis — gpt-oss píše fence bez
      // newline před zavíracími backticky (```tool_read src/App.tsx```),
      // původní /\n```/ regex ho míjel a agent čekal na výsledek donekonečna.
      const toolReadRe = /```tool_read\s+([^\n`]+?)\s*\n?```/g;
      const toolListRe = /```tool_list\s*\n?```/g;
      const toolGitLogRe = /```tool_git_log\s*\n?```/g;
      const toolEnvStatusRe = /```tool_env_status\s*\n?```/g;
      const toolLogsRe = /```tool_logs\s*\n?```/g;
      const toolPublishStatusRe = /```tool_publish_status\s*\n?```/g;
      const toolScreenshotRe = /```tool_screenshot\s*\n?```/g;
      // step_done N — agent oznamuje dokončení kroku aktivního plánu.
      // Format ```step_done 2``` — N je 1-based (jak user vidí v plánu).
      const stepDoneRe = /```step_done\s+(\d+)\s*\n?```/g;

      const toolReads: string[] = [];
      let mm: RegExpExecArray | null;
      while ((mm = toolReadRe.exec(acc)) !== null) {
        const p = mm[1]?.trim();
        if (p) toolReads.push(p);
      }
      // let — XML parser níže je může zapnout když model použije <tool_call>
      let wantsList = toolListRe.test(acc);
      let wantsGitLog = toolGitLogRe.test(acc);
      let wantsEnvStatus = toolEnvStatusRe.test(acc);
      let wantsLogs = toolLogsRe.test(acc);
      let wantsPublishStatus = toolPublishStatusRe.test(acc);
      let wantsScreenshot = toolScreenshotRe.test(acc);
      // Extract step_done indices (1-based v fence, uložíme 0-based)
      const stepDoneIndices: number[] = [];
      let sdm: RegExpExecArray | null;
      while ((sdm = stepDoneRe.exec(acc)) !== null) {
        const n = parseInt(sdm[1] ?? '', 10);
        if (Number.isFinite(n) && n > 0) stepDoneIndices.push(n - 1);
      }
      // Plán: dokončení / posun. Samotný step_done fence nespouštěl žádný
      // followup — model po něm často ukončí tah a konverzace zůstane viset
      // (Filip 2026-07-12: „zastaví jen tak" + „3/3 nekončí ověřením").
      // Mid-plan → auto-continue nudge; poslední krok → plán UZAVŘÍT (chip
      // zmizí, system injekce přestane) a vynutit závěrečné ověření.
      let planCompleted: ActivePlan | null = null;
      let planNextStep: string | null = null;
      const planCur = activePlanRef.current; // čerstvý stav — closure by tu lhala
      if (stepDoneIndices.length > 0 && planCur) {
        const newDone = Array.from(
          new Set([...planCur.done, ...stepDoneIndices]),
        )
          .filter((i) => i >= 0 && i < planCur.steps.length)
          .sort((a, b) => a - b);
        if (newDone.length !== planCur.done.length) {
          if (newDone.length === planCur.steps.length) {
            planCompleted = { ...planCur, done: newDone };
            activePlanRef.current = planCompleted;
            setActivePlan(planCompleted);
            updatePlan(null, { status: 'completed' });
          } else {
            updatePlan({ ...planCur, done: newDone });
            const next = planCur.steps.findIndex((_, i) => !newDone.includes(i));
            if (next >= 0) planNextStep = `${next + 1}. ${planCur.steps[next]}`;
          }
        }
      }

      // XML tool-call parser (převzato z modules/superagent/superagent-llm.ts:538).
      // Modely fine-tuned na Qwen/Hermes/Kimi tool-calling nezvládají system prompt
      // který jim říká „používej markdown fence" a stejně posílají XML. Podporované
      // formáty:
      //   <tool_call>{"name":"X","arguments":{...}}</tool_call>              — Kimi JSON
      //   <tool_call><function=X>{"arg":...}</function></tool_call>          — Kimi function tag
      //   <tool_call> <function=X> </function> </tool_call>                  — Qwen no-args
      //   <function=X>{"arg":...}</function>                                 — bez wrapperu
      // Volání se z acc (message.content) OKAMŽITĚ odstraní — místo XML tagů
      // zůstane placeholder „🔧 <name>". Filip 2026-07-10: „<tool_call> ... </tool_call>
      // to není dobré."
      const xmlCallsExtracted: Array<{ name: string; args: string }> = [];
      let usedXmlFallback = false;
      {
        let remaining = acc;
        let cleaned = '';
        while (remaining.length > 0) {
          const tcIdx = remaining.indexOf('<tool_call>');
          const fnIdx = remaining.search(/<function=/);
          if (tcIdx === -1 && fnIdx === -1) {
            cleaned += remaining;
            break;
          }
          const nextIdx = tcIdx === -1 ? fnIdx : fnIdx === -1 ? tcIdx : Math.min(tcIdx, fnIdx);
          cleaned += remaining.slice(0, nextIdx);
          remaining = remaining.slice(nextIdx);
          let name = '';
          let args = '{}';
          let consumed = 0;
          if (remaining.startsWith('<tool_call>')) {
            const inner = remaining.slice('<tool_call>'.length);
            const end = inner.indexOf('</tool_call>');
            if (end === -1) {
              cleaned += remaining;
              break;
            }
            const body = inner.slice(0, end).trim();
            consumed = '<tool_call>'.length + end + '</tool_call>'.length;
            const fnMatch = body.match(
              /<function=([^>]+)>([\s\S]*?)<\/function>/,
            );
            if (fnMatch) {
              name = fnMatch[1]!.trim();
              args = fnMatch[2]!.trim() || '{}';
            } else {
              try {
                const p = JSON.parse(body);
                name = String(p.name ?? p.tool_name ?? '');
                const a = p.arguments ?? p.parameters ?? p.args ?? {};
                args = typeof a === 'string' ? a : JSON.stringify(a);
              } catch { /* neplatný JSON */ }
            }
          } else {
            const fnMatch = remaining.match(
              /^<function=([^>]+)>([\s\S]*?)<\/function>/,
            );
            if (!fnMatch) {
              cleaned += '<';
              remaining = remaining.slice(1);
              continue;
            }
            name = fnMatch[1]!.trim();
            args = fnMatch[2]!.trim() || '{}';
            consumed = fnMatch[0].length;
          }
          if (name) {
            xmlCallsExtracted.push({ name, args });
            cleaned += `*🔧 ${name}*`;
            usedXmlFallback = true;
          }
          remaining = remaining.slice(consumed || 1);
        }
        if (usedXmlFallback) {
          acc = cleaned;
          setMessages((m) => m.map((x) => x.id === aMsg.id ? { ...x, content: acc } : x));
        }
      }
      // Mapování XML tool_* jmen → naše marker flags. Args jsou volitelné JSON;
      // pro tool_read očekáváme {path}. Ostatní tools jsou bez args.
      for (const call of xmlCallsExtracted) {
        const n = call.name;
        if (n === 'tool_read') {
          let path = '';
          try {
            const parsed = JSON.parse(call.args);
            path = String(parsed?.path ?? parsed?.file ?? '');
          } catch { /* ignore */ }
          if (!path) {
            const pathM = call.args.match(
              /[\w.\-/]+\.(?:tsx?|jsx?|css|json|md|html|yml|yaml)/,
            );
            if (pathM) path = pathM[0];
          }
          if (path) toolReads.push(path);
        } else if (n === 'tool_list') wantsList = true;
        else if (n === 'tool_git_log') wantsGitLog = true;
        else if (n === 'tool_env_status') wantsEnvStatus = true;
        else if (n === 'tool_logs') wantsLogs = true;
        else if (n === 'tool_publish_status') wantsPublishStatus = true;
        else if (n === 'tool_screenshot') wantsScreenshot = true;
      }
      const anyToolRequested = toolReads.length > 0 || wantsList ||
        wantsGitLog || wantsEnvStatus || wantsLogs || wantsPublishStatus ||
        wantsScreenshot;
      // Interaktivní karty: při Planning VYP plan fence NENÍ brána (jen patch/commit/question).
      const hasInteractiveCard = planningMode
        ? /```(plan|patch|commit|question)[ \t\n]/.test(acc)
        : /```(patch|commit|question)[ \t\n]/.test(acc);
      // A na liště = ten přepínač (Filip 10. 9.: „mělo by to přepínat
      // schvalování jednotlivých tahů“). Dřívší „|| workspace“ přepínač zrušilo.
      const autoModeOn = autoAcceptOn;
      let planDecision: Message['planDecision'] | undefined;
      const planCompleteNote = planCompleted
        ? t(
          'codableChat.planCompleteNote',
          '✅ Všech {{count}} kroků plánu je hotových — plán je UZAVŘENÝ, neposílej další ```step_done```. Proveď závěrečné ověření: zkontroluj preview přes tool_screenshot; bez vision zavolej `codable_ocr`, při potížích `codable_logs`. Krátce shrň, co se změnilo, a pokud jsou necommitnuté změny, pošli commit kartu.',
          { count: planCompleted.steps.length },
        )
        : '';
      if (anyToolRequested) {
        try {
          const parts: string[] = [];
          const tools: string[] = [];
          const followupImages: Attachment[] = [];
          for (const p of toolReads) tools.push(`tool_read ${p}`);
          if (wantsList) tools.push('tool_list');
          if (wantsGitLog) tools.push('tool_git_log');
          if (wantsEnvStatus) tools.push('tool_env_status');
          if (wantsLogs) tools.push('tool_logs');
          if (wantsPublishStatus) tools.push('tool_publish_status');
          if (wantsScreenshot) tools.push('tool_screenshot');
          if (toolReads.length > 0) {
            const readResults = await Promise.all(toolReads.map(async (p) => {
              const r = await api.mcp<
                { ok: boolean; error?: string; content?: string }
              >(
                'codable.chat_file_read',
                { slug, path: p },
              );
              return { path: p, ...r };
            }));
            if (readResults.some((result) => result.ok)) {
              successfulToolsThisTurn.push('codable_chat_file_read');
            }
            parts.push(
              readResults.map((r) =>
                r.ok && r.content
                  ? `**Obsah \`${r.path}\`:**\n\`\`\`\n${r.content}\n\`\`\``
                  : t('codableChat.cannotReadFile', '**Nelze načíst `{{path}}`:** {{error}}', {
                    path: r.path,
                    error: r.error ?? 'unknown',
                  })
              ).join('\n\n'),
            );
          }
          if (wantsList) {
            const r = await api.mcp<
              {
                ok: boolean;
                entries?: Array<{ path: string; type: string }>;
                error?: string;
              }
            >(
              'codable.files',
              { slug, action: 'tree' },
            );
            if (r.ok && r.entries) {
              const files = r.entries.filter((e) => e.type === 'blob').map((
                e,
              ) => e.path).slice(
                0,
                200,
              );
              parts.push(
                `**Repo tree (${files.length} souborů${
                  r.entries.length > 200 ? ', truncated na 200' : ''
                }):**\n\`\`\`\n${files.join('\n')}\n\`\`\``,
              );
            } else {
              parts.push(`**tool_list selhal:** ${r.error ?? 'unknown'}`);
            }
          }
          if (wantsGitLog) {
            const r = await api.mcp<
              {
                ok: boolean;
                commits?: Array<
                  {
                    id: string;
                    message: string;
                    author_name: string;
                    created_at: string;
                  }
                >;
                error?: string;
              }
            >(
              'codable.history',
              { slug, action: 'list' },
            );
            if (r.ok && r.commits) {
              const rows = r.commits.slice(0, 30).map((c) =>
                `${c.id.slice(0, 8)} ${
                  c.created_at.slice(0, 16).replace('T', ' ')
                } ${c.author_name}: ${c.message.split('\n')[0]}`
              );
              parts.push(
                t(
                  'codableChat.commitHistoryBlock',
                  '**Commit history (posledních {{count}}):**\n```\n{{rows}}\n```',
                  { count: rows.length, rows: rows.join('\n') },
                ),
              );
            } else {
              parts.push(`**tool_git_log selhal:** ${r.error ?? 'unknown'}`);
            }
          }
          if (wantsEnvStatus) {
            const g = await api.mcp<
              {
                ok: boolean;
                env_status?: string;
                services?: unknown;
                error?: string;
              }
            >(
              'codable.project_get',
              { slug },
            );
            if (g.ok) {
              successfulToolsThisTurn.push('codable_project_get');
              parts.push(
                `**Env status:** \`${g.env_status ?? 'unknown'}\`\n\n**Services:**\n\`\`\`json\n${
                  JSON.stringify(g.services ?? {}, null, 2)
                }\n\`\`\``,
              );
            } else {
              parts.push(`**tool_env_status selhal:** ${g.error ?? 'unknown'}`);
            }
          }
          if (wantsLogs) {
            // codable.logs (slug-scoped wrapper) místo přímého apphost.logs —
            // funguje i pod project tokenem bez apphost:read scope.
            const r = await api.mcp<
              { ok: boolean; logs?: string; error?: string }
            >(
              'codable.logs',
              { slug, lines: 200 },
            );
            if (r.ok && r.logs) {
              successfulToolsThisTurn.push('codable_logs');
              parts.push(
                t(
                  'codableChat.apphostLogsBlock',
                  '**Apphost logy (posledních ~200 řádků):**\n```\n{{logs}}\n```',
                  { logs: r.logs.slice(-8000) },
                ),
              );
            } else {
              parts.push(`**tool_logs selhal:** ${r.error ?? 'unknown'}`);
            }
          }
          if (wantsPublishStatus) {
            const g = await api.mcp<
              {
                ok: boolean;
                publish?: {
                  domain: string | null;
                  desired: boolean;
                  actual: boolean;
                  url: string | null;
                };
                preview_url?: string;
                error?: string;
              }
            >(
              'codable.project_get',
              { slug },
            );
            if (g.ok) {
              parts.push(
                `**Publish stav:**\n\`\`\`json\n${
                  JSON.stringify(
                    { preview_url: g.preview_url, ...g.publish },
                    null,
                    2,
                  )
                }\n\`\`\``,
              );
            } else {
              parts.push(
                `**tool_publish_status selhal:** ${g.error ?? 'unknown'}`,
              );
            }
          }
          if (wantsScreenshot) {
            const r = await api.mcp<
              {
                ok: boolean;
                png_base64?: string;
                size_bytes?: number;
                url?: string;
                error?: string;
                failure_kind?: string;
                action_required?: string;
                diagnostics?: Array<
                  { step?: string; ok?: boolean; message?: string }
                >;
              }
            >(
              'codable.preview_screenshot',
              { slug },
            );
            if (r.ok && r.png_base64 && visionProfile?.vision === true) {
              successfulToolsThisTurn.push('codable_preview_screenshot');
              followupImages.push({
                name: `snapshot-${Date.now()}.png`,
                kind: 'image',
                media_type: 'image/png',
                base64: r.png_base64,
                content: `data:image/png;base64,${r.png_base64}`,
                size: r.size_bytes ?? 0,
              });
              parts.push(
                t(
                  'codableChat.screenshotAttached',
                  '**Screenshot preview** — přiložen jako obrázek ({{kb}} kB, {{url}}). Analyzuj přiložený obrázek přímo; nepoužívej OCR.',
                  { kb: Math.round((r.size_bytes ?? 0) / 1024), url: r.url ?? '?' },
                ),
              );
            } else if (r.ok && r.png_base64) {
              successfulToolsThisTurn.push('codable_preview_screenshot');
              // Text-only model — obrázek nelze přiložit (image part by shodil
              // request). Agent dostane info, ať navrhne přepnutí profilu.
              parts.push(
                t(
                  'codableChat.screenshotNoVision',
                  '**Screenshot pořízen** ({{kb}} kB, {{url}}), ale aktivní model nemá vision — obrázek nepřikládám. Zavolej `codable_ocr` pro textový obsah stránky, nebo popiš userovi, že obrázek vidí jen multimodální model (přepínač nahoře).',
                  { kb: Math.round((r.size_bytes ?? 0) / 1024), url: r.url ?? '?' },
                ),
              );
            } else {
              parts.push(
                `**tool_screenshot selhal:** ${snapshotErrorText(r)}${snapshotDiagnosticsText(r)}`,
              );
            }
          }
          const formatHint = usedXmlFallback
            ? t(
              'codableChat.xmlToolHint',
              '\n\n⚠ Použil jsi `<tool_call>` XML syntax — UI ho toleruje, ale příště používej VÝHRADNĚ markdown fence ```tool_<název>\n```.',
            )
            : '';
          const fullContent = `${toolFollowupPrefix()}\n\n${parts.join('\n\n')}${formatHint}` +
            (planCompleteNote
              ? `\n\n${planCompleteNote}`
              : planNextStep
              ? t(
                'codableChat.nextStepHint',
                '\n\nNEXT: {{step}}. NEopakuj plán — rovnou tooly na NEXT.',
                { step: planNextStep },
              )
              : t(
                'codableChat.continueNextTool',
                '\n\nPokračuj dalším konkrétním krokem (tool), bez restartu plánu.',
              ));
          // Summary pro starší tool_output v LLM prompt payloadu (šetří kontext).
          // Přednost mají tool_read paths (agent si je pamatuje že načetl konkrétní
          // soubor); ostatní nástroje jen jménem.
          const summary = t(
            'codableChat.prevToolsSummary',
            '[předchozí kolo MCP nástrojů: {{tools}}] — obsah v tomto turnu neopakuji, pokud potřebuješ původní data, zavolej daný tool znovu.',
            { tools: tools.join(', ') },
          );
          // Fronta po busy=false (enqueueSend) — ne setTimeout(send) s race.
          // `text` je protokol (prefix + instrukce pro model) a posílá se do LLM;
          // `body` je to jediné, co se ukazuje uživateli — vtělí se do panelu
          // nástrojů agentova tahu, tedy na totéž místo jako nativní chips.
          pendingFollowup = {
            text: fullContent,
            toolMeta: { tools, summary, body: parts.join('\n\n') },
            images: followupImages,
          };
        } catch (e) {
          setMessages((m) => [...m, {
            id: uid(),
            role: 'user',
            createdAt: new Date().toISOString(),
            content: `${toolErrorPrefix()} ${(e as Error).message}`,
          }]);
        }
      } else if (!hasInteractiveCard && (planCompleteNote || planNextStep)) {
        // step_done bez toolů — postrč dál BEZ restartu celého úkolu.
        const nudge = planCompleteNote ||
          t(
            'codableChat.stepNudge',
            'Hotovo pro předchozí krok. NEXT: {{step}}. NEposílej znovu plán, NEzačínej od kroku 1, NEopakuj status — rovnou tooly na NEXT, pak step_done.',
            { step: planNextStep },
          );
        pendingFollowup = { text: nudge };
      }

      // Plan fence po streamu:
      // - Planning ZAP + Auto/Live → accept + (jen když planning) continue followup
      // - Planning VYP → tiše badge / tracker; NIKDY PLAN_ACCEPT_FOLLOWUP (to vypadá jako planning mode)
      {
        const planBodies = extractPlanBlocks(acc);
        if (planBodies.length > 0) {
          const body = planBodies[0]!;
          const parsedNew = parsePlanMarkdown(body);
          const curPlan = activePlanRef.current;
          const sameAsActive = !!(curPlan && plansRoughlySame(curPlan.steps, parsedNew.steps));
          const claimKey = planAutoClaimKey(body);
          const alreadyClaimed = autoDoneRef.current.has(claimKey);

          if (!planningMode) {
            // Jen UI badge — žádný re-import smyčky, žádný „plán přijat pokračuj“.
            planDecision = 'auto';
            if (
              !hadActivePlanAtStart && !alreadyClaimed &&
              parsedNew.steps.length > 0
            ) {
              // Volitelný tichý tracker pro panel (bez followup user message).
              autoDoneRef.current.add(claimKey);
              persistChatAutoDone(projectId, autoDoneRef.current);
              const plan: ActivePlan = {
                historyId: makePlanHistoryId(),
                steps: parsedNew.steps,
                done: parsedNew.done,
                createdAt: new Date().toISOString(),
                title: t('codableChat.planFromChat', 'Plán z chatu'),
                source: 'chat',
              };
              updatePlan(plan, {
                source: 'chat',
                title: plan.title ?? undefined,
              });
            }
          } else if (autoModeOn) {
            if (hadActivePlanAtStart && (sameAsActive || alreadyClaimed)) {
              planDecision = 'auto';
            } else if (
              hadActivePlanAtStart && !sameAsActive &&
              parsedNew.steps.length > 0
            ) {
              planDecision = undefined;
            } else if (!alreadyClaimed) {
              autoDoneRef.current.add(claimKey);
              persistChatAutoDone(projectId, autoDoneRef.current);
              let plan = await importPlanMarkdown(
                body,
                'chat',
                t('codableChat.planFromChat', 'Plán z chatu'),
              );
              if (!plan && parsedNew.steps.length > 0) {
                plan = {
                  historyId: makePlanHistoryId(),
                  steps: parsedNew.steps,
                  done: parsedNew.done,
                  createdAt: new Date().toISOString(),
                  title: t('codableChat.planFromChat', 'Plán z chatu'),
                  source: 'chat',
                };
                updatePlan(plan, {
                  source: 'chat',
                  title: plan.title ?? undefined,
                });
              }
              if (plan) {
                planDecision = 'auto';
                const alreadyMoving = stepDoneIndices.length > 0 ||
                  /```patch\b/i.test(acc) ||
                  nativeToolsThisTurn.some((n) =>
                    /workspace_(?:write|replace)|commit|promote|agent_memory_put/i.test(n)
                  );
                if (!alreadyMoving) {
                  if (pendingFollowup) {
                    pendingFollowup = {
                      ...pendingFollowup,
                      text: `${pendingFollowup.text}\n\n${planAcceptFollowup()}`,
                    };
                  } else {
                    pendingFollowup = { text: planAcceptFollowup() };
                  }
                }
              }
            } else {
              planDecision = 'auto';
            }
          }
        }
      }

      // Live: chybějící zápis se vrací AGENTOVI (jeden opravný tah).
      // Git se nenutí. Uživateli se neukazuje stop hláška.
      const evidenceRetry = fullText.startsWith(COMPLETION_EVIDENCE_PREFIX);
      const combinedEvidence = [
        ...priorCompletionEvidence(fullText),
        ...successfulToolsThisTurn,
      ];
      const wroteThisTurn = combinedEvidence.some((name) =>
        /workspace_(?:write|replace)|god_write/i.test(name.replaceAll('.', '_'))
      );
      const evidenceGap = completionEvidenceGap({
        userText: fullText,
        assistantText: acc,
        successfulTools: combinedEvidence,
        autonomyMode,
        autoCommit: autoCommitActive,
        hasInteractiveCard,
        evidenceRetry,
      }) ?? (
        autonomyMode === 'workspace' && livePatchFallbackHandled && !wroteThisTurn &&
          !evidenceRetry
          ? 'missing_write'
          : null
      );
      // Uříznutý inline výpis: model se musí vrátit k nativnímu zápisu, jinak
      // to zkusí znovu vypsat do chatu a znovu se to uřízne (Filip 2026-08-05:
      // „ať používá dobré formátování, ne aby to řezal").
      if (truncatedInlineWrite) {
        const rewriteHint = t(
          'codableChat.truncatedWriteHint',
          'Obsah souboru NEVYPISUJ do chatu — output limit ho uřízne a zápis se nepoužije. Zapiš změnu jedním voláním nativního toolu codable_workspace_write (path + celý content v argumentech), pak jen krátce napiš co se změnilo. U velkého souboru měň jen ten jeden soubor a v odpovědi kód neopakuj.',
        );
        pendingFollowup = pendingFollowup
          ? { ...pendingFollowup, text: `${rewriteHint}\n\n${pendingFollowup.text}` }
          : {
            text: rewriteHint,
            toolMeta: {
              tools: ['inline_write_truncated'],
              summary: t('codableChat.truncatedWriteSummary', '[uříznutý výpis → dokončení nativním zápisem]'),
            },
          };
      }
      if (evidenceGap && !evidenceRetry) {
        const guardFollowup = completionEvidenceFollowup(
          evidenceGap,
          fullText,
          combinedEvidence,
          autoCommitActive,
          (key, fallback, values) => String(i18n.t(key, fallback, values)),
        );
        pendingFollowup = pendingFollowup
          ? {
            ...pendingFollowup,
            text: `${guardFollowup}\n\n${pendingFollowup.text}`,
          }
          : {
            text: guardFollowup,
            toolMeta: {
              tools: ['completion_evidence'],
              summary: t('codableChat.evidenceSummary', '[zápis do workspace — pokračování]'),
            },
          };
      }

      // Stream + post-processing hotov → teprve teď uvolnit kartu (auto-accept)
      // a busy (následující enqueueSend).
      setMessages((m) =>
        m.map((x) =>
          x.id === aMsg.id
            ? {
              ...x,
              pending: false,
              thinking: false,
              content: acc,
              ...(planDecision ? { planDecision } : {}),
            }
            : x
        )
      );
      if (pendingFollowup) {
        sendAfterBusyRef.current = pendingFollowup.text;
        sendAfterBusyExtrasRef.current = {
          toolMeta: pendingFollowup.toolMeta,
          images: pendingFollowup.images,
        };
      }
    } catch (e) {
      const errName = (e as Error).name || '';
      const errMsg = String((e as Error).message ?? e);
      const streamDrop = errName === 'AbortError' || errName === 'TimeoutError' ||
        /network|fetch|load failed|aborted|premature/i.test(errMsg);
      if (!streamDrop) {
        setMessages((m) =>
          m.map((x) => {
            if (x.id !== aMsg.id) return x;
            const prev = (typeof x.content === 'string' ? x.content : acc).trimEnd();
            return {
              ...x,
              content: prev || t('codable.errorGeneric', { msg: errMsg }),
              pending: false,
              thinking: false,
              interrupted: true,
            };
          })
        );
      } else {
        // User stop — keep whatever was generated
        setMessages((m) =>
          m.map((x) =>
            x.id === aMsg.id
              ? {
                ...x,
                pending: false,
                thinking: false,
                content: (typeof x.content === 'string' && x.content.trim())
                  ? x.content
                  : acc || x.content,
              }
              : x
          )
        );
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  }, [
    input,
    messages,
    projectId,
    slug,
    repo,
    previewUrl,
    prodPreviewUrl,
    gitlabUrl,
    publish,
    envStatus,
    workspaceInstance,
    prodInstance,
    feSplit,
    topology,
    projectStatus,
    lastCommitSha,
    lastPublishEvent,
    planningMode,
    autonomyMode,
    autoAccept,
    autoCommitActive,
    attachments,
    activePlan,
    selectedProfile,
    contextBudget,
    preferredLang,
    preferredLangInfo,
    rememberPrompt,
    onDeployed,
    startPlatformPolling,
    godActive,
    workspaceTreeSnap,
    packageMeta,
    memoryContent,
    memoryPath,
    hasAgentMemory,
    importPlanMarkdown,
    updatePlan,
    stopVoice,
    t,
  ]);

  const handleComposerKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        send();
        return;
      }
      if (
        (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') ||
        e.shiftKey || e.metaKey || e.ctrlKey || e.altKey
      ) {
        return;
      }
      if (promptHistory.length === 0) return;

      const el = e.currentTarget;
      if (el.selectionStart !== el.selectionEnd) return;
      const atStart = el.selectionStart === 0;
      const atEnd = el.selectionStart === input.length;
      const singleLine = !input.includes('\n');

      if (e.key === 'ArrowUp') {
        if (!singleLine && !atStart) return;
        e.preventDefault();
        const nextIndex = promptHistoryIndex === null
          ? promptHistory.length - 1
          : Math.max(0, promptHistoryIndex - 1);
        if (promptHistoryIndex === null) promptDraftRef.current = input;
        setPromptHistoryIndex(nextIndex);
        setComposerFromHistory(promptHistory[nextIndex] ?? '');
        return;
      }

      if (!singleLine && !atEnd) return;
      if (promptHistoryIndex === null) return;
      e.preventDefault();
      const nextIndex = promptHistoryIndex + 1;
      if (nextIndex >= promptHistory.length) {
        setPromptHistoryIndex(null);
        setComposerFromHistory(promptDraftRef.current);
        promptDraftRef.current = '';
        return;
      }
      setPromptHistoryIndex(nextIndex);
      setComposerFromHistory(promptHistory[nextIndex] ?? '');
    },
    [input, promptHistory, promptHistoryIndex, send, setComposerFromHistory],
  );

  const abort = () => {
    abortRef.current?.abort();
    // Stop = přerušení tahu na SERVERU (stream/turns.ts). Zavření čtení eventů
    // samo tah nezastaví — přesně proto tahy na serveru existují.
    const turnId = turnIdRef.current;
    if (turnId) void interruptUiChatTurn(turnId);
    const sessionId = chatSessionIdRef.current;
    if (!sessionId) return;
    void fetch('/ui/capabilities/chat-stop', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    }).catch(() => {});
  };

  /** Pošli hned, nebo zařaď až busy spadne (auto-accept / patch followup race). */
  const enqueueSend = useCallback((
    text: string,
    toolMeta?: ToolOutputMeta,
    extraImages?: Attachment[],
  ) => {
    const msg = text.trim();
    if (!msg) return;
    if (busyRef.current) {
      sendAfterBusyRef.current = msg;
      sendAfterBusyExtrasRef.current = { toolMeta, images: extraImages };
      setQueuedPreview(msg);
      return;
    }
    void send(msg, toolMeta, extraImages);
  }, [send]);
  enqueueSendRef.current = enqueueSend;

  useEffect(() => {
    const run = (text: string) => {
      const msg = text.trim();
      if (msg) enqueueSend(msg);
    };
    const pending = takeCodableChatAsk(slug);
    if (pending) run(pending);
    const onAsk = (e: Event) => {
      const d = (e as CustomEvent<{ slug?: string; text?: string }>).detail;
      if (!d?.text || (d.slug && d.slug !== slug)) return;
      run(takeCodableChatAsk(slug) ?? d.text);
    };
    window.addEventListener(CODABLE_CHAT_ASK, onAsk);
    return () => window.removeEventListener(CODABLE_CHAT_ASK, onAsk);
  }, [slug, enqueueSend]);

  useEffect(() => {
    if (busy || !sendAfterBusyRef.current) return;
    const msg = sendAfterBusyRef.current;
    const extras = sendAfterBusyExtrasRef.current;
    sendAfterBusyRef.current = null;
    sendAfterBusyExtrasRef.current = null;
    setQueuedPreview(null);
    void send(msg, extras?.toolMeta, extras?.images);
  }, [busy, send]);

  // God resume po reloadu — pošli continuity až je send ready a !busy
  useEffect(() => {
    if (busy || !pendingGodResume) return;
    const msg = pendingGodResume;
    setPendingGodResume(null);
    enqueueSend(msg);
  }, [busy, pendingGodResume, enqueueSend]);
  // Ruční kompakce — stejný sumarizátor jako autocompact, ale na user klik
  // (Filip 2026-07-12: „potřebujeme možnost compacting chatu").
  const manualCompact = async () => {
    if (busy) return;
    const hist = messages.filter((m) => !m.pending);
    if (hist.length < KEEP_RECENT + 4) {
      toast.info(t('codableChat.chatTooShort'));
      return;
    }
    setBusy(true);
    try {
      const compacted = await compactHistory(hist);
      if (compacted) {
        setMessages(compacted);
        toast.success(t('codableChat.compactDone'));
      } else {
        toast.error(t('codableChat.compactFail'));
      }
    } finally {
      setBusy(false);
    }
  };
  const clear = async () => {
    if (!(await themedConfirm(t('codableChat.clearConfirm', 'Vyčistit chat i aktivní plán pro tento projekt?')))) {
      return;
    }
    setMessages([]);
    if (activePlanRef.current) {
      updatePlan(null, { status: 'cancelled' });
    } else {
      activePlanRef.current = null;
      setActivePlan(null);
    }
    try {
      window.localStorage.removeItem(historyKey);
    } catch { /* ignore */ }
    try {
      window.localStorage.removeItem(planKey);
    } catch { /* ignore */ }
    // Nová konverzace na serveru: starý přepis v DB zůstává, model ho už nevidí.
    conversationIdRef.current = newConversationId(historyKey);
    api.mcp('codable.chat_history', { slug, action: 'clear' }).catch(() => {});
  };

  // Action handler — když user klikne Accept/Reject/Apply na kartu z asistenta,
  // pošle followup zprávu do chatu (bez agent runtime — LLM čte a pokračuje).
  const handleAction = useCallback((p: ActionPayload) => {
    let followup = '';
    switch (p.type) {
      case 'plan_accept': {
        followup = planAcceptFollowup();
        void (async () => {
          // Ruční accept — pokud už běží stejný plán, jen continue bez re-import smyčky.
          const parsed = parsePlanMarkdown(p.block);
          const cur = activePlanRef.current;
          if (cur && plansRoughlySame(cur.steps, parsed.steps)) {
            enqueueSend(followup);
            return;
          }
          let plan = await importPlanMarkdown(
            p.block,
            'chat',
            t('codableChat.planFromChat'),
          );
          if (!plan && parsed.steps.length > 0) {
            plan = {
              historyId: makePlanHistoryId(),
              steps: parsed.steps,
              done: parsed.done,
              createdAt: new Date().toISOString(),
              title: t('codableChat.planFromChat', 'Plán z chatu'),
              source: 'chat',
            };
            updatePlan(plan, {
              source: 'chat',
              title: plan.title ?? undefined,
            });
          }
          if (plan) enqueueSend(followup);
          else {
            toast.error(
              t(
                'codableChat.planImportFail',
                'Plán se nepodařilo načíst — zkontroluj plan blok',
              ),
            );
          }
        })();
        return;
      }
      case 'plan_reject':
        updatePlan(null, { status: 'cancelled' });
        followup = t('codableChat.planRejected');
        break;
      case 'plan_edit':
        // Vloží plán do input pro user editaci
        setInput(p.block.trim());
        return;
      case 'patch_apply': {
        // PatchCard zapsal přes codable.workspace_write přímo do dev workspace
        // — vite HMR promítl změnu do preview (Filip 2026-07-12: úpravy na
        // serveru, git až checkpoint). Deploy se neděje.
        const path = p.meta?.path ?? '?';
        onDeployed?.(); // iframe soft-refresh — pojistka když HMR ws neprojde
        followup = t(
          'codableChat.patchAppliedFollowup',
          'Patch `{{path}}` zapsán do workspace — preview se aktualizovalo (HMR). Pokračuj dalším krokem; až bude funkční celek, pošli commit kartu jako GitLab checkpoint.',
          { path },
        );
        break;
      }
      case 'patch_error': {
        const path = p.meta?.path ?? '?';
        followup = t(
          'codableChat.patchErrorFollowup',
          'Zápis patch karty pro `{{path}}` selhal: {{error}}. Zkontroluj aktuální obsah souboru (codable_chat_file_read) a zapiš KOMPLETNÍ nový obsah přes codable_workspace_write nebo full-file patch kartu (ne unified diff).',
          { path, error: p.meta?.error ?? 'unknown' },
        );
        break;
      }
      case 'patch_reject':
        followup = t(
          'codableChat.patchRejectFollowup',
          'Patch neaplikuj. Vysvětli proč byl špatně a zkus jinak.',
        );
        break;
      case 'patch_revert':
        // Uživatel vrátil úpravu z chatu (PatchCard zapsal původní obsah zpět
        // přes workspace_write → HMR). Jen soft-refresh iframe jako pojistka;
        // NEprompti agenta k pokračování — revert je uživatelské rozhodnutí.
        onDeployed?.();
        return;
      case 'commit_confirm': {
        // CommitCard už provedl skutečný codable.commit — meta nese výsledek.
        // Followup dává agentovi ground truth místo slibů.
        if (p.meta?.ok === 'true' && p.meta?.nothing === 'true') {
          // Prázdný commit = DŮKAZ, že agent nic nezapsal — bez tvrdého textu
          // dál klidně hlásil „Krok 3 hotov" (Filip 2026-07-12 transcript).
          followup = t(
            'codableChat.commitEmptyFollowup',
            'Commit: žádné změny k commitu — workspace je čistý. To znamená, že jsi ŽÁDNOU změnu reálně nezapsal: popsat úpravu v textu ≠ provést ji. Kroky, které jsi popsal, se NESTALY — neposílej step_done a teď proveď zápis přes codable_workspace_replace nebo codable_workspace_write pro každý soubor, který jsi chtěl změnit.',
          );
        } else if (p.meta?.ok === 'true') {
          followup = t(
            'codableChat.commitOkFollowup',
            'Commit `{{sha}}` ({{files}} souborů) pushnut na GitLab — checkpoint hotov. Pokračuj.',
            { sha: p.meta?.sha ?? '?', files: p.meta?.files ?? '?' },
          );
        } else {
          followup = t(
            'codableChat.commitFailFollowup',
            'Commit selhal: {{error}}. Zkontroluj stav workspace a navrhni řešení.',
            { error: p.meta?.error ?? 'unknown' },
          );
        }
        break;
      }
      case 'commit_reject':
        followup = t(
          'codableChat.commitRejectFollowup',
          'Commit neproveď. Zkus jiný popis nebo změny.',
        );
        break;
      case 'question_reply':
        // ask_user options — user klikl na jedno z tlačítek. Text option
        // se pošle jako user zpráva (Filip 2026-07-10: superagent ask_user).
        followup = p.meta?.text ?? p.block ?? '';
        if (!followup) return;
        break;
      default:
        return;
    }
    // Auto-send followup — enqueue když ještě běží stream/post-processing.
    enqueueSend(followup);
  }, [enqueueSend, importPlanMarkdown, t, updatePlan, onDeployed]);

  return (
    <div
      className={`flex flex-col h-full min-h-0 overflow-hidden bg-gray-950 border-l border-gray-800${
        compactChrome ? ' codable-chat-compact' : ''
      }`}
    >
      {
        /* Header — naming + model picker + akce (text-sm, ikony 14-15)
          Klik na brand = God mode (admin): Codable AI ↔ žlutý God Mode. */
      }
      <div className='flex items-center justify-between px-4 py-3 border-b border-gray-800'>
        <div className='flex items-center gap-2 min-w-0 flex-1'>
          <Bot
            size={15}
            className={`shrink-0 ${godActive ? 'text-amber-400' : 'text-blue-400'}`}
          />
          <button
            type='button'
            onClick={requestGodToggle}
            disabled={godBusy}
            className={`text-sm font-medium shrink-0 rounded px-1 -mx-1 transition-colors disabled:opacity-60 ${
              godActive
                ? 'text-amber-300 hover:text-amber-200'
                : 'text-gray-200 hover:text-white'
            }`}
            title={godActive
              ? t(
                'codableChat.godModeOn',
                'God mode ZAP — agent má ACL k souborům chatu + deploy. Vyprší: {{exp}}. Klik = zamknout.',
                { exp: godExpires ?? '?' },
              )
              : t(
                'codableChat.godBrandHint',
                'Codable AI — kliknutím (admin) odemkneš God mode pro soubory chatu.',
              )}
            aria-pressed={godActive}
          >
            {godBusy
              ? '…'
              : godActive
              ? t('codableChat.godModeLabel', 'God Mode')
              : t('codableChat.brandLabel', 'Codable AI')}
          </button>
          <div ref={modelMenuRef} className='relative min-w-0 flex-1'>
            <button
              onClick={() => {
                setShowModelPicker((v) => !v);
                setShowModelSettings(false);
              }}
              className='ml-1 flex items-center gap-1 px-2 py-0.5 rounded text-xs text-gray-400 hover:text-gray-100 max-w-full truncate transition-colors'
              title={t('codableChat.switchModelTitle', 'Přepnout model')}
            >
              <span className='truncate'>
                {selectedProfile
                  ? profileLabel(selectedProfile)
                  : (profiles[0]
                    ? profileLabel(profiles[0])
                    : t('codableChat.modelsFallback', 'Modely'))}
              </span>
              <ChevronDown size={12} className='shrink-0' />
            </button>
            {showModelPicker && (
              <div className='absolute top-full left-0 mt-1 bg-gray-900 border border-gray-800 rounded-lg shadow-lg py-1 min-w-[280px] max-w-[360px] z-50'>
                {profiles.length > 0
                  ? (
                    profiles.map((p, i) => {
                      const label = profileLabel(p);
                      const key = profileKey(p);
                      const origin = profileOrigin(p);
                      const OriginIcon = origin === 'private'
                        ? Lock
                        : origin === 'cloud'
                        ? Cloud
                        : Home;
                      const originClass = origin === 'private'
                        ? 'text-amber-300'
                        : origin === 'cloud'
                        ? 'text-sky-300'
                        : 'text-emerald-300';
                      const originLabel = profileOriginLabel(origin);
                      const active = key === selectedModel ||
                        (!profiles.some((x) => profileKey(x) === selectedModel) &&
                          (label === selectedModel ||
                            p.model === selectedModel));
                      return (
                        <button
                          key={i}
                          onClick={() => {
                            pickModel(key);
                            setShowModelPicker(false);
                          }}
                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-800 ${
                            active ? 'text-blue-400' : 'text-gray-300'
                          }`}
                        >
                          <div className='flex items-center gap-1.5 font-medium'>
                            <span className='truncate'>{label}</span>
                            <OriginIcon
                              size={10}
                              className={`shrink-0 ${originClass}`}
                              aria-label={originLabel}
                            >
                              <title>{originLabel}</title>
                            </OriginIcon>
                          </div>
                          <div className='text-[10px] text-gray-500'>
                            <span className='font-mono'>
                              {p.model ?? p.id ?? 'model'}
                            </span>
                            {' · '}
                            {inferAgentFamily(p)} · {p.preferred_patch_mode ?? 'full-file'}
                            {p.vision ? ' · vision' : ''}
                          </div>
                          {p.endpoint && (
                            <div className='text-[10px] text-gray-500 truncate'>
                              {p.endpoint}
                            </div>
                          )}
                        </button>
                      );
                    })
                  )
                  : (
                    <div className='px-3 py-2 text-xs text-gray-400'>
                      {profilesQ.isFetching
                        ? (
                          <div className='flex items-center gap-2'>
                            <Loader2
                              size={12}
                              className='animate-spin text-blue-400'
                            />
                            {t(
                              'codableChat.loadingProfiles',
                              'Načítám LLM profily…',
                            )}
                          </div>
                        )
                        : profilesQ.isError
                        ? (
                          <div>
                            <div className='text-red-300'>
                              {t(
                                'codableChat.profilesLoadFailed',
                                'LLM profily se nepodařilo načíst.',
                              )}
                            </div>
                            <div className='mt-1 text-[10px] text-gray-500'>
                              {profilesQ.error instanceof Error ? profilesQ.error.message : t(
                                'codableChat.unknownError',
                                'Neznámá chyba',
                              )}
                            </div>
                          </div>
                        )
                        : allProfiles.length > 0
                        ? (
                          <div>
                            <div className='text-amber-300'>
                              {t(
                                'codableChat.noCodableProfile',
                                'Žádný profil není povolený pro Codable.',
                              )}
                            </div>
                            <div className='mt-1 text-[10px] text-gray-500'>
                              {t(
                                'codableChat.enableCodableProfileHint',
                                'V LLM Profiles zapni profilu `codable_enabled`.',
                              )}
                            </div>
                          </div>
                        )
                        : (
                          <div>
                            <div className='text-gray-300'>
                              {t(
                                'codableChat.noProfiles',
                                'Žádné LLM profily.',
                              )}
                            </div>
                            <div className='mt-1 text-[10px] text-gray-500'>
                              {t(
                                'codableChat.addSyncProfiles',
                                'Přidej nebo synchronizuj profily v LLM Profiles.',
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                <div className='mt-1 flex flex-wrap items-center justify-between gap-1 border-t border-gray-800 px-2 pt-1'>
                  <button
                    onClick={() => void profilesQ.refetch()}
                    className='flex items-center gap-1 rounded px-2 py-1 text-[10px] text-gray-400 hover:bg-gray-800 hover:text-gray-200 disabled:opacity-40'
                    disabled={profilesQ.isFetching}
                    title={t(
                      'codableChat.reloadProfilesTitle',
                      'Znovu načíst LLM profily',
                    )}
                  >
                    {profilesQ.isFetching
                      ? <Loader2 size={11} className='animate-spin' />
                      : <RefreshCw size={11} />}
                    {t('codableChat.refresh', 'Obnovit')}
                  </button>
                  {onOpenLlmModels && (
                    <button
                      onClick={() => {
                        setShowModelPicker(false);
                        onOpenLlmModels();
                      }}
                      className='flex items-center gap-1 rounded px-2 py-1 text-[10px] text-gray-300 hover:bg-gray-800 hover:text-gray-100'
                      title={t('codableChat.addOwnModelTitle')}
                    >
                      <Plus size={11} />
                      {t('codableChat.addOwnModel')}
                    </button>
                  )}
                  <button
                    onClick={() => setShowOwnModelSetup((v) => !v)}
                    className={`flex items-center gap-1 rounded px-2 py-1 text-[10px] ${
                      showOwnModelSetup
                        ? 'bg-blue-500/15 text-blue-300'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-gray-100'
                    }`}
                    title={t('codableChat.perUserAccount')}
                  >
                    Codex
                  </button>
                  {onOpenConnectIde && (
                    <button
                      onClick={() => {
                        setShowModelPicker(false);
                        onOpenConnectIde();
                      }}
                      className='flex items-center gap-1 rounded px-2 py-1 text-[10px] text-gray-300 hover:bg-gray-800 hover:text-gray-100'
                      title={t(
                        'codableChat.connectIdeTitle',
                        'Připojit externí IDE přes MCP',
                      )}
                    >
                      <Plug size={11} />
                      {t('codableChat.connectIde', 'Připojit IDE/MCP')}
                    </button>
                  )}
                </div>
                {showOwnModelSetup && (
                  <div className='mx-2 mt-2 rounded-lg border border-blue-500/20 bg-blue-500/5 p-2'>
                    <div className='text-xs font-medium text-blue-200'>
                      Codex OAuth
                    </div>
                    <div className='mt-1 text-[10px] text-gray-500'>
                      {t('codableChat.perUserAccount')}
                    </div>
                    <div className='mt-2 rounded-lg border border-gray-800 bg-gray-950/80 p-2'>
                      <div className='flex items-center justify-between gap-2'>
                        <div>
                          <div className='text-[11px] font-medium text-gray-200'>
                            Codex OAuth
                          </div>
                          <div className='text-[10px] text-gray-500'>
                            {t('codableChat.codexOauthHint')}
                          </div>
                        </div>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] ${
                            codexNeedsReconnect
                              ? 'bg-amber-500/15 text-amber-300'
                              : userCodexProfile
                              ? 'bg-emerald-500/15 text-emerald-300'
                              : 'bg-gray-800 text-gray-500'
                          }`}
                        >
                          {codexNeedsReconnect
                            ? t('codableChat.reconnect')
                            : userCodexProfile
                            ? t('codableChat.connected')
                            : t('codableChat.waiting')}
                        </span>
                      </div>
                      <div className='mt-2 flex items-center justify-end gap-1'>
                        {userCodexProfile && (
                          <button
                            onClick={() => void disconnectCodexProfile()}
                            disabled={codexSaveBusy}
                            className='rounded px-2 py-1 text-[10px] text-gray-400 hover:bg-gray-800 hover:text-gray-200 disabled:opacity-40'
                          >
                            {t('codableChat.remove')}
                          </button>
                        )}
                        {(codexNeedsReconnect || !userCodexProfile) && (
                          <button
                            onClick={startCodexConnect}
                            className='rounded bg-blue-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-blue-500'
                          >
                            {codexNeedsReconnect
                              ? t('codableChat.loginAgain')
                              : t('codableChat.login')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className='flex items-center gap-1 shrink-0'>
          <div className='relative'>
            <button
              onClick={() => {
                setShowModelSettings((v) => !v);
                setShowModelPicker(false);
                setShowOwnModelSetup(false);
              }}
              className='p-1.5 rounded text-gray-400 hover:text-gray-100 transition-colors'
              title={t('codableChat.modelSettingsTitle', 'Nastavení modelů')}
            >
              <Settings size={14} />
            </button>
            {showModelSettings && (
              <div className='absolute top-full right-0 mt-1 w-80 bg-gray-900 border border-gray-800 rounded-lg shadow-lg p-3 z-50'>
                <div className='text-xs font-medium text-gray-200'>
                  {t('codableChat.modelsForCodable', 'Modely pro Codable')}
                </div>
                <div className='mt-1 text-[11px] text-gray-500'>
                  {t(
                    'codableChat.modelSettingsHint',
                    'Do pickeru jdou platformní profily s `codable_enabled=true`; vlastní Codex/OpenAI model přidej přímo ve výběru modelu.',
                  )}
                </div>
                <div className='mt-3 flex items-center justify-between gap-2'>
                  <button
                    onClick={() => void profilesQ.refetch()}
                    className='flex items-center gap-1 rounded px-2 py-1 text-[10px] text-gray-400 hover:bg-gray-800 hover:text-gray-200 disabled:opacity-40'
                    disabled={profilesQ.isFetching}
                    title={t(
                      'codableChat.reloadProfilesTitle',
                      'Znovu načíst LLM profily',
                    )}
                  >
                    {profilesQ.isFetching
                      ? <Loader2 size={11} className='animate-spin' />
                      : <RefreshCw size={11} />}
                    {t('codableChat.refresh', 'Obnovit')}
                  </button>
                  {onOpenConnectIde && (
                    <button
                      onClick={() => {
                        setShowModelSettings(false);
                        onOpenConnectIde();
                      }}
                      className='flex items-center gap-1 rounded px-2 py-1 text-[10px] text-gray-400 hover:text-gray-100'
                      title={t(
                        'codableChat.connectIdeTitle',
                        'Připojit externí IDE přes MCP',
                      )}
                    >
                      <Plug size={11} />
                      {t('codableChat.connectIdeShort', 'IDE/MCP')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowModelSettings(false);
                      navigate('/modules/llm-profiles');
                    }}
                    className='flex items-center gap-1 rounded px-2 py-1 text-[10px] text-gray-400 hover:text-gray-100'
                    title={t(
                      'codableChat.openProfilesTitle',
                      'Otevřít platformní LLM profily',
                    )}
                  >
                    <Settings size={11} />
                    {t('codableChat.platformProfiles')}
                  </button>
                </div>
              </div>
            )}
          </div>
          <span
            className={`hidden sm:inline-flex h-7 min-w-[5.5rem] items-center justify-center px-1 text-[10px] font-mono tabular-nums ${
              ctxPct >= 85 ? 'text-red-400' : ctxPct >= 60 ? 'text-amber-400' : 'text-gray-500'
            }`}
            title={t('codableChat.contextUsageTitle', {
              used: Math.round(ctxUsed / 1000),
              budget: contextBudget / 1000,
              pct: Math.round(COMPACT_AT * 100),
              defaultValue:
                'Orientační obsazenost kontextu: ~{{used}}k / {{budget}}k tokenů. Nad {{pct}} % se starší zprávy automaticky zkomprimují do souhrnu.',
            })}
          >
            {formatTokenCount(ctxUsed)}/{formatTokenCount(contextBudget)}
          </span>
          <button
            onClick={manualCompact}
            disabled={busy}
            className='p-1.5 rounded text-gray-400 hover:text-gray-100 disabled:opacity-40 transition-colors'
            title={t('codableChat.compactTitle', {
              count: KEEP_RECENT,
              defaultValue:
                'Zkomprimovat chat — starší zprávy se shrnou do souhrnu, posledních {{count}} zůstane doslovně. Uvolní kontext modelu.',
            })}
          >
            <FoldVertical size={14} />
          </button>
          <button
            onClick={clear}
            className='p-1.5 rounded text-gray-400 hover:text-gray-100 transition-colors'
            title={t('codableChat.clearChatTitle', 'Vyčistit chat')}
          >
            <Trash2 size={14} />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className='p-1.5 rounded text-gray-400 hover:text-gray-100 transition-colors'
              title={t('codableChat.close', 'Zavřít')}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Aktivní plán — trvalý panel, ne schovaný v liště (Filip 2026-07-18) */}
      {activePlan && (
        <div className='shrink-0 border-b border-emerald-500/20 bg-emerald-500/5'>
          <button
            type='button'
            onClick={() => setPlanPanelExpanded((v) => !v)}
            className='flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-emerald-500/10 transition-colors'
          >
            <Check size={13} className='shrink-0 text-emerald-400' />
            <span className='text-xs font-semibold text-emerald-300 truncate'>
              {activePlan.title ||
                t('codableChat.activePlanDefaultTitle', 'Aktivní plán')}
            </span>
            <span className='text-[11px] text-emerald-400/80 shrink-0'>
              {t('codableChat.activePlan', {
                done: activePlan.done.length,
                total: activePlan.steps.length,
                defaultValue: 'Plán {{done}}/{{total}}',
              })}
            </span>
            <ChevronDown
              size={13}
              className={`ml-auto shrink-0 text-emerald-400/70 transition-transform ${
                planPanelExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>
          {planPanelExpanded && (
            <div className='px-4 pb-3 space-y-2'>
              <ol className='space-y-1'>
                {activePlan.steps.map((step, i) => {
                  const done = activePlan.done.includes(i);
                  return (
                    <li key={i} className='flex items-start gap-2 text-xs'>
                      <span
                        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                          done
                            ? 'border-emerald-500/40 bg-emerald-500/20 text-emerald-300'
                            : 'border-gray-700 text-gray-600'
                        }`}
                      >
                        {done ? <Check size={10} /> : <span className='text-[9px]'>{i + 1}</span>}
                      </span>
                      <span
                        className={done ? 'text-gray-500 line-through' : 'text-gray-300'}
                      >
                        {step}
                      </span>
                    </li>
                  );
                })}
              </ol>
              <button
                type='button'
                onClick={async () => {
                  if (
                    !(await themedConfirm(
                      t('codableChat.cancelActivePlanConfirm', {
                        done: activePlan.done.length,
                        total: activePlan.steps.length,
                        defaultValue: 'Zrušit aktivní plán? ({{done}}/{{total}} kroků hotovo)',
                      }),
                    ))
                  ) {
                    return;
                  }
                  const progress = `${activePlan.done.length}/${activePlan.steps.length}`;
                  updatePlan(null, { status: 'cancelled' });
                  const cancelMsg = t(
                    'codableChat.planCancelledFollowup',
                    'Aktivní plán byl zrušen uživatelem (hotovo {{progress}} kroků). Nepokračuj v krocích zrušeného plánu a neposílej ```step_done```. Krátce potvrď a počkej na nové zadání.',
                    { progress },
                  );
                  if (busy) {
                    sendAfterBusyRef.current = cancelMsg;
                    abortRef.current?.abort();
                  } else {
                    void send(cancelMsg);
                  }
                }}
                className='inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-red-400 transition-colors'
              >
                <X size={11} /> {t('codableChat.cancelPlan', 'Zrušit plán')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Messages + floating scroll-down */}
      <div className='relative flex-1 min-h-0'>
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className='h-full overflow-y-auto px-4 py-4 space-y-4'
        >
          {messages.length === 0 && (
            <div className='h-full flex flex-col items-center justify-center gap-4 px-4 text-center'>
              <div className='w-12 h-12 rounded-2xl bg-blue-600/15 border border-blue-500/25 flex items-center justify-center'>
                <CodableIcon size={22} className='text-blue-400' />
              </div>
              <div className='text-base font-medium text-gray-200'>
                {t('codableChat.emptyTitle', 'Co postavíme?')}
              </div>
              <div className='flex flex-wrap justify-center gap-2'>
                {getSuggestions(t).map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setInput(s);
                      taRef.current?.focus();
                    }}
                    className='px-3 py-1.5 rounded-full bg-gray-900 border border-gray-800 hover:border-blue-500/50 text-xs text-gray-400 hover:text-gray-200 transition-colors'
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, idx) => {
            // Tool followup (auto-zpráva s výstupem MCP tool volání). Nově se
            // výsledek vtěluje do panelu nástrojů agentova tahu -> tady se jen
            // vyřadí z proudu. Bez hostitele (stará uložená historie, řádek bez
            // assistantova tahu nad sebou) se renderuje samostatně.
            const isToolFollowup = !!m.toolOutput ||
              (m.role === 'user' && startsWithToolFollowup(m.content));
            if (isToolFollowup) {
              if (m.toolOutput?.body?.trim() && hostedToolOutputs.hostedIds.has(m.id)) {
                return null;
              }
              const tools = m.toolOutput?.tools ?? [];
              const label = tools.length > 0
                ? tools.length === 1 ? tools[0] : t('codableChat.toolCount', {
                  count: tools.length,
                  tools: tools.slice(0, 3).join(', '),
                  more: tools.length > 3 ? '…' : '',
                  defaultValue: '{{count}} nástroje: {{tools}}{{more}}',
                })
                : t('codableChat.toolResults', 'Výsledky nástrojů');
              return (
                <div key={m.id}>
                  <details className='group max-w-full'>
                    <summary className='cursor-pointer list-none select-none inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg border font-mono bg-emerald-500/10 text-emerald-300 border-emerald-500/20'>
                      <Check size={10} className='shrink-0' />
                      {label}
                      <ChevronDown
                        size={10}
                        className='shrink-0 transition-transform group-open:rotate-180'
                      />
                    </summary>
                    <div className={`mt-2 ${PROSE_CLASSES}`}>
                      <ReactMarkdown>
                        {m.toolOutput?.body?.trim() || stripToolFollowupPrefix(m.content)}
                      </ReactMarkdown>
                    </div>
                  </details>
                </div>
              );
            }
            if (m.role === 'user' && startsWithToolError(m.content)) {
              return (
                <div key={m.id}>
                  <span className='inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg border font-mono bg-red-500/10 text-red-300 border-red-500/20'>
                    <X size={10} className='shrink-0' />
                    {t('codableChat.toolError', 'Chyba MCP nástroje')}
                  </span>
                </div>
              );
            }
            if (m.platformEvent) {
              // Platformní zpráva (výsledek operace, kterou agent spustil) —
              // sbalený marker uprostřed, ne bublina usera.
              return (
                <div key={m.id} className='flex justify-center'>
                  <details className='group max-w-[90%]'>
                    <summary className='cursor-pointer list-none select-none inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border font-medium bg-gray-900 text-sky-300 border-sky-500/30'>
                      <Bot size={10} className='shrink-0' />
                      {t('codableChat.platformEvent', 'Platforma')} · {platformEventTitle(m.content)}
                      <ChevronDown
                        size={10}
                        className='shrink-0 transition-transform group-open:rotate-180'
                      />
                    </summary>
                    <div
                      className={`mt-2 p-3 rounded-xl border border-gray-800 bg-gray-900 ${PROSE_CLASSES}`}
                    >
                      <ReactMarkdown>{platformEventBody(m.content)}</ReactMarkdown>
                    </div>
                  </details>
                </div>
              );
            }
            if (m.compacted) {
              // Autocompact marker — sbalený souhrn, ať je vidět co model ví
              return (
                <div key={m.id} className='flex justify-center'>
                  <details className='group max-w-[90%]'>
                    <summary className='cursor-pointer list-none select-none inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border font-medium bg-gray-900 text-purple-300 border-purple-500/30'>
                      <Archive size={10} className='shrink-0' />
                      {t(
                        'codableChat.contextCompacted',
                        'Kontext zkomprimován',
                      )}
                      <ChevronDown
                        size={10}
                        className='shrink-0 transition-transform group-open:rotate-180'
                      />
                    </summary>
                    <div
                      className={`mt-2 p-3 rounded-xl border border-gray-800 bg-gray-900 ${PROSE_CLASSES}`}
                    >
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                    </div>
                  </details>
                </div>
              );
            }
            if (m.role === 'user') {
              // Kompaktní bublina vpravo
              return (
                <div key={m.id} className='flex flex-col items-end gap-1'>
                  <div className='max-w-[85%] bg-blue-600 text-white rounded-2xl rounded-br-md px-3.5 py-2'>
                    <div className='prose prose-invert prose-sm max-w-none text-white
                      prose-p:my-0.5 prose-p:leading-relaxed prose-p:text-white prose-p:break-words
                      prose-strong:text-white prose-li:text-white prose-headings:text-white
                      prose-code:text-blue-100 prose-code:break-all
                      prose-pre:bg-blue-700/50 prose-pre:text-xs prose-pre:overflow-x-auto prose-pre:whitespace-pre-wrap
                      prose-li:my-0.5 prose-ul:my-1 prose-ol:my-1
                      [&_*]:max-w-full [&_*]:break-words'>
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                    </div>
                  </div>
                  {formatMessageTime(m.createdAt) && (
                    <time
                      dateTime={m.createdAt}
                      className='text-[10px] text-gray-600 mr-1 tabular-nums'
                    >
                      {formatMessageTime(m.createdAt)}
                    </time>
                  )}
                </div>
              );
            }
            // Assistant — plná šířka bez bubliny; hlavička jen u první zprávy
            // v sekvenci (tool followupy sekvenci nepřerušují)
            let j = idx - 1;
            while (j >= 0 && isToolRow(messages[j])) j--;
            const firstOfSeq = j < 0 || messages[j].role !== 'assistant';
            // Nástroje tohoto tahu: nativní (SSE tool_call/tool_result) +
            // výsledky fence nástrojů, které sem vtělil hostitel. Oboje na
            // JEDNOM místě, aby aktivita „někde v průběhu" nebyla.
            const hosted = hostedToolOutputs.byHost.get(m.id);
            const nativeTools = m.nativeTools ?? [];
            const toolCount = nativeTools.length + (hosted?.tools.length ?? 0);
            const toolFailed = nativeTools.some((tc) => tc.ok === false);
            return (
              <div key={m.id} className='w-full'>
                {firstOfSeq && (
                  <div className='flex items-center gap-1.5 mb-1.5'>
                    <CodableIcon size={12} className='text-blue-400' />
                    <span className='text-[10px] font-medium uppercase tracking-wider text-gray-500'>
                      Codable
                    </span>
                    {formatMessageTime(m.createdAt) && (
                      <time
                        dateTime={m.createdAt}
                        className='text-[10px] text-gray-600 tabular-nums'
                      >
                        {formatMessageTime(m.createdAt)}
                      </time>
                    )}
                  </div>
                )}
                {toolCount > 0 && (
                  // JEDNO místo pro nástroje tohoto tahu: nativní tool cally z
                  // backend exec loopu (chips) + vtělené výsledky fence nástrojů.
                  // Dřív byly chips inline v bublině a výsledky fence plovoucí
                  // user řádky v proudu — aktivita bez konkrétního místa.
                  <details className='group mb-1.5 max-w-full'>
                    <summary className='cursor-pointer list-none select-none inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg border font-mono bg-gray-800/60 text-gray-300 border-gray-700 hover:border-gray-600'>
                      {toolFailed
                        ? <X size={10} className='shrink-0 text-red-400' />
                        : <Check size={10} className='shrink-0 text-emerald-400' />}
                      <span>
                        {t('codableChat.toolsInSection', {
                          count: toolCount,
                          defaultValue: 'Nástroje ({{count}})',
                        })}
                      </span>
                      <ChevronDown
                        size={10}
                        className='shrink-0 transition-transform group-open:rotate-180'
                      />
                    </summary>
                    <div className='mt-1.5 flex flex-wrap gap-1.5'>
                      {nativeTools.map((tc, i) => {
                        const kind = toolKind(tc.name);
                        return (
                          <span
                            key={`${tc.name}-${i}`}
                            className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg border font-mono ${
                              toolChipClass(tc.name, tc.ok)
                            }`}
                            title={`${toolKindLabel(kind)}: ${tc.name}`}
                          >
                            {tc.ok === false
                              ? <X size={10} className='shrink-0' />
                              : <Check size={10} className='shrink-0' />}
                            <span className='text-[9px] font-bold opacity-80'>
                              {toolKindLabel(kind)}
                            </span>
                            <span>{tc.name}</span>
                          </span>
                        );
                      })}
                      {(hosted?.tools ?? []).map((name, i) => {
                        const kind = toolKind(name);
                        return (
                          <span
                            key={`fence-${name}-${i}`}
                            className={
                              `inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg border font-mono ${
                                toolChipClass(name, true)
                              }`
                            }
                            title={`${toolKindLabel(kind)}: ${name}`}
                          >
                            <Check size={10} className='shrink-0' />
                            <span className='text-[9px] font-bold opacity-80'>
                              {toolKindLabel(kind)}
                            </span>
                            <span>{name}</span>
                          </span>
                        );
                      })}
                    </div>
                    {(hosted?.bodies.length ?? 0) > 0 && (
                      <div className='mt-2 flex flex-col gap-2'>
                        {hosted!.bodies.map((b, i) => (
                          <div key={`body-${i}`} className={PROSE_CLASSES}>
                            <ReactMarkdown>{b}</ReactMarkdown>
                          </div>
                        ))}
                      </div>
                    )}
                  </details>
                )}
                {m.pending && !m.content
                  ? (
                    // Streaming indikátor — tři pulzující tečky (+ thinking label)
                    <div className='flex items-center gap-1 py-1.5'>
                      <span className='w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce' />
                      <span className='w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce [animation-delay:150ms]' />
                      <span className='w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce [animation-delay:300ms]' />
                      {m.thinking && (
                        <span className='ml-1.5 text-[11px] text-gray-500 italic'>
                          {t('codableChat.thinking', 'přemýšlí…')}
                        </span>
                      )}
                    </div>
                  )
                  : m.content
                  ? (
                    <>
                      {m.pending
                        ? (
                          // Markdown se renderuje OD PRVNÍHO chunku. Původní
                          // plain-text větev (2026-07-12) řešila jediný problém
                          // — syrový patch/plan fence rozbíjel layout — ale
                          // vyřešila ji plošně: maskování zůstalo, formatter se
                          // vypnul pro celou zprávu. Výsledek: text se nejdřív
                          // celý vysype jako surovina a nadpisy, kód a seznamy
                          // se doplní až po dojetí streamu. Maskování
                          // (`maskStreamingFence` uřízne obsah před
                          // neuzavřeným speciálním fence) zároveň drží objem
                          // parsování dole — zatímco teče patch, markdown vidí
                          // jen to, co bylo před ním.
                          //
                          // `whitespace-pre-wrap` z původní větve tu záměrně
                          // není: markdown dělá z prázdného řádku odstavec a
                          // pre-wrap by k němu přidal ještě newline → dvojitý
                          // odstup. Stejně jako v ChatPanelu.
                          <div className={`break-words ${PROSE_CLASSES}`}>
                            <ReactMarkdown>{maskStreamingFence(m.content)}</ReactMarkdown>
                          </div>
                        )
                        : (
                      <AssistantMessage
                        content={m.content}
                        pending={false}
                        projectSlug={slug}
                        onAction={(payload) => handleAction(payload)}
                        // Planning VYP: plan karty se neauto-acceptují jako brána (jen badge / tichý tracker).
                        // Auto-accept platí pro patch (a commit přes autoCommit) u tahů z této session —
                        // ne jen poslední assistant (followup by jinak vrátil tlačítka Přijmout/Odmítnout).
                        // Podmínkou je A na liště: v M režimu karta čeká na klik i v Live.
                        autoAccept={autoAcceptOn &&
                          liveAutoMsgIdsRef.current.has(m.id)}
                        autoCommit={autoCommitActive &&
                          autoAcceptOn &&
                          liveAutoMsgIdsRef.current.has(m.id)}
                        claimAuto={claimAuto}
                        wasAuto={wasAuto}
                        planDecision={m.planDecision ??
                          (!planningMode ? 'auto' : undefined)}
                      />
                        )}
                      {m.pending && m.thinking && (
                        // Thinking mezi tool koly — text už existuje, ale model
                        // znovu přemýšlí; bez tohohle to vypadá jako zamrznutí.
                        <div className='flex items-center gap-1.5 mt-1 text-[11px] text-gray-500 italic'>
                          <span className='w-1 h-1 rounded-full bg-gray-500 animate-pulse' />
                          {t('codableChat.thinking', 'přemýšlí…')}
                        </div>
                      )}
                    </>
                  )
                  : null}
              </div>
            );
          })}
        </div>
        {showScrollDown && (
          <button
            onClick={scrollToBottom}
            className='absolute bottom-3 right-4 w-8 h-8 rounded-lg text-gray-400 hover:text-gray-100 flex items-center justify-center transition-colors'
            title={t('codableChat.down', 'Dolů')}
          >
            <ArrowDown size={14} />
          </button>
        )}
      </div>

      {/* Composer */}
      <div
        className={compactChrome
          ? 'px-2 pt-1 pb-[max(0.4rem,env(safe-area-inset-bottom))] space-y-2'
          : 'p-3 pt-1 space-y-2'}
      >
        {(liveCtx.open_file || liveCtx.selection) && (
          <div className='flex flex-wrap gap-1'>
            {liveCtx.open_file && (
              <span
                className='max-w-full truncate rounded-lg border border-gray-800 bg-gray-900/80 px-2 py-0.5 font-mono text-[10px] text-gray-300'
                title={t('codableChat.ctxFileHint', 'Chat ví, který soubor máš otevřený')}
              >
                {liveCtx.open_file}
                {typeof liveCtx.open_file_line === 'number' ? `:${liveCtx.open_file_line}` : ''}
              </span>
            )}
            {liveCtx.selection && (liveCtx.selection as { selector?: string }).selector && (
              <span
                className='max-w-full truncate rounded-lg border border-blue-500/30 bg-blue-950/40 px-2 py-0.5 font-mono text-[10px] text-blue-300'
                title={t('codableChat.ctxSelHint', 'Chat ví, který prvek v náhledu jsi vybral')}
              >
                {(liveCtx.selection as { selector?: string }).selector}
                {(liveCtx.selection as { text?: string }).text
                  ? ` „${(liveCtx.selection as { text?: string }).text}"`
                  : ''}
              </span>
            )}
          </div>
        )}
        {attachments.length > 0 && (
          <div className='flex flex-wrap gap-1.5'>
            {attachments.map((a, i) => (
              <div
                key={i}
                className='inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-gray-800 border border-gray-700 text-xs text-gray-300'
              >
                {a.kind === 'image'
                  ? <ImageIcon size={11} className='text-blue-400' />
                  : <FileText size={11} className='text-gray-500' />}
                <span className='font-mono max-w-[160px] truncate'>
                  {a.name}
                </span>
                <span className='text-gray-500'>
                  ({Math.round(a.size / 1024) || 1} kB)
                </span>
                <button
                  onClick={() => removeAttachment(i)}
                  className='text-gray-500 hover:text-red-400'
                  title={t('codableChat.removeAttachment', 'Odebrat')}
                >
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
        {busy && queuedPreview && (
          // Fronta se hlásí NAD vstupem, ne v ikonové řadě pod ním (Filip 10. 9.:
          // „zařazená zpráva by se měla objevit nad oknem chatu, jako skoro v
          // každým chat rozhraním"). Ve spodní řadě překážela ikonám a byla
          // uříznutá na 240 px.
          <div className='flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-950/30 px-2.5 py-1.5'>
            <div className='min-w-0 flex-1'>
              <div className='text-[10px] font-medium uppercase tracking-wide text-amber-300/80'>
                {t('codableChat.queuedHint', 'Zařazeno')}
              </div>
              <div
                className='line-clamp-3 whitespace-pre-line break-words text-xs text-amber-100'
                title={queuedPreview}
              >
                {queuedPreview}
              </div>
            </div>
            <button
              type='button'
              onClick={() => {
                sendAfterBusyRef.current = null;
                sendAfterBusyExtrasRef.current = null;
                setQueuedPreview(null);
              }}
              className='shrink-0 rounded p-0.5 text-amber-400/70 transition-colors hover:bg-amber-500/15 hover:text-amber-200'
              title={t('codableChat.queuedCancel', 'Zrušit zařazenou zprávu')}
            >
              <X size={12} />
            </button>
          </div>
        )}
        <div
          className='rounded-2xl bg-gray-900 border border-gray-800 focus-within:border-blue-500 transition-colors'
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
          }}
        >
          <input
            ref={fileInputRef}
            type='file'
            multiple
            className='hidden'
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <input
            ref={cameraInputRef}
            type='file'
            accept='image/*'
            capture='environment'
            className='hidden'
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <input
            ref={planFileInputRef}
            type='file'
            accept='.md,.markdown,text/markdown,text/plain'
            className='hidden'
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void importPlanMd(file);
              e.target.value = '';
            }}
          />
          <textarea
            ref={taRef}
            rows={compactChrome ? 1 : 2}
            className={`w-full bg-transparent px-3.5 pt-3 pb-1 text-sm text-gray-100 placeholder:text-gray-400 focus:outline-none resize-none max-h-40 overflow-y-auto ${
              compactChrome ? 'min-h-[44px]' : 'min-h-[64px]'
            }`}
            placeholder={attachments.length > 0
              ? t(
                'codableChat.attachmentPlaceholder',
                'Popiš k přiloženému souboru…',
              )
              : t('codableChat.changePlaceholder', 'Popiš změnu…')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleComposerKeyDown}
          />
          <div
            className='flex flex-wrap items-center gap-1 px-2 pb-2 min-w-0 gap-y-1'
          >
            <div className='flex h-8 shrink-0 items-center gap-0.5'>
              <button
                type='button'
                onClick={() => fileInputRef.current?.click()}
                className={CHAT_ICON}
                title={t(
                  'codableChat.uploadFileTitle',
                  'Nahrát soubor (nebo drop do chat area)',
                )}
              >
                <Paperclip size={14} />
              </button>
              {compactChrome && (
                <button
                  type='button'
                  onClick={() => cameraInputRef.current?.click()}
                  className={CHAT_ICON}
                  title={t('codableChat.takePhoto', 'Fotka do promptu')}
                >
                  <Camera size={14} />
                </button>
              )}
              <button
                type='button'
                onClick={() => void toggleVoice()}
                className={`${CHAT_ICON} ${voiceOn ? '!text-red-400 hover:!text-red-300' : ''}`}
                title={voiceOn
                  ? t('codableChat.voiceStop', 'Zastavit hlas')
                  : t('codableChat.voiceStart', 'Říct prompt')}
              >
                <Mic size={14} />
              </button>
              {!compactChrome && (
              <>
              <button
                type='button'
                onClick={takeSnapshot}
                className={CHAT_ICON}
                title={t(
                  'codableChat.snapshotTitle',
                  'Snapshot preview (headless chromium → přiloží screenshot k další zprávě)',
                )}
                disabled={busy || snapshotBusy}
              >
                {snapshotBusy
                  ? <Loader2 size={14} className='animate-spin' />
                  : <Camera size={14} />}
              </button>
              <div
                className='relative shrink-0'
                onPointerDown={(e) => e.stopPropagation()}
              >
                <button
                  type='button'
                  onClick={() => setShowPromptTips((v) => !v)}
                  className={`${CHAT_ICON} ${showPromptTips ? '!text-amber-300 hover:!text-amber-200' : ''}`}
                  title={t('codableChat.tipsTitle')}
                  aria-label={t('codableChat.tipsTitle')}
                >
                  <Lightbulb size={14} />
                </button>
                {showPromptTips && (
                  <div className='absolute bottom-full left-0 z-30 mb-2 w-72 rounded-lg border border-gray-800 bg-gray-950 p-1 shadow-2xl'>
                    {getPromptTips(t).map((tip) => (
                      <button
                        key={tip}
                        onClick={() => {
                          setInput(tip);
                          setShowPromptTips(false);
                          requestAnimationFrame(() => taRef.current?.focus());
                        }}
                        className='flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-xs leading-snug text-gray-300 hover:bg-gray-800 hover:text-gray-100'
                      >
                        <Lightbulb
                          size={12}
                          className='mt-0.5 shrink-0 text-gray-400'
                        />
                        <span>{tip}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              </>
              )}
            </div>
            <div className='flex min-w-0 flex-1 flex-wrap items-center gap-1'>
              <ToolbarToggle
                active={planningMode}
                onClick={togglePlanning}
                label={t('codableChat.planToggle', 'Plán')}
                title={planningMode
                  ? t(
                    'codableChat.planningOnTitle',
                    'Planning mode ZAP — plán před každou akcí',
                  )
                  : t(
                    'codableChat.planningOffTitle',
                    'Planning mode VYP — drobné změny rovnou',
                  )}
                tone='amber'
              />
              {!compactChrome && (
              <details
                className='relative shrink-0'
                data-codable-menu='plan-tools'
                onToggle={(e) => {
                  const current = e.currentTarget;
                  if (current.open) closeCodableMenus(current);
                }}
              >
                <summary
                  className={`${CHAT_ICON} cursor-pointer list-none [&::-webkit-details-marker]:hidden ${
                    activePlan || showPlanHistory
                      ? '!text-emerald-300 hover:!text-emerald-200'
                      : ''
                  }`}
                  title={t('codableChat.planToolsTitle', 'Nástroje plánu')}
                  aria-label={t('codableChat.planToolsTitle', 'Nástroje plánu')}
                >
                  <FileText size={14} />
                </summary>
                <div className='absolute bottom-full left-0 z-20 mb-2 w-60 rounded-lg border border-gray-800 bg-gray-950 p-1 shadow-2xl'>
                  <button
                    onClick={() => {
                      if (planningMode) planFileInputRef.current?.click();
                      closeCodableMenus();
                    }}
                    className='flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-gray-300 hover:bg-gray-800 hover:text-emerald-300 disabled:opacity-35 disabled:hover:text-gray-300 disabled:hover:bg-transparent'
                    disabled={busy || !planningMode}
                    title={planningMode
                      ? t(
                        'codableChat.importPlanTitle',
                        'Importovat plán z Markdown souboru',
                      )
                      : t(
                        'codableChat.importPlanDisabledTitle',
                        'Nejdřív zapni Plán',
                      )}
                  >
                    <Upload size={13} />
                    {planningMode
                      ? t(
                        'codableChat.importPlanTitle',
                        'Importovat plán z Markdown souboru',
                      )
                      : t(
                        'codableChat.importNeedsPlanning',
                        'Import MD: zapni Plán',
                      )}
                  </button>
                  <button
                    onClick={() => {
                      openPlanEditor();
                      closeCodableMenus();
                    }}
                    className='flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-gray-300 hover:bg-gray-800 hover:text-emerald-300 disabled:opacity-35 disabled:hover:text-gray-300 disabled:hover:bg-transparent'
                    disabled={!activePlan}
                  >
                    <FileText size={13} />
                    {activePlan
                      ? t(
                        'codableChat.editPlanMd',
                        'Editovat aktivní plán jako MD',
                      )
                      : t(
                        'codableChat.noActivePlanEdit',
                        'Není aktivní plán k editaci',
                      )}
                  </button>
                  <button
                    onClick={async () => {
                      void exportPlanMd();
                      closeCodableMenus();
                    }}
                    className='flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-gray-300 hover:bg-gray-800 hover:text-emerald-300 disabled:opacity-35 disabled:hover:text-gray-300 disabled:hover:bg-transparent'
                    disabled={!activePlan}
                  >
                    <Download size={13} />
                    {activePlan
                      ? t(
                        'codableChat.exportPlanTitle',
                        'Exportovat aktivní plán do Markdown souboru',
                      )
                      : t(
                        'codableChat.noActivePlanExport',
                        'Není aktivní plán k exportu',
                      )}
                  </button>
                  <button
                    onClick={async () => {
                      setShowPlanHistory(true);
                      closeCodableMenus();
                    }}
                    className='flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-gray-300 hover:bg-gray-800 hover:text-emerald-300'
                  >
                    <History size={13} />
                    {t(
                      'codableChat.showPlanHistory',
                      'Zobrazit historii plánů projektu',
                    )}
                  </button>
                  {activePlan && (
                    <button
                      onClick={async () => {
                        if (
                          await themedConfirm(
                            t('codableChat.cancelActivePlanConfirm', {
                              done: activePlan.done.length,
                              total: activePlan.steps.length,
                              defaultValue:
                                'Zrušit aktivní plán? ({{done}}/{{total}} kroků hotovo)',
                            }),
                          )
                        ) {
                          const progress = `${activePlan.done.length}/${activePlan.steps.length}`;
                          updatePlan(null, { status: 'cancelled' });
                          const cancelMsg = t(
                            'codableChat.planCancelledFollowup',
                            'Aktivní plán byl zrušen uživatelem (hotovo {{progress}} kroků). Nepokračuj v krocích zrušeného plánu a neposílej ```step_done```. Krátce potvrď a počkej na nové zadání.',
                            { progress },
                          );
                          if (busy) {
                            sendAfterBusyRef.current = cancelMsg;
                            abortRef.current?.abort();
                          } else {
                            void send(cancelMsg);
                          }
                          closeCodableMenus();
                        }
                      }}
                      className='mt-1 flex w-full items-center gap-2 rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-1.5 text-left text-xs text-emerald-300 hover:bg-emerald-500/20'
                      title={t('codableChat.activePlanTitle', {
                        steps: activePlan.steps.map((s, i) =>
                          `${activePlan.done.includes(i) ? '✅' : '⬜'} ${i + 1}. ${s}`
                        ).join('\n'),
                        defaultValue: 'Aktivní plán:\n{{steps}}\n\nKlik zruší plán.',
                      })}
                    >
                      <Check size={12} />
                      {t('codableChat.activePlan', {
                        done: activePlan.done.length,
                        total: activePlan.steps.length,
                        defaultValue: 'Plán {{done}}/{{total}}',
                      })}
                    </button>
                  )}
                </div>
              </details>
              )}
              {/* Stavové ikony lišty: A/M = schvalování tahů, G = Git commit,
                zámeček = pojistka (Filip 10. 9.: „tlačítko live je natvrdlý…
                nahradil bych ho ikonkou zámečku… v zamčeném stavu červený a
                agent by mohl pouze konsultovat"). Default je odomčeno — agent,
                který nemůže zapisovat, je k ničemu.
                Písmeno se mění: A = tah se schvaluje sám, M = každá karta
                chce klik. „Mělo by se to měnit z a na M." (Filip) */}
              <IconToggle
                active={autoAcceptOn}
                onClick={() => {
                  if (consultLock) {
                    toast.info(
                      t(
                        'codableChat.autoAcceptLockedHint',
                        'Zamčeno — agent jen konzultuje, auto-accept se nepoužije.',
                      ),
                    );
                    return;
                  }
                  setAutoAccept((v) => !v);
                }}
                title={autoAcceptOn
                  ? t(
                    'codableChat.autoAcceptOn',
                    'A — tahy se schvalují samy (plány i patch). Klik přepne na M: každá karta bude čekat na potvrzení.',
                  )
                  : t(
                    'codableChat.autoAcceptOff',
                    'M — každá karta (plán, patch, commit) čeká na tvé kliknutí. Klik přepne na A: schvaluje se samo.',
                  )}
                tone='purple'
              >
                {autoAcceptOn ? 'A' : 'M'}
                <ChevronsUpDown size={11} className='-ml-0.5 opacity-80' />
              </IconToggle>
              <IconToggle
                active={autoCommitActive}
                disabled={!autoAcceptOn}
                onClick={() => setAutoCommit((v) => !v)}
                title={autoAcceptOn
                  ? t(
                    'codableChat.autoCommitTitle',
                    'V režimu A po ověření povolí jeden GitLab commit/push jako checkpoint.',
                  )
                  : t(
                    'codableChat.autoCommitNeedsAuto',
                    'GIT checkpoint platí jen v A — v M schvaluješ každý tah a commit jde z karty.',
                  )}
                tone='purple'
              >
                G
              </IconToggle>
              <IconToggle
                active={consultLock}
                activeClass='text-red-400 hover:text-red-300'
                onClick={() => {
                  if (consultLock) {
                    setAutonomyMode('workspace');
                    // Plný cyklus (write + commit tool) musí být dostupný.
                    setAutoCommit(true);
                  } else {
                    setAutonomyMode('ask');
                  }
                }}
                title={consultLock
                  ? t(
                    'codableChat.consultLockedTitle',
                    'Zamčeno — agent jen konzultuje, nic nemění. Kliknutí odomkne.',
                  )
                  : t(
                    'codableChat.consultOpenTitle',
                    'Agent pracuje: zapisuje do DEV a smí commit. Kliknutím zamkneš na konzultaci.',
                  )}
                tone='purple'
              >
                {consultLock ? <Lock size={14} /> : <Unlock size={14} />}
              </IconToggle>
            </div>
            <div className='ml-auto flex h-8 shrink-0 items-center gap-1.5'>
              {busy && (
                // Stop stejný ghost jako ostatní ikony — jen červený symbol.
                <button
                  type='button'
                  onClick={abort}
                  className={`${CHAT_ICON} !text-red-400 hover:!text-red-300`}
                  title={t('codableChat.stop', 'Zastavit')}
                >
                  <Square size={14} />
                </button>
              )}
              {/* Odeslat je k dispozici i během tahu: 1× = zařadit, 2× = přerušit (Filip 2026-09-06). */}
              <button
                type='button'
                onClick={() => send()}
                disabled={!busy && !input.trim() && attachments.length === 0}
                className={`${CHAT_ICON} ${
                  busy && queuedPreview
                    ? '!text-amber-300 hover:!text-amber-200'
                    : '!text-blue-400 hover:!text-blue-300'
                }`}
                title={busy
                  ? (queuedPreview
                    ? t('codableChat.sendInterruptTitle', 'Odeslat znovu = přerušit běžící tah a poslat zařazené hned')
                    : t('codableChat.sendQueueTitle', 'Zařadit zprávu — pošle se po dokončení tahu'))
                  : t('codableChat.sendTitle')}
              >
                <ArrowUp size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showPlanHistory}
        onClose={() => setShowPlanHistory(false)}
        size='lg'
        title={
          <span className='inline-flex items-center gap-2'>
            <History size={15} className='text-emerald-300' />
            {t('codableChat.planHistory', 'Historie plánů')}
            <span className='text-xs font-normal text-gray-500'>
              {planHistory.length}
            </span>
          </span>
        }
        footer={
          <Button
            variant='secondary'
            onClick={() => setShowPlanHistory(false)}
          >
            {t('codableChat.closeBtn')}
          </Button>
        }
      >
        {planHistory.length === 0
          ? (
            <div className='rounded-lg border border-gray-800 bg-gray-950/50 px-3 py-4 text-sm text-gray-500'>
              {t(
                'codableChat.noPlanHistory',
                'Zatím tu není žádný uložený plán.',
              )}
            </div>
          )
          : (
            <div className='max-h-[60vh] overflow-y-auto rounded-lg border border-gray-800 bg-gray-950/40'>
              <div className='divide-y divide-gray-800/80'>
                {planHistory.map((p) => {
                  const title = p.title || p.steps[0] ||
                    t('codableChat.untitledPlan', 'Plán bez názvu');
                  const progress = `${p.done.length}/${p.steps.length}`;
                  const finishedAt = p.status === 'completed'
                    ? p.completed_at
                    : p.status === 'cancelled'
                    ? p.cancelled_at
                    : null;
                  const expanded = expandedPlanHistoryId === p.id;
                  return (
                    <div
                      key={p.id}
                      className='px-3 py-2.5 hover:bg-gray-800/40'
                    >
                      <button
                        type='button'
                        onClick={() => setExpandedPlanHistoryId(expanded ? null : p.id)}
                        className='flex w-full items-center gap-2 min-w-0 text-left'
                      >
                        <ChevronDown
                          size={13}
                          className={`shrink-0 text-gray-500 transition-transform ${
                            expanded ? '' : '-rotate-90'
                          }`}
                        />
                        <span
                          className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] ${
                            planStatusClass(p.status)
                          }`}
                        >
                          {planStatusLabel(p.status)}
                        </span>
                        <span className='min-w-0 flex-1 truncate text-sm text-gray-200'>
                          {title}
                        </span>
                        <span className='shrink-0 text-[11px] font-mono text-gray-500'>
                          {progress}
                        </span>
                      </button>
                      <div className='mt-1 flex flex-wrap items-center gap-2 pl-[21px] text-[11px] text-gray-500'>
                        <span>
                          {p.source === 'import'
                            ? t('codableChat.sourceImport', 'MD import')
                            : p.source === 'manual'
                            ? t('codableChat.sourceManual', 'ručně')
                            : t('codableChat.sourceChat', 'chat')}
                        </span>
                        <span>
                          {t('codableChat.createdAt', {
                            time: formatPlanTime(p.created_at),
                            defaultValue: 'vytvořeno {{time}}',
                          })}
                        </span>
                        {finishedAt && (
                          <span>
                            {t('codableChat.finishedAt', {
                              time: formatPlanTime(finishedAt),
                              defaultValue: 'konec {{time}}',
                            })}
                          </span>
                        )}
                        <span className='ml-auto flex shrink-0 items-center gap-1'>
                          <button
                            type='button'
                            onClick={() => exportPlanHistoryItem(p)}
                            className='inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                            title={t(
                              'codableChat.exportPlanTitle',
                              'Exportovat do Markdown souboru',
                            )}
                          >
                            <Download size={12} />
                            {t('codableChat.exportBtn', 'Export')}
                          </button>
                          <button
                            type='button'
                            onClick={() => void reactivatePlanHistoryItem(p)}
                            className='inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                            title={t(
                              'codableChat.reactivatePlanTitle',
                              'Nastavit jako aktivní plán',
                            )}
                          >
                            <RefreshCw size={12} />
                            {t('codableChat.reactivateBtn', 'Aktivovat')}
                          </button>
                        </span>
                      </div>
                      {expanded && (
                        <ul className='mt-2 ml-[21px] space-y-1 border-l border-gray-800 pl-2.5'>
                          {p.steps.map((step, i) => (
                            <li
                              key={i}
                              className={`flex items-start gap-1.5 text-xs ${
                                p.done.includes(i) ? 'text-gray-500 line-through' : 'text-gray-300'
                              }`}
                            >
                              <span className='mt-0.5 shrink-0'>
                                {p.done.includes(i) ? '✅' : '⬜'}
                              </span>
                              <span>{step}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
      </Modal>

      <Modal
        open={planEditorOpen}
        onClose={() => setPlanEditorOpen(false)}
        size='lg'
        title={t('codableChat.editPlanMd', 'Editovat aktivní plán jako MD')}
        footer={
          <>
            <Button
              variant='secondary'
              onClick={() => setPlanEditorOpen(false)}
            >
              {t('codableChat.cancel')}
            </Button>
            <Button variant='primary' onClick={savePlanEditor}>
              {t('codableChat.savePlan')}
            </Button>
          </>
        }
      >
        <textarea
          className='min-h-[360px] w-full resize-y rounded-lg border border-gray-800 bg-gray-950 p-3 font-mono text-xs leading-relaxed text-gray-200 outline-none focus:border-emerald-500'
          value={planEditorMd}
          onChange={(e) => setPlanEditorMd(e.target.value)}
          spellCheck={false}
        />
        <div className='mt-2 text-xs text-gray-500'>
          {t('codableChat.planMdHint')}
        </div>
      </Modal>

      {/* God mode — ACM await themedConfirm(ne window.confirm). Brand v hlavičce. */}
      <Modal
        open={godConfirmOpen}
        onClose={() => {
          if (!godBusy) setGodConfirmOpen(false);
        }}
        size='sm'
        title={godActive
          ? t('codableChat.godLockTitle', 'Zamknout God Mode')
          : t('codableChat.godUnlockTitle', 'Odemknout God Mode')}
        footer={
          <>
            <Button
              variant='secondary'
              disabled={godBusy}
              onClick={() => setGodConfirmOpen(false)}
            >
              {t('codableChat.cancel', 'Zrušit')}
            </Button>
            <Button
              variant={godActive ? 'secondary' : 'warning'}
              disabled={godBusy}
              loading={godBusy}
              onClick={() => void applyGodSession(godActive ? 'lock' : 'unlock')}
            >
              {godActive
                ? t('codableChat.godLockAction', 'Zamknout')
                : t('codableChat.godUnlockAction', 'Odemknout God Mode')}
            </Button>
          </>
        }
      >
        <div className='space-y-3 text-sm text-gray-300'>
          {godActive
            ? (
              <>
                <p>
                  {t(
                    'codableChat.godLockBody',
                    'God Mode se vypne. Agent ztratí ACL k souborům chatu a deploy tool. Brand se vrátí na „Codable AI“.',
                  )}
                </p>
                {godExpires && (
                  <p className='text-xs text-gray-500'>
                    {t(
                      'codableChat.godExpiresAt',
                      'Aktuální session vyprší: {{exp}}',
                      {
                        exp: godExpires,
                      },
                    )}
                  </p>
                )}
              </>
            )
            : (
              <>
                <p>
                  {t(
                    'codableChat.godUnlockBody',
                    'Odemkneš admin ACL jen k souborům chatu (Codable stack) a tool nasazení. Platí 2 hodiny, nebo dokud to znovu nezamkneš. Agent God Mode neodemyká sám.',
                  )}
                </p>
                <ul className='list-disc pl-5 text-xs text-gray-400 space-y-1'>
                  <li>
                    {t(
                      'codableChat.godUnlockBulletAcl',
                      'Read/write allowlist kódu chatu na serveru',
                    )}
                  </li>
                  <li>
                    {t(
                      'codableChat.godUnlockBulletDeploy',
                      'Deploy s resume kontextu po restartu',
                    )}
                  </li>
                  <li>
                    {t(
                      'codableChat.godUnlockBulletBrand',
                      'Nápis v hlavičce se přepne na žlutý „God Mode“',
                    )}
                  </li>
                </ul>
              </>
            )}
        </div>
      </Modal>
    </div>
  );
}
